import {Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit} from '@angular/core';
import {CommonModule, formatNumber, formatDate} from '@angular/common';
import {FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {NgxPaginationModule} from 'ngx-pagination';
import {Router} from '@angular/router';
import {catchError, finalize} from 'rxjs/operators';
import {of} from 'rxjs';
import {BaseComponent} from '../../base/base.component';
import flatpickr from 'flatpickr';
import {Italian} from 'flatpickr/dist/l10n/it';
import {Instance as FlatpickrInstance} from 'flatpickr/dist/types/instance';
import {NotificationService} from '../../services/notification.service';
import {ErrorHelperService} from '../../services/error-helper.service';
import {FunctionService} from '../../services/function.service';
import {ShipmentManagementService, ClientsBrands, PagedResponse, ShipmentSummary, PageRequest} from '../../services/shipment-management.service';
import * as XLSX from 'xlsx';
import {Brand, Client} from '../../services/shipment-request.service';
import {Waybill, WaybillStatus} from '../../interfaces/Waybills.interfaces';
import {Parcels} from '../../interfaces/Parcels.interfaces';
import {Carriers} from '../../interfaces/Carriers.interfaces';
import {Dealer} from '../../interfaces/waybill.interfaces';
import {WaybillService} from '../../services/waybill.service';
import {DealerDetailPopupComponent} from './dealer-detail-popup/dealer-detail-popup.component';
import {ClientDetailsPopupComponent} from './client-details-popup/client-details-popup.component';
import {CarrierDetailsPopupComponent} from './carrier-details-popup/carrier-details-popup.component';
import {WaybillDetailPopupComponent} from './waybill-detail-popup/waybill-detail-popup.component';
import {ParcelDetailPopupComponent} from './parcel-detail-popup/parcel-detail-popup.component';
import {ParcelCompletePopupComponent} from './parcel-complete-popup/parcel-complete-popup.component';
import {WaybillCompletePopupComponent} from './waybill-complete-popup/waybill-complete-popup.component';
import {AxylogDetailPopupComponent} from './axylog-detail-popup/axylog-detail-popup.component';
import {MailDetailPopupComponent} from './mail-detail-popup/mail-detail-popup.component';
import {ToursPrintPopupComponent} from './tours-print-popup/tours-print-popup.component';
import {TourGuideService} from '../../services/tour-guide.service';
import {TourGuideComponent} from '../../components/tour-guide/tour-guide.component';
import {SHIPMENT_MANAGEMENT_TOUR_CONFIG} from '../../configs/shipment-management-tour.config';
import {RoutingSummary} from '../../interfaces/Routing.interfaces';
import {AnagraphicService} from '../../services/anagraphic.service';
import {TourRouting} from '../../interfaces/TourRouting.interfaces';
import {Warehouse} from '../../interfaces/Warehouses.interfaces';
import {SearchableSelectComponent, SearchableSelectOption} from '../../components/searchable-select/searchable-select.component';
import {ScrollTopButtonComponent} from '../../components/scroll-top-button/scroll-top-button.component';
import {LabelGenerationService} from '../../services/label-generation.service';
import {FloatingHScrollController} from '../../utils/floating-hscroll.controller';

/** Colonna gestibile (visibilità e posizione) delle tabelle Bolle/Colli */
export interface TableColumnOption {
    key: string;
    labelKey?: string;
    label?: string;
    /** Classi bootstrap di visibilità responsive (es. 'd-none d-lg-table-cell') */
    responsive?: string;
    /** Allineamento: numerico (destra) o centrato */
    align?: 'num' | 'center';
    /** false per colonne non ordinabili (default: ordinabile) */
    sortable?: boolean;
}

/** Filtro immagini dei colli: tutti / solo con immagine / solo senza immagine */
export type ParcelImagesFilter = 'ALL' | 'WITH' | 'WITHOUT';

/** Filtro invio in Axylog: tutte / solo inviate / solo non inviate */
export type AxylogSentFilter = 'ALL' | 'SENT' | 'NOT_SENT';

/** Snapshot grezzo dei filtri, per poter riapplicare una ricerca */
interface SavedSearchState {
    filterParcelCode: string;
    filterWaybillCode: string;
    filterDealerCode: string;
    filterStatus: string;
    /** Array nelle ricerche nuove; string in quelle salvate prima della selezione multipla */
    filterWarehouse: string[] | string;
    filterCarrier: string[] | string;
    filterCustomer: string[] | string;
    filterBrand: string[] | string;
    /** Array di zone; string nelle ricerche salvate prima della selezione multipla */
    filterRouting: string[] | string;
    filterCompanyName: string;
    filterAddress: string;
    filterCity: string;
    filterProvince: string;
    filterParcelType: string;
    filterWeight: string;
    filterWeightOperator: string;
    filterVolume: string;
    filterVolumeOperator: string;
    /** Opzionale: assente nelle ricerche salvate prima dell'introduzione del toggle */
    filterDamagedParcels?: boolean;
    /** Opzionale: assente nelle ricerche salvate prima dell'introduzione del toggle */
    filterParcelImages?: ParcelImagesFilter;
    filterAxylogSent?: AxylogSentFilter;
    filterMisroutingParcels?: boolean;
    selectedFilterDateFrom: string | null;
    selectedFilterDateTo: string | null;
    selectedFilterEstimatedDelivery: string | null;
    selectedFilterActualDelivery: string | null;
    isAdvancedFiltersCollapsed: boolean;
}

/** Insieme di filtri salvato dall'utente con il bottone "Salva filtri" */
interface SavedSearch {
    /** Nome dato dall'utente; assente nelle ricerche salvate automaticamente dalla vecchia cronologia */
    name?: string;
    timestamp: number;
    filters: { label: string; value: string }[];
    state?: SavedSearchState;
}
import {
    InvalidateLoadingListsPopupComponent
} from './invalidate-loading-lists-popup/invalidate-loading-lists-popup.component';

@Component({
    selector: 'app-shipment-management',
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule, NgxPaginationModule, DealerDetailPopupComponent, ClientDetailsPopupComponent, CarrierDetailsPopupComponent,WaybillDetailPopupComponent, ParcelDetailPopupComponent, ParcelCompletePopupComponent, WaybillCompletePopupComponent, AxylogDetailPopupComponent, MailDetailPopupComponent, ToursPrintPopupComponent, TourGuideComponent, InvalidateLoadingListsPopupComponent, SearchableSelectComponent, ScrollTopButtonComponent],
    templateUrl: './shipment-management.component.html',
    styleUrl: './shipment-management.component.css'
})
export class ShipmentManagementComponent extends BaseComponent implements OnInit, OnDestroy, AfterViewInit {
    // Cache for volume validation results
    private _volumeValidationCache = new Map<string, any>();

    // Form per gestione waybill
    waybillForm!: FormGroup;

    // Form per gestione parcel
    parcelForm!: FormGroup;

    // ViewChild per il campo data Flatpickr
    @ViewChild('estimatedDateInput', {static: false}) estimatedDateInput!: ElementRef<HTMLInputElement>;

    // ==================== BARRE DI SCORRIMENTO ORIZZONTALE FLOTTANTI (tabelle Bolle e Colli) ====================
    // Logica in FloatingHScrollController: sticky al bordo inferiore, sincronizzazione nei due
    // sensi con la tabella, comparsa solo con almeno 25 righe e overflow, sparizione a fine tabella.

    readonly waybillsHScrollCtl = new FloatingHScrollController();
    readonly parcelsHScrollCtl = new FloatingHScrollController();

    @ViewChild('waybillsScroll', {static: false})
    set waybillsScroll(ref: ElementRef<HTMLElement> | undefined) {
        this.waybillsHScrollCtl.setScrollElement(ref?.nativeElement);
    }

    @ViewChild('waybillsHScroll', {static: false})
    set waybillsHScroll(ref: ElementRef<HTMLElement> | undefined) {
        this.waybillsHScrollCtl.setBarElement(ref?.nativeElement);
    }

    @ViewChild('waybillsEndSentinel', {static: false})
    set waybillsEndSentinel(ref: ElementRef<HTMLElement> | undefined) {
        this.waybillsHScrollCtl.setSentinelElement(ref?.nativeElement);
    }

    @ViewChild('parcelsScroll', {static: false})
    set parcelsScroll(ref: ElementRef<HTMLElement> | undefined) {
        this.parcelsHScrollCtl.setScrollElement(ref?.nativeElement);
    }

    @ViewChild('parcelsHScroll', {static: false})
    set parcelsHScroll(ref: ElementRef<HTMLElement> | undefined) {
        this.parcelsHScrollCtl.setBarElement(ref?.nativeElement);
    }

    @ViewChild('parcelsEndSentinel', {static: false})
    set parcelsEndSentinel(ref: ElementRef<HTMLElement> | undefined) {
        this.parcelsHScrollCtl.setSentinelElement(ref?.nativeElement);
    }

    /** True se la barra flottante della tabella bolle deve essere mostrata */
    get showWaybillsFloatingScrollbar(): boolean {
        return !this.showParcelsView && !this.showRoutingView
            && this.waybillsHScrollCtl.shouldShow(this.filteredWaybills.length);
    }

    /** True se la barra flottante della tabella colli deve essere mostrata */
    get showParcelsFloatingScrollbar(): boolean {
        return this.showParcelsView
            && this.parcelsHScrollCtl.shouldShow(this.filteredParcels.length);
    }

    @ViewChild('axylogDetailPopup', {static: false}) axylogDetailPopup!: AxylogDetailPopupComponent;
    @ViewChild('mailDetailPopup', {static: false}) mailDetailPopup!: MailDetailPopupComponent;


    // Invalidate loading lists popup state
    // Invalidate parcels popup state
    isInvalidateLoadingListsPopupVisible: boolean = false;

    selectedParcelsForInvalidation: {
        parcelCode: string;
        parcelInactiveForLoadingLists: boolean;
    }[] = [];

    loadingListsPopupInitialTab: 'invalidate' | 'validate' = 'invalidate';

    dealerSearchPerformed: boolean = false;

    // Istanza Flatpickr
    private flatpickrInstance: FlatpickrInstance | null = null;

    // Flag per evitare loop di aggiornamenti
    private isUpdatingDate = false;

    // Proprietà separata per gestire la data estimata (scollegata dal form)
    public selectedEstimatedDate: string = '';

    // Data arrays
    waybills: Waybill[] = []; // Contains both PENDING and UNSENT waybills
    parcels: Parcels[] = [];
    carriers: Carriers[] = [];
    warehouses: Warehouse[] = [];
    dealers: Dealer[] = [];

    // Opzioni normalizzate per i select con ricerca (app-searchable-select)
    carrierSelectOptions: SearchableSelectOption[] = [];
    warehouseSelectOptions: SearchableSelectOption[] = [];
    clientSelectOptions: SearchableSelectOption[] = [];
    brandSelectOptions: SearchableSelectOption[] = [];
    zoneSelectOptions: SearchableSelectOption[] = [];
    clientsBrands: ClientsBrands[] = [];
    filteredClients: Client[] = [];
    filteredBrands: Brand[] = [];
    clients: Client[] = [];
    brands: Brand[] = [];

    // Original data arrays (for restoring original order without backend calls)
    // Made public to be accessible from filter functions in shipment-management-filters.ts
    originalWaybills: Waybill[] = [];
    originalParcels: Parcels[] = [];

    // Selected waybill for editing
    selectedWaybill: Waybill | null = null;

    // Selected parcel for editing
    selectedParcel: Parcels | null = null;

    // Pagination properties
    currentPage: number = 1;
    itemsPerPage: number = 10;

    // Paginazione server-side
    totalElements: number = 0;            // totale risultati della vista corrente
    currentSearchFilters: any = {};       // filtri applicati all'ultima ricerca (riusati per pagina/vista)

    // Numero totale di elementi della vista corrente (dal server, non solo la pagina)
    get currentResultCount(): number {
        return this.totalElements;
    }

    // Numero di elementi caricati nella pagina corrente
    get currentPageItemsCount(): number {
        if (this.showRoutingView) return (this.filteredRoutingSummaries ?? []).length;
        return this.showParcelsView ? this.filteredParcels.length : this.filteredWaybills.length;
    }

    // Computed pagination property (server-side)
    get totalPages(): number {
        return Math.max(1, Math.ceil(this.totalElements / this.itemsPerPage));
    }

    /**
     * Get total pages (method version for consistency with shipment-request)
     */
    getTotalPages(): number {
        return this.totalPages;
    }

    // Search and filter
    searchTerm: string = '';

    // Filter fields
    filterParcelCode: string = '';
    filterWaybillCode: string = '';
    filterDealerCode: string = '';
    filterWaybillDeliveryMethod: string = '';
    filterStatus: string = '';

    // Stati disponibili per il filtro (tutti i valori tracciati in WaybillStatus)
    statusOptions: { value: string; label: string }[] = [
        {value: WaybillStatus.ACTIVE, label: 'Attiva'},
        {value: WaybillStatus.PENDING, label: 'In Sospeso'},
        {value: WaybillStatus.DELIVERED, label: 'Consegnata'},
        {value: WaybillStatus.NOT_DELIVERED, label: 'Non Consegnata'},
        {value: WaybillStatus.DELETED, label: 'Eliminata'}
        
        
    ];

    // Advanced column filtering
    isColumnFiltersVisible = false; // Toggle for column filters visibility

    // Selection mode for multiple selection
    isSelectionModeActive = true; // Sempre attiva per mostrare le checkbox
    selectedWaybillCodes = new Set<string>(); // Set of selected waybill codes
    selectedParcelCodes = new Set<string>(); // Set of selected parcel codes
    selectedRoutingCodes = new Set<string>(); // Set of selected routing codes

    // Filtri unificati per bolle e colli
    unifiedFilters: { [key: string]: string } = {};
    unifiedWeightOperator: string = '=';
    unifiedVolumeOperator: string = '=';
    showUnifiedSuggestions: { [key: string]: boolean } = {};
    filteredUnifiedValues: { [key: string]: string[] } = {};
    uniqueUnifiedValues: { [key: string]: string[] } = {};

    comparisonOperators = [
        {value: '>', label: 'Maggiore di'},
        {value: '=', label: 'Uguale a'},
        {value: '<', label: 'Minore di'}
    ];

    // Loading states
    isLoading = false;
    isSubmitting = false;
    hasSearchBeenPerformed = false;

    // UI state properties
    isFiltersCollapsed = false;
    isAdvancedFiltersCollapsed = true;
    isReportCollapsed = false;
    disableFilterDate = false;

    // Strumentario (toolbox) state
    isCompactTable = false; // Densità compatta della tabella risultati
    isColumnsDropdownOpen = false; // Dropdown gestione colonne visibili
    hiddenColumns = new Set<string>(); // Chiavi delle colonne nascoste (persistite)

    // Colonne gestibili (visibilità e posizione) - vista Bolle
    waybillColumnOptions: TableColumnOption[] = [
        {key: 'waybillClientCode', label: 'Cliente', responsive: 'd-none d-sm-table-cell'},
        {key: 'waybillDate', labelKey: 'SHIPMENT_MANAGEMENT.WAYBILL_DATE', responsive: 'd-none d-sm-table-cell'},
        {key: 'waybillEstimatedDeliveryDate', label: 'Stima Consegna', responsive: 'd-none d-lg-table-cell'},
        {key: 'waybillActualDeliveryDate', label: 'Data Consegna', responsive: 'd-none d-lg-table-cell'},
        {key: 'waybillDeliveryType', labelKey: 'SHIPMENT_MANAGEMENT.DELIVERY_TYPE', responsive: 'd-none d-md-table-cell'},
        {key: 'waybillCarrierName', label: 'Vettore Ultimo Miglio', responsive: 'd-none d-md-table-cell'},
        {key: 'companyName', labelKey: 'SHIPMENT_MANAGEMENT.COMPANY_NAME', responsive: 'd-none d-lg-table-cell'},
        {key: 'address', labelKey: 'SHIPMENT_MANAGEMENT.ADDRESS', responsive: 'd-none d-lg-table-cell'},
        {key: 'city', labelKey: 'SHIPMENT_MANAGEMENT.CITY', responsive: 'd-none d-xl-table-cell'},
        {key: 'province', labelKey: 'SHIPMENT_MANAGEMENT.PROVINCE', responsive: 'd-none d-xl-table-cell'},
        {key: 'parcelsCount', labelKey: 'SHIPMENT_MANAGEMENT.PARCELS_COUNT', responsive: 'd-none d-lg-table-cell', align: 'num'},
        {key: 'waybillStatus', labelKey: 'SHIPMENT_MANAGEMENT.STATUS'},
        {key: 'download', labelKey: 'SHIPMENT_MANAGEMENT.DOWNLOAD', align: 'center', sortable: false},
        {key: 'pod', labelKey: 'SHIPMENT_MANAGEMENT.POD', align: 'center', sortable: false}
    ];

    // Colonne gestibili (visibilità e posizione) - vista Colli
    parcelColumnOptions: TableColumnOption[] = [
        {key: 'parcelDate', labelKey: 'SHIPMENT_MANAGEMENT.PARCEL_DATE', responsive: 'd-none d-sm-table-cell'},
        {key: 'parcelEstimatedDeliveryDate', label: 'Stima Consegna', responsive: 'd-none d-lg-table-cell'},
        {key: 'parcelDeliveryDate', label: 'Data Consegna', responsive: 'd-none d-lg-table-cell'},
        {key: 'parcelDealerCode', labelKey: 'SHIPMENT_MANAGEMENT.DEALER_CODE', responsive: 'd-none d-md-table-cell'},
        {key: 'parcelClientCode', label: 'Cliente', responsive: 'd-none d-lg-table-cell'},
        {key: 'parcelType', label: 'Tipologia Collo', responsive: 'd-none d-lg-table-cell'},
        {key: 'parcelWeight', labelKey: 'SHIPMENT_MANAGEMENT.WEIGHT', responsive: 'd-none d-lg-table-cell', align: 'num'},
        {key: 'parcelVolume', labelKey: 'SHIPMENT_MANAGEMENT.VOLUME', responsive: 'd-none d-lg-table-cell', align: 'num'},
        {key: 'parcelLength', labelKey: 'SHIPMENT_MANAGEMENT.LENGTH', responsive: 'd-none d-xl-table-cell', align: 'num'},
        {key: 'parcelWidth', labelKey: 'SHIPMENT_MANAGEMENT.WIDTH', responsive: 'd-none d-xl-table-cell', align: 'num'},
        {key: 'parcelHeight', labelKey: 'SHIPMENT_MANAGEMENT.HEIGHT', responsive: 'd-none d-xl-table-cell', align: 'num'}
    ];

    // Ordine delle colonne per vista (chiavi; persistito). Le chiavi assenti
    // vengono accodate nell'ordine di default.
    columnOrder: { waybill: string[]; parcel: string[] } = {waybill: [], parcel: []};

    // Pannello "Colonne" nell'header della sezione risultati
    isColumnsPanelOpen = false;

    // Drag & drop delle colonne (intestazione tabella e pannello)
    draggedColumnKey: string | null = null;
    dragOverColumnKey: string | null = null;

    // Sorting state
    sortColumn: string = '';
    sortDirection: 'asc' | 'desc' | '' = '';

    // View mode for table (Bolle/Colli/Routing toggle)
    showParcelsView: boolean = false;
    showRoutingView: boolean = false;

    // Routing data for routing view
    routingSummaries: RoutingSummary[] = [];
    filteredRoutingSummaries: RoutingSummary[] = [];

    // Routing detail popup state
    isRoutingDetailPopupVisible: boolean = false;
    selectedRoutingSummary: RoutingSummary | null = null;
    routingDetailWaybills: Waybill[] = [];
    routingDetailParcels: Parcels[] = [];
    routingDetailActiveTab: string = 'summary';

    // Summary/Report data - updates based on filters
    summaryData = {
        totalWaybills: 0,
        totalParcels: 0,
        totalDealers: 0,
        totalWeight: 0,
        totalVolume: 0,
        totalValue: 0
    };


    isCarrierPopupVisible: boolean = false;

    isDealerAssignmentPopupVisible: boolean = false;
    dealerSearchCode: string = '';
    dealerSearchName: string = '';
    dealerSearchCity: string = '';
    dealerSearchAddress: string = '';
    dealerSearchResults: Dealer[] = [];
    selectedDealerForAssignment: Dealer | null = null;

    // Parcel edit popup state
    isParcelEditPopupVisible: boolean = false;

    // Detail popup state
    isDetailPopupVisible: boolean = false;
    detailPopupData: { type: string, code: string } | null = null;
    selectedParcelForDetail: Parcels | null = null;
    selectedWaybillForDetail: Waybill | null = null;
    selectedDealerForDetailPopup: any = null;
    selectedClientForDetailPopup: any = null;
    selectedWaybillParcels: Parcels[] = []; // All parcels of the selected waybill for label generation

    // Dealer detail popup state
    isDealerPopupVisible: boolean = false;
    selectedDealerForDetail: any = null;
    dealerPopupActiveTab: string = 'details'; // 'details' or 'map'

    // Completion popup states
    isParcelCompletePopupVisible: boolean = false;
    isWaybillCompletePopupVisible: boolean = false;
    selectedParcelForCompletion: Parcels | null = null;
    selectedWaybillForCompletion: Waybill | null = null;

    // Status confirmation popup states
    isStatusConfirmationPopupVisible: boolean = false;
    statusConfirmationData: {
        operation: 'pdf' | 'axylog';
        selectedWaybillCodes: string[];
        activeWaybillCodes: string[];
        activeWaybillsMap: { key: string, value: string }[];
        nonActiveWaybillCodes: string[];
    } | null = null;

    // Axylog detail popup state
    isAxylogDetailPopupVisible: boolean = false;

    // Mail detail popup state
    isMailDetailPopupVisible: boolean = false;

    // Print tour popup state
    isPrintTourPopupVisible: boolean = false;

    // Print and export states
    isPrintingPDF: boolean = false;
    isExporting: boolean = false;
    isSendingAxylog: boolean = false;
    isSendingEmail: boolean = false;
    isInvalidating: boolean = false;

    // Zones for delivery type
    zones: string[] = [];
    isLoadingZones = false;
    routings: TourRouting[] = [];

    // Available clients for advanced filter dropdown
    availableClients: { clientCode: string; clientName: string }[] = [];
    filterCustomer: string[] = [];

    // Available brands for advanced filter dropdown
    availableBrands: { brandCode: string; brandName: string }[] = [];
    filterBrand: string[] = [];

    // Filter for routing (selezione multipla: array di zone)
    filterRouting: string[] = [];

    // Filter for carrier (selezione multipla)
    filterCarrier: string[] = [];

    // Filter for warehouse (selezione multipla)
    filterWarehouse: string[] = [];

    // Filtri salvati dall'utente con "Salva filtri" (persistiti in localStorage).
    // Chiave nuova: la vecchia cronologia automatica delle ricerche non viene ripresa.
    private readonly SAVED_SEARCHES_KEY = 'shipment-management-saved-searches';
    private readonly MAX_SAVED_SEARCHES = 30;
    savedSearches: SavedSearch[] = [];
    isSavedSearchPopupVisible = false;
    selectedSavedSearch: SavedSearch | null = null;

    // Popup "Rinomina": il nome è facoltativo e si assegna dopo il salvataggio, dalla card
    isSaveSearchPopupVisible = false;
    saveSearchName = '';
    /** Card in rinomina mentre il popup è aperto */
    editingSavedSearch: SavedSearch | null = null;
    @ViewChild('savedSearchNameInput', {static: false}) savedSearchNameInput?: ElementRef<HTMLInputElement>;

    // Filtri avanzati aggiuntivi (provenienti dai filtri colonna della tabella)
    filterCompanyName: string = '';
    filterAddress: string = '';
    filterCity: string = '';
    filterProvince: string = '';
    filterParcelType: string = '';
    filterWeight: string = '';
    filterWeightOperator: string = '=';
    filterVolume: string = '';
    filterVolumeOperator: string = '=';

    /**
     * Toggle "Colli Danneggiati" (default spento). Da attivo la ricerca invia
     * damagedParcels: true nei filtri, per avere solo i colli danneggiati.
     */
    filterDamagedParcels = false;

    setFilterDamagedParcels(value: boolean): void {
        this.filterDamagedParcels = value;
    }

    /**
     * Toggle a 3 posizioni "Immagini Colli" (default: tutti). Con "solo con immagine" /
     * "solo senza immagine" la ricerca invia hasImages: true / false nei filtri.
     */
    filterParcelImages: ParcelImagesFilter = 'ALL';
    readonly parcelImagesOptions: { value: ParcelImagesFilter; label: string; icon: string }[] = [
        {value: 'ALL', label: 'Tutti i colli', icon: 'fa-boxes-stacked'},
        {value: 'WITH', label: 'Solo colli con immagine', icon: 'fa-image'},
        {value: 'WITHOUT', label: 'Solo colli senza immagine', icon: 'fa-eye-slash'}
    ];

    setFilterParcelImages(value: ParcelImagesFilter): void {
        this.filterParcelImages = value;
    }

    /** Click sul toggle: passa alla posizione successiva (tutti → con immagine → senza immagine → tutti) */
    cycleFilterParcelImages(): void {
        const order = this.parcelImagesOptions.map(o => o.value);
        const next = (order.indexOf(this.filterParcelImages) + 1) % order.length;
        this.filterParcelImages = order[next];
    }

    get parcelImagesFilterLabel(): string {
        return this.parcelImagesOptions.find(o => o.value === this.filterParcelImages)?.label ?? '';
    }

    /**
     * Toggle a 3 posizioni "Inviato in Axylog" (default: tutte). Con "inviate" / "non inviate"
     * la ricerca invia sentToAxylog: true / false nei filtri.
     */
    filterAxylogSent: AxylogSentFilter = 'ALL';
    readonly axylogSentOptions: { value: AxylogSentFilter; label: string; icon: string; pos: string }[] = [
        {value: 'ALL', label: 'Tutte', icon: 'fa-list', pos: 'ALL'},
        {value: 'SENT', label: 'Solo inviate in Axylog', icon: 'fa-cloud-upload-alt', pos: 'WITH'},
        {value: 'NOT_SENT', label: 'Solo non inviate in Axylog', icon: 'fa-ban', pos: 'WITHOUT'}
    ];

    /** Click sul toggle: passa alla posizione successiva (tutte → inviate → non inviate → tutte) */
    cycleFilterAxylogSent(): void {
        const order = this.axylogSentOptions.map(o => o.value);
        const next = (order.indexOf(this.filterAxylogSent) + 1) % order.length;
        this.filterAxylogSent = order[next];
    }

    get axylogSentOption() {
        return this.axylogSentOptions.find(o => o.value === this.filterAxylogSent) ?? this.axylogSentOptions[0];
    }

    /**
     * Toggle "Colli in Misrouting" (default spento). Da attivo la ricerca invia
     * misroutingParcels: true nei filtri, per avere solo i colli con misrouting.
     */
    filterMisroutingParcels = false;

    setFilterMisroutingParcels(value: boolean): void {
        this.filterMisroutingParcels = value;
    }

    // Loading states for search operations
    isSearchLoading = false;
    isAdvancedSearchLoading = false;
    isUnifiedFilterLoading = false;

    // Expose Math for template
    Math = Math;

    // Expose Array for template
    Array = Array;

    // Expose WaybillStatus enum for template
    WaybillStatus = WaybillStatus;

    // LocalStorage key for shipment management settings
    private readonly STORAGE_KEY = 'shipment-management-settings';

    // Date filter properties
    selectedFilterDate: Date | null = null;
    private filterDateFlatpickrInstance: FlatpickrInstance | null = null;
    private isUpdatingFilterDate = false;

    // Date range filter properties
    selectedFilterDateFrom: Date | null = null;
    selectedFilterDateTo: Date | null = null;
    private filterDateFromFlatpickrInstance: FlatpickrInstance | null = null;
    private filterDateToFlatpickrInstance: FlatpickrInstance | null = null;

    // Estimated delivery date filter properties
    selectedFilterEstimatedDelivery: Date | null = null;
    private filterEstimatedDeliveryFlatpickrInstance: FlatpickrInstance | null = null;

    // Actual delivery date filter properties
    selectedFilterActualDelivery: Date | null = null;
    private filterActualDeliveryFlatpickrInstance: FlatpickrInstance | null = null;

    // ViewChild for date range inputs
    @ViewChild('filterDateFromInput', {static: false}) filterDateFromInput!: ElementRef<HTMLInputElement>;
    @ViewChild('filterDateToInput', {static: false}) filterDateToInput!: ElementRef<HTMLInputElement>;
    @ViewChild('filterEstimatedDeliveryInput', {static: false}) filterEstimatedDeliveryInput!: ElementRef<HTMLInputElement>;
    @ViewChild('filterActualDeliveryInput', {static: false}) filterActualDeliveryInput!: ElementRef<HTMLInputElement>;

    // ViewChild per il campo data
    @ViewChild('filterDateInput', {static: false}) filterDateInput!: ElementRef;

    constructor(
        private fb: FormBuilder,
        private translate: TranslateService,
        private notificationService: NotificationService,
        private shipmentManagementService: ShipmentManagementService,
        private waybillService: WaybillService,
        private tourGuideService: TourGuideService,
        private anagraphicService: AnagraphicService,
        private labelGenerationService: LabelGenerationService,
        private errorHelperService: ErrorHelperService,
        functionService: FunctionService,
        router: Router
    ) {
        super(functionService, router);
        this.initializeForm();
    }

    /**
     * Load user preferences from localStorage
     */
    private loadUserPreferences(): void {
        try {
            const savedSettings = localStorage.getItem(this.STORAGE_KEY);
            if (savedSettings) {
                const settings = JSON.parse(savedSettings);

                // Apply saved settings
                if (typeof settings.showParcelsView === 'boolean') {
                    this.showParcelsView = settings.showParcelsView;
                }

                if (typeof settings.showRoutingView === 'boolean') {
                    this.showRoutingView = settings.showRoutingView;
                }

                if (typeof settings.isFiltersCollapsed === 'boolean') {
                    this.isFiltersCollapsed = settings.isFiltersCollapsed;
                }

                if (typeof settings.isReportCollapsed === 'boolean') {
                    this.isReportCollapsed = settings.isReportCollapsed;
                }

                if (typeof settings.isColumnFiltersVisible === 'boolean') {
                    this.isColumnFiltersVisible = settings.isColumnFiltersVisible;
                }

                if (typeof settings.itemsPerPage === 'number' && settings.itemsPerPage > 0) {
                    this.itemsPerPage = settings.itemsPerPage;
                }

                if (typeof settings.isCompactTable === 'boolean') {
                    this.isCompactTable = settings.isCompactTable;
                }

                if (Array.isArray(settings.hiddenColumns)) {
                    this.hiddenColumns = new Set<string>(settings.hiddenColumns);
                }

                if (settings.columnOrder && typeof settings.columnOrder === 'object') {
                    this.columnOrder = {
                        waybill: Array.isArray(settings.columnOrder.waybill)
                            ? settings.columnOrder.waybill.map(String) : [],
                        parcel: Array.isArray(settings.columnOrder.parcel)
                            ? settings.columnOrder.parcel.map(String) : []
                    };
                }

                //console.log('Loaded shipment management preferences:', settings);
            }
        } catch (error) {
            console.warn('Error loading shipment management preferences:', error);
        }
    }

    /**
     * Save user preferences to localStorage
     */
    private saveUserPreferences(): void {
        try {
            const settings = {
                showParcelsView: this.showParcelsView,
                showRoutingView: this.showRoutingView,
                isFiltersCollapsed: this.isFiltersCollapsed,
                isReportCollapsed: this.isReportCollapsed,
                isColumnFiltersVisible: this.isColumnFiltersVisible,
                itemsPerPage: this.itemsPerPage,
                isCompactTable: this.isCompactTable,
                hiddenColumns: Array.from(this.hiddenColumns),
                columnOrder: this.columnOrder
            };

            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(settings));
            //console.log('Saved shipment management preferences:', settings);
        } catch (error) {
            console.warn('Error saving shipment management preferences:', error);
        }
    }

    /**
     * Apply filters from function service configuration
     * Reads filters defined in the function service for shipment-management
     * and applies them to the corresponding component filter properties
     */
    private applyFunctionServiceFilters(): void {
        try {
            // Get stored functions from localStorage
            const stored = localStorage.getItem('userFunctions');
            if (!stored) {
                return;
            }

            const userFunctions = JSON.parse(stored);

            // Find shipment-management function
            const shipmentManagementFunction = userFunctions.functions?.find(
                (fn: any) => fn.functionName === 'shipment-management'
            );

            if (!shipmentManagementFunction) {
                return;
            }

            // Apply filters if they exist
            if (shipmentManagementFunction.filters && Array.isArray(shipmentManagementFunction.filters)) {
                shipmentManagementFunction.filters.forEach((filter: any) => {
                    const filterName = filter.name;
                    const filterValue = filter.value;

                    // Check if the filter property exists in the component
                    if (filterName in this) {
                        (this as any)[filterName] = filterValue;
                    }
                });
            }
        } catch (error) {
            console.error('Error applying function service filters:', error);
        }
    }

    ngOnInit(): void {
        // Load user preferences first
        this.loadUserPreferences();

        // Carica i filtri salvati dall'utente
        this.loadSavedSearches();

        // Log function service per debug
        console.log('🔍 Function Service:', this.functionService);

        console.log('filterWaybillDeliveryMethod on init: ', this.filterWaybillDeliveryMethod);

        // Apply filters from function service
        this.applyFunctionServiceFilters();

        // Override della traduzione per "Tipo Consegna" -> "Instradamento"
        this.translate.set('SHIPMENT_MANAGEMENT.DELIVERY_TYPE', 'Instradamento');

        // Inizializza i filtri unificati
        this.initializeUnifiedFilters();

        // Load clients and brands from localStorage (same as shipment-request)
        const stored = localStorage.getItem('userFunctions');
        console.log("stored : ", stored);
        if (stored) {
            const parsed = JSON.parse(stored);
            this.clientsBrands = parsed.clients;
            this.filteredClients = this.clientsBrands.map(cb => ({
                clientCode: cb.clientCode,
                clientName: cb.clientName
            })).sort((a, b) => {
                if (!a.clientName && !b.clientName) return 0;
                if (!a.clientName) return 1;
                if (!b.clientName) return -1;
                return a.clientName.localeCompare(b.clientName);
            });

            // Populate available clients for advanced filter dropdown
            this.availableClients = this.clientsBrands
                .filter(cb => cb.clientName) // Filter out clients without name
                .map(cb => ({
                    clientCode: cb.clientCode,
                    clientName: cb.clientName!
                }));
            this.clientSelectOptions = this.availableClients.map(c => ({
                value: c.clientCode,
                label: c.clientName
            }));

            // Set all brands
            const allBrands: Brand[] = this.clientsBrands.flatMap(cb =>
                cb.brands.map(b => ({
                    brandCode: b.brandCode,
                    brandName: b.brandName,
                }))
            ).sort((a, b) => {
                if (!a.brandName && !b.brandName) return 0;
                if (!a.brandName) return 1;
                if (!b.brandName) return -1;
                return a.brandName.localeCompare(b.brandName);
            });

            this.filteredBrands = Array.from(
                new Map(allBrands.map(b => [b.brandCode, b])).values()
            );

            // Populate available brands for advanced filter dropdown
            this.availableBrands = Array.from(
                new Map(allBrands.map(b => [b.brandCode, b])).values()
            )
                .filter(b => b.brandName) // Filter out brands without name
                .map(b => ({
                    brandCode: b.brandCode,
                    brandName: b.brandName!
                }));
            this.brandSelectOptions = this.availableBrands.map(b => ({
                value: b.brandCode,
                label: b.brandName
            }));

            // Load data after setting up clients and brands
            // Pass base search filter parameters to loadWaybills
            // You can set these from component properties or form controls as needed
            const baseFilters: any = {
                parcelCode: this.searchTerm, // Codice Collo
                waybillCode: this.searchTerm, // Codice Bolla
                dealerCode: this.searchTerm, // Codice Dealer
                date: this.selectedFilterDate // Data (Date object or formatted string)
            };

            // Aggiungi filterWaybillDeliveryMethod solo se valorizzato
            if (this.filterWaybillDeliveryMethod && this.filterWaybillDeliveryMethod.trim() !== '') {
                baseFilters.filterWaybillDeliveryMethod = this.filterWaybillDeliveryMethod;
            }

            this.loadWaybills(baseFilters); // This will trigger loadDealers and loadParcels after waybills are loaded
            this.loadCarriers();
            this.loadWarehouses();
            this.loadZones();
        }
    }

    ngOnDestroy(): void {
        this.destroyStickyTableHeaders();
        this.waybillsHScrollCtl.destroy();
        this.parcelsHScrollCtl.destroy();
        // Cleanup subscriptions if any
        // Distruggi l'istanza di Flatpickr se esiste
        if (this.flatpickrInstance) {
            this.flatpickrInstance.destroy();
        }

        // Distruggi l'istanza di Flatpickr per il filtro data se esiste
        if (this.filterDateFlatpickrInstance) {
            this.filterDateFlatpickrInstance.destroy();
        }

        // Distruggi le istanze di Flatpickr per i filtri data range se esistono
        if (this.filterDateFromFlatpickrInstance) {
            this.filterDateFromFlatpickrInstance.destroy();
        }

        if (this.filterDateToFlatpickrInstance) {
            this.filterDateToFlatpickrInstance.destroy();
        }
    }

    // ---- Header sticky sotto la navbar (tabelle bolle/colli) ----
    // L'header della card dei risultati è sticky in CSS (#results-table > .card-header) subito
    // sotto la navbar, la cui altezza viene misurata qui e passata via --sm-sticky-top.
    // La testata della tabella invece sta in un wrapper con overflow-x (scorrimento
    // orizzontale), quindi `position: sticky` non arriva fino alla finestra. Quando la testata
    // reale esce sotto l'header della card, mostriamo una copia `position: fixed` (nessun
    // ritardo di scroll) agganciata al bordo inferiore dell'header e fermata al limite
    // inferiore della tabella.
    private stickyOverlays = new Map<HTMLElement, HTMLElement>();
    private readonly onStickyScroll = () => this.updateStickyTableHeaders();

    private initStickyTableHeaders(): void {
        window.addEventListener('scroll', this.onStickyScroll, true); // capture: include lo scroll orizzontale dei wrapper
        window.addEventListener('resize', this.onStickyScroll);
        // Primo calcolo senza aspettare uno scroll (es. pagina ricaricata già scorsa)
        setTimeout(() => this.updateStickyTableHeaders(), 0);
    }

    private destroyStickyTableHeaders(): void {
        window.removeEventListener('scroll', this.onStickyScroll, true);
        window.removeEventListener('resize', this.onStickyScroll);
        this.stickyOverlays.forEach(overlay => overlay.remove());
        this.stickyOverlays.clear();
    }

    updateStickyTableHeaders(): void {
        const navbar = document.querySelector('nav.navbar') as HTMLElement | null;
        const navBottom = navbar ? navbar.getBoundingClientRect().bottom : 0;
        const wrappers = Array.from(document.querySelectorAll<HTMLElement>('.sm-data-table-wrapper'));

        // Header della card dei risultati: sticky in CSS, qui riceve l'altezza della navbar.
        // La classe sm-stuck sulla card segnala che l'header è agganciato (angoli squadrati):
        // succede quando il bordo superiore della card è salito sopra il bordo della navbar
        const resultsCard = document.querySelector<HTMLElement>('#results-table');
        const resultsHeader = resultsCard?.querySelector<HTMLElement>(':scope > .card-header') ?? null;
        if (resultsCard && resultsHeader) {
            const stickyTop = `${navBottom}px`;
            if (resultsHeader.style.getPropertyValue('--sm-sticky-top') !== stickyTop) {
                resultsHeader.style.setProperty('--sm-sticky-top', stickyTop);
            }
            const cardRect = resultsCard.getBoundingClientRect();
            const stuck = cardRect.top < navBottom && cardRect.bottom > navBottom;
            resultsCard.classList.toggle('sm-stuck', stuck);
        }

        // Rimuove gli overlay dei wrapper non più presenti nel DOM
        this.stickyOverlays.forEach((overlay, wrapper) => {
            if (!wrappers.includes(wrapper)) {
                overlay.remove();
                this.stickyOverlays.delete(wrapper);
            }
        });

        wrappers.forEach(wrapper => {
            const table = wrapper.querySelector('table') as HTMLTableElement | null;
            const head = table?.tHead;
            if (!table || !head) return;

            // La testata si aggancia al bordo inferiore dell'header (sticky) della card che
            // contiene la tabella, o alla navbar se la card non ha header
            const cardHeader = wrapper.closest('.card')?.querySelector<HTMLElement>(':scope > .card-header') ?? null;
            const anchor = cardHeader ? Math.max(navBottom, cardHeader.getBoundingClientRect().bottom) : navBottom;

            const wrapperRect = wrapper.getBoundingClientRect();
            const headRect = head.getBoundingClientRect();
            const tableBottom = table.getBoundingClientRect().bottom;
            const needsSticky = headRect.top < anchor && tableBottom > anchor;

            let overlay = this.stickyOverlays.get(wrapper);
            if (!needsSticky) {
                if (overlay) overlay.style.display = 'none';
                return;
            }

            if (!overlay) {
                overlay = document.createElement('div');
                overlay.style.cssText = 'position:fixed;overflow:hidden;z-index:999;pointer-events:auto;';
                // Il click sulle intestazioni copiate viene girato sulla corrispondente intestazione reale
                overlay.addEventListener('click', ev => {
                    const th = (ev.target as HTMLElement).closest('th');
                    if (!th || !overlay) return;
                    const index = Array.from(th.parentElement!.children).indexOf(th);
                    const realTh = head.rows[0]?.cells[index] as HTMLElement | undefined;
                    const realInput = realTh?.querySelector('input') as HTMLInputElement | null;
                    ev.preventDefault();
                    (realInput ?? realTh)?.click();
                    setTimeout(() => this.updateStickyTableHeaders(), 50);
                });
                document.body.appendChild(overlay);
                this.stickyOverlays.set(wrapper, overlay);
            }

            // Ricrea la copia solo se l'header reale è cambiato (ordinamento, colonne, ...)
            const signature = head.innerHTML;
            if (overlay.dataset['sig'] !== signature) {
                const clone = table.cloneNode(false) as HTMLTableElement;
                const headClone = head.cloneNode(true) as HTMLTableSectionElement;
                clone.appendChild(headClone);
                clone.style.tableLayout = 'fixed';
                clone.style.margin = '0';
                headClone.querySelectorAll('[draggable]').forEach(el => el.removeAttribute('draggable'));
                overlay.replaceChildren(clone);
                overlay.dataset['sig'] = signature;
            }

            const clone = overlay.firstElementChild as HTMLTableElement;
            const realCells = Array.from(head.rows[0].cells);
            const cloneCells = Array.from((clone.tHead as HTMLTableSectionElement).rows[0].cells);
            const realInputs = Array.from(head.querySelectorAll('input'));
            clone.querySelectorAll('input').forEach((input, i) => {
                if (realInputs[i]) (input as HTMLInputElement).checked = realInputs[i].checked;
            });
            realCells.forEach((cell, i) => {
                const w = cell.getBoundingClientRect().width;
                const target = cloneCells[i];
                if (target) target.style.cssText += `;width:${w}px;min-width:${w}px;max-width:${w}px;box-sizing:border-box;`;
            });
            clone.style.width = `${table.offsetWidth}px`;
            clone.style.marginLeft = `${-wrapper.scrollLeft}px`;

            // Si ferma al bordo inferiore della tabella: non esce dai suoi contorni
            const top = Math.min(anchor, tableBottom - headRect.height);
            overlay.style.cssText += `;display:block;top:${top}px;left:${wrapperRect.left}px;width:${wrapper.clientWidth}px;height:${headRect.height}px;box-shadow:0 2px 6px rgba(0,0,0,0.08);`;
        });
    }

    ngAfterViewInit(): void {
        this.initStickyTableHeaders();
        // Inizializza Flatpickr dopo che la view è stata inizializzata
        this.initializeFlatpickr();

        // Inizializza Flatpickr per il filtro data
        setTimeout(() => {
            this.initializeFilterDateFlatpickr();
            this.initializeFilterDateFromFlatpickr();
            this.initializeFilterDateToFlatpickr();
            this.initializeFilterEstimatedDeliveryFlatpickr();
            this.initializeFilterActualDeliveryFlatpickr();
        }, 100);

        // Initialize Bootstrap tooltips
        setTimeout(() => {
            this.initializeBootstrapTooltips();
        }, 300);
    }

    /**
     * Initialize Bootstrap tooltips
     */
    private initializeBootstrapTooltips(): void {
        // Check if we're in a browser environment and Bootstrap is available
        if (typeof document !== 'undefined' && typeof window !== 'undefined' && (window as any).bootstrap) {
            const tooltipTriggerList = Array.from(document.querySelectorAll('[data-bs-toggle="tooltip"], [data-toggle="tooltip"]'));
            tooltipTriggerList.forEach(tooltipTriggerEl => {
                try {
                    new (window as any).bootstrap.Tooltip(tooltipTriggerEl);
                } catch (error) {
                    console.error('Error initializing tooltip:', error);
                }
            });
        }
    }

    /**
     * Hide all active Bootstrap tooltips
     */
    private hideTooltips(): void {
        if (typeof document !== 'undefined' && typeof window !== 'undefined' && (window as any).bootstrap) {
            const tooltipElements = document.querySelectorAll('[data-bs-toggle="tooltip"], [data-toggle="tooltip"]');
            tooltipElements.forEach(element => {
                try {
                    const tooltipInstance = (window as any).bootstrap.Tooltip.getInstance(element);
                    if (tooltipInstance) {
                        tooltipInstance.hide();
                    }
                } catch (error) {
                    console.error('Error hiding tooltip:', error);
                }
            });
        }
    }

    /**
     * Inizializza Flatpickr per il campo data
     */
    private initializeFlatpickr(): void {
        if (this.estimatedDateInput?.nativeElement) {
            const currentLang = this.translate.currentLang || 'it';

            // Distruggi l'istanza precedente se esiste
            if (this.flatpickrInstance) {
                this.flatpickrInstance.destroy();
            }

            this.flatpickrInstance = flatpickr(this.estimatedDateInput.nativeElement, {
                dateFormat: 'd/m/Y',
                allowInput: true,
                clickOpens: true,
                locale: currentLang === 'it' ? Italian : undefined,
                minDate: new Date(),
                onChange: (selectedDates: Date[], dateStr: string) => {
                    if (selectedDates.length > 0) {
                        const selectedDate = selectedDates[0];
                        // Gestione sicura del timezone per evitare shift di giorni
                        const year = selectedDate.getFullYear();
                        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
                        const day = String(selectedDate.getDate()).padStart(2, '0');
                        this.selectedEstimatedDate = `${year}-${month}-${day}`;
                        // Aggiorna automaticamente il campo della bolla
                        this.updateWaybillEstimatedDelivery();
                    }
                }
            });
        }
    }

    /**
     * Reinizializza Flatpickr (utile per cambio lingua)
     */
    private reinitializeFlatpickr(): void {
        if (this.flatpickrInstance) {
            this.flatpickrInstance.destroy();
        }
        this.initializeFlatpickr();
    }

    /**
     * Metodo pubblico per riattivare il datepicker
     */
    public reactivateDatePicker(): void {
        if (this.flatpickrInstance) {
            this.flatpickrInstance.open();
        } else {
            this.initializeFlatpickr();
            setTimeout(() => {
                if (this.flatpickrInstance) {
                    this.flatpickrInstance.open();
                }
            }, 100);
        }
    }

    /**
     * Gestisce l'evento blur per preservare il valore
     */
    public onDateInputBlur(): void {
        // Non fare nulla
    }

    /**
     * Restituisce la data formattata per la visualizzazione
     */
    public getFormattedDate(): string {
        if (this.selectedEstimatedDate) {
            // Parsing sicuro della data per evitare problemi di timezone
            const [year, month, day] = this.selectedEstimatedDate.split('-').map(Number);
            const date = new Date(year, month - 1, day); // month è 0-based in JavaScript
            return date.toLocaleDateString('it-IT');
        }
        return '';
    }

    /**
     * Apre il datepicker
     */
    public openDatePicker(): void {
        if (!this.flatpickrInstance) {
            this.initializeFlatpickr();
        }

        setTimeout(() => {
            if (this.flatpickrInstance) {
                // Imposta la data corrente se disponibile, gestendo correttamente il timezone
                if (this.selectedEstimatedDate) {
                    const [year, month, day] = this.selectedEstimatedDate.split('-').map(Number);
                    const date = new Date(year, month - 1, day);
                    this.flatpickrInstance.setDate(date);
                }
                this.flatpickrInstance.open();
            }
        }, 100);
    }

    /**
     * Focalizza il campo dell'ora per permettere l'inserimento manuale
     */
    public focusTimeInput(): void {
        const timeInput = document.getElementById('waybillEstimatedDeliveryTime') as HTMLInputElement;
        if (timeInput) {
            timeInput.focus();
            // Se il browser supporta showPicker(), lo usa per mostrare il selettore
            if ('showPicker' in timeInput) {
                try {
                    (timeInput as any).showPicker();
                } catch (error) {
                    // Se showPicker() non è supportato o fallisce, mantieni solo il focus
                    console.log('showPicker non supportato o non disponibile');
                }
            }
        }
    }

    /**
     * Aggiorna automaticamente il campo Data Consegna Stimata della bolla
     * combinando data e ora selezionate
     */
    public updateWaybillEstimatedDelivery(): void {
        if (this.waybillForm) {
            const estimatedTime = this.waybillForm.get('waybillEstimatedDeliveryTime')?.value;
            let combinedDateTime = '';

            if (this.selectedEstimatedDate && estimatedTime) {
                // Combina data e ora
                const [year, month, day] = this.selectedEstimatedDate.split('-').map(Number);
                const date = new Date(year, month - 1, day);
                combinedDateTime = `${date.toLocaleDateString('it-IT')} ${estimatedTime}`;
            } else if (this.selectedEstimatedDate) {
                // Solo data
                const [year, month, day] = this.selectedEstimatedDate.split('-').map(Number);
                const date = new Date(year, month - 1, day);
                combinedDateTime = date.toLocaleDateString('it-IT');
            }

            // Aggiorna il campo della bolla
            this.waybillForm.patchValue({
                waybillEstimatedDeliveryDate: combinedDateTime
            });
        }
    }

    /**
     * Initialize the waybill and parcel forms
     */
    private initializeForm(): void {
        this.waybillForm = this.fb.group({
            waybillId: [''],
            waybillCode: ['', Validators.required],
            waybillShipmentCode: ['', Validators.required],
            waybillDate: [''],
            waybillEstimatedDeliveryDate: [''],
            waybillDeliveryMethod: [''],
            waybillDeliveryType: [''],
            waybillCarrierCode: ['', Validators.required], // Required for carrier selection
            waybillBrandCode: [''],
            waybillDealerCode: [''],
            waybillClientCode: [''],
            waybillOriginLocation: [''],
            waybillDestinationLocation: [''],
            waybillStatus: [WaybillStatus.PENDING], // Default status can be PENDING or UNSENT
            // Nuovi campi per data e ora di consegna stimata
            // estimatedDeliveryDate: [''], // Rimosso dal form control
            waybillEstimatedDeliveryTime: ['']
        });

        this.parcelForm = this.fb.group({
            parcelId: [''],
            parcelWeight: [0, [this.weightVolumeValidator.bind(this)]],
            parcelVolume: [0, [this.volumeValidator.bind(this)]],
            parcelLength: [0, [this.dimensionValidator.bind(this)]],
            parcelWidth: [0, [this.dimensionValidator.bind(this)]],
            parcelHeight: [0, [this.dimensionValidator.bind(this)]]
        });

        // Add listeners for dimension changes to update volume automatically
        this.parcelForm.get('parcelLength')?.valueChanges.subscribe(() => this.updateParcelVolume());
        this.parcelForm.get('parcelWidth')?.valueChanges.subscribe(() => this.updateParcelVolume());
        this.parcelForm.get('parcelHeight')?.valueChanges.subscribe(() => this.updateParcelVolume());
    }

    /**
     * Punto di ingresso storico: imposta i filtri (con eventuale conversione data
     * singola -> intervallo) e avvia la ricerca paginata della vista corrente + resoconto.
     */
    private loadWaybills(filters?: any): void {
        this.currentSearchFilters = this.resolveDateFilters(filters ?? this.buildSearchFilters());
        this.currentPage = 1;
        this.loadCurrentView();
        this.loadSummary();
    }

    /**
     * Converte un eventuale filtro `date` singolo in dateFrom/dateTo (inizio/fine giornata).
     */
    private resolveDateFilters(filters: any): any {
        const f = {...(filters || {})};
        if (f.date && !f.dateFrom && !f.dateTo) {
            const from = new Date(f.date); from.setHours(0, 0, 0, 0);
            const to = new Date(f.date); to.setHours(23, 59, 59, 999);
            f.dateFrom = from;
            f.dateTo = to;
        }
        delete f.date;
        return f;
    }

    /** Parametri di paginazione/ordinamento per la richiesta corrente */
    private get currentPageRequest(): PageRequest {
        return {
            page: Math.max(0, this.currentPage - 1),
            size: this.itemsPerPage,
            sort: this.sortColumn || undefined,
            direction: (this.sortDirection as 'asc' | 'desc') || undefined
        };
    }

    private emptyPage<T>(): PagedResponse<T> {
        return {content: [], totalElements: 0, totalPages: 0, number: 0, size: this.itemsPerPage};
    }

    /**
     * Carica la pagina della vista attiva (bolle / colli / instradamenti).
     */
    loadCurrentView(): void {
        this.hasSearchBeenPerformed = true;
        if (this.showRoutingView) {
            this.loadToursPage();
        } else if (this.showParcelsView) {
            this.loadParcelsPage();
        } else {
            this.loadWaybillsPage();
        }
    }

    /** Ricarica la vista corrente + il resoconto (dopo modifiche) */
    refreshResults(): void {
        this.loadCurrentView();
        this.loadSummary();
    }

    /** Bolle: richiesta paginata server-side */
    private loadWaybillsPage(): void {
        console.log('[loadWaybillsPage] currentSearchFilters:', this.currentSearchFilters);
        this.isLoading = true;
        const cb = this.getClientsBrandsSelected();
        this.shipmentManagementService.searchWaybillsPaged(cb, this.currentSearchFilters, this.currentPageRequest)
            .pipe(
                catchError(() => {
                    this.notificationService.error('Errore nel caricamento delle bolle');
                    return of(this.emptyPage<Waybill>());
                }),
                finalize(() => {
                    this.isLoading = false;
                    this.isSearchLoading = false;
                    this.isAdvancedSearchLoading = false;
                })
            )
            .subscribe(res => {
                const content = res?.content ?? [];
                console.log('[loadWaybills] waybillCodes:', content.map(w => w.waybillCode));
                this.waybills = content;
                this.originalWaybills = [...content];
                this.totalElements = res?.totalElements ?? 0;
                this.selectedWaybillCodes.clear();
                this.selectedParcelCodes.clear();
                // I dealer arrivano embedded in ogni bolla: niente richiesta separata
                this.dealers = this.extractDealersFromWaybills(content);
                this.populateUniqueUnifiedValues();
            });
    }

    /** Estrae i dealer (deduplicati per codice) dalle bolle della pagina corrente */
    private extractDealersFromWaybills(waybills: Waybill[]): Dealer[] {
        const byCode = new Map<string, Dealer>();
        for (const w of waybills) {
            if (w.dealer?.dealerCode && !byCode.has(w.dealer.dealerCode)) {
                byCode.set(w.dealer.dealerCode, w.dealer);
            }
        }
        return Array.from(byCode.values());
    }

    /** Colli: richiesta paginata server-side */
    private loadParcelsPage(): void {
        console.log('[loadParcelsPage] currentSearchFilters:', this.currentSearchFilters);
        this.isLoading = true;
        const cb = this.getClientsBrandsSelected();
        this.shipmentManagementService.searchParcelsPaged(cb, this.currentSearchFilters, this.currentPageRequest)
            .pipe(
                catchError(() => {
                    this.notificationService.error('Errore nel caricamento dei colli');
                    return of(this.emptyPage<Parcels>());
                }),
                finalize(() => {
                    this.isLoading = false;
                    this.isSearchLoading = false;
                    this.isAdvancedSearchLoading = false;
                })
            )
            .subscribe(res => {
                const content = res?.content ?? [];
                this.parcels = content;
                this.originalParcels = [...content];
                this.totalElements = res?.totalElements ?? 0;
                this.selectedParcelCodes.clear();
                // I dealer arrivano embedded in ogni collo: niente richiesta separata
                this.dealers = this.extractDealersFromParcels(content);
                this.populateUniqueUnifiedValues();
            });
    }

    /** Estrae i dealer (deduplicati per codice) dai colli della pagina corrente */
    private extractDealersFromParcels(parcels: Parcels[]): Dealer[] {
        const byCode = new Map<string, Dealer>();
        for (const p of parcels) {
            if (p.dealer?.dealerCode && !byCode.has(p.dealer.dealerCode)) {
                byCode.set(p.dealer.dealerCode, p.dealer);
            }
        }
        return Array.from(byCode.values());
    }

    /** Instradamenti / tours: richiesta paginata server-side */
    private loadToursPage(): void {
        console.log('[loadToursPage] currentSearchFilters:', this.currentSearchFilters);
        this.isLoading = true;
        const cb = this.getClientsBrandsSelected();
        this.shipmentManagementService.searchToursPaged(cb, this.currentSearchFilters, this.currentPageRequest)
            .pipe(
                catchError(() => {
                    this.notificationService.error('Errore nel caricamento degli instradamenti');
                    return of(this.emptyPage<RoutingSummary>());
                }),
                finalize(() => {
                    this.isLoading = false;
                    this.isSearchLoading = false;
                    this.isAdvancedSearchLoading = false;
                })
            )
            .subscribe(res => {
                const content = res?.content ?? [];
                this.routingSummaries = content;
                this.filteredRoutingSummaries = content;
                this.totalElements = res?.totalElements ?? 0;
            });
    }

    /** Resoconto: aggregati sull'intero set filtrato (endpoint /summary) */
    private loadSummary(): void {
        console.log('[loadSummary] currentSearchFilters:', this.currentSearchFilters);
        this.isLoadingSummaryReport = true;
        const cb = this.getClientsBrandsSelected();
        this.shipmentManagementService.getSummary(cb, this.currentSearchFilters)
            .pipe(
                catchError(() => of(null)),
                finalize(() => {
                    this.isLoadingSummaryReport = false;
                })
            )
            .subscribe(summary => {
                if (summary) {
                    this.summaryReport = summary;
                }
            });
    }

    getClientsBrandsSelected(): ClientsBrands[] {
        let clientsBrandsSelected: ClientsBrands[];
        if (this.filteredClients.length == 1) {
            this.clients = [...this.filteredClients];
        }
        if (this.clients.length > 0) {
            clientsBrandsSelected = this.clients.map(sc => {
                // trovo il client originale da clientsBrands
                const originalClient = this.clientsBrands.find(cb => cb.clientCode === sc.clientCode);


                var filteredBrandsFromClient: Brand[] = [];
                if (this.brands.length > 0) {
                    // brands effettivi = intersezione tra quelli del client e quelli selezionati
                    filteredBrandsFromClient = (originalClient?.brands || []).filter(b =>
                        this.brands.some(sb => sb.brandCode === b.brandCode)
                    );
                } else {
                    filteredBrandsFromClient = this.filteredBrands;
                }


                return {
                    clientCode: sc.clientCode,
                    clientName: sc.clientName,
                    brands: filteredBrandsFromClient
                } as ClientsBrands;
            });
        } else {
            clientsBrandsSelected = this.clientsBrands;
        }

        return clientsBrandsSelected;
    }

    /**
     * Load available carriers
     */
    loadCarriers(): void {
        const clientsBrandsSelected = this.getClientsBrandsSelected();

        this.shipmentManagementService.getCarriers(clientsBrandsSelected)
            .pipe(
                catchError(error => {
                    console.error('Error loading carriers:', error);
                    this.notificationService.error('Errore nel caricamento dei corrieri');
                    return of([]);
                })
            )
            .subscribe(carriers => {
                this.carriers = carriers;
                this.carrierSelectOptions = carriers.map(c => ({
                    value: c.carrierCode,
                    label: c.carrierName || c.carrierCode
                }));
            });
    }

    /**
     * Load warehouses for the warehouse filter dropdown
     */
    loadWarehouses(): void {
        this.anagraphicService.getAllWarehouses()
            .pipe(
                catchError(error => {
                    console.error('Error loading warehouses:', error);
                    this.notificationService.error('Errore nel caricamento dei magazzini');
                    return of([] as Warehouse[]);
                })
            )
            .subscribe(warehouses => {
                this.warehouses = warehouses;
                this.warehouseSelectOptions = warehouses.map(w => ({
                    value: w.warehouseCode,
                    label: w.warehouseName || w.warehouseCode
                }));
            });
    }

    /**
     * Load zones for delivery type selection
     */
    loadZones(): void {
        this.isLoadingZones = true;

        this.anagraphicService.getAllTourRoutings()
            .pipe(
                catchError(error => {
                    console.error('Error loading routings:', error);
                    this.notificationService.error('Errore nel caricamento degli instradamenti');
                    return of([]);
                }),
                finalize(() => {
                    this.isLoadingZones = false;
                })
            )
            .subscribe(zones => {
                this.zones = zones.map(z => z.tourRoutingCode);
                this.routings = zones;
                this.zoneSelectOptions = this.zones.map(z => ({value: z, label: z}));
            });
    }

    /**
     * Load all parcels for calculating totals
     */
    loadParcels(dateFrom?: Date, dateTo?: Date): void {
        const clientsBrandsSelected = this.getClientsBrandsSelected();

        // Extract waybill codes if waybills are loaded
        let waybillCodes: string[] | undefined;
        if (this.waybills.length > 0) {
            if (this.waybills.length > 999) {
                waybillCodes = [];
            } else {
                waybillCodes = this.waybills.map(w => w.waybillCode).filter(code => !!code);
            }
        } else {
            waybillCodes = undefined;
        }

        // Create request body with waybillCodes and date filters
        const requestBody: any = {};
        if (waybillCodes) {
            requestBody.waybillCodes = waybillCodes;
        }
        if (this.waybills.length === 0) {
            requestBody.dateFrom = null;
            requestBody.dateTo = null;
        } else {
            if (dateFrom) {
                requestBody.dateFrom = dateFrom;
            }
            if (dateTo) {
                requestBody.dateTo = dateTo;
            }
        }

        this.shipmentManagementService.getParcels(clientsBrandsSelected, requestBody)
            .pipe(
                catchError(error => {
                    console.error('Error loading parcels:', error);
                    this.notificationService.error('Errore nel caricamento dei colli');
                    return of([]);
                })
            )
            .subscribe(parcels => {
                console.log("colli : ", parcels);
                // Salva una copia dell'array originale completo prima di applicare filtri
                this.originalParcels = [...parcels];

                // Applica filtro per Codice Collo solo se il filtro non è vuoto
                if (this.filterParcelCode && this.filterParcelCode.trim() !== '') {
                    this.parcels = parcels.filter(parcel =>
                        parcel.parcelCode?.toLowerCase().includes(this.filterParcelCode.trim().toLowerCase())
                    );
                } else {
                    this.parcels = parcels;
                }

                // Popola i valori unici per i filtri dopo aver caricato i colli
                this.populateUniqueUnifiedValues();

            });
    }

    /**
     * Load dealers for address information
     * @returns Promise that resolves when dealers are loaded
     */
    loadDealers(): Promise<Dealer[]> {
        return new Promise<Dealer[]>((resolve, reject) => {
            const clientsBrandsSelected = this.getClientsBrandsSelected();

            // Extract unique dealer codes if waybills are loaded
            let dealerCodes = this.waybills.length > 0
                ? Array.from(new Set(
                    this.waybills
                        .map(w => w.waybillDealerCode)
                        .filter((code): code is string => !!code && code.trim() !== '')
                ))
                : undefined;

            this.shipmentManagementService.getDealers(clientsBrandsSelected, dealerCodes)
                .pipe(
                    catchError(error => {
                        console.error('Error loading dealers:', error);
                        this.notificationService.error('Errore nel caricamento dei rivenditori');
                        reject(error);
                        return of([]);
                    })
                )
                .subscribe(dealers => {
                    this.dealers = dealers;
                    if (dealerCodes) {
                        console.log(`Loaded ${dealers.length} dealers for ${dealerCodes.length} unique codes`);
                    }

                    // Popola i valori unici per i filtri dopo aver caricato i dealers
                    this.populateUniqueUnifiedValues();

                    resolve(dealers);
                });
        });
    }

    /**
     * Select a waybill for editing and open carrier assignment popup
     */
    selectWaybill(waybill: Waybill): void {
        // If the same waybill is already selected, just open the popup
        if (this.selectedWaybill?.waybillId === waybill.waybillId) {
            this.openDealerAssignmentPopup(waybill);
            return;
        }

        // Otherwise, select the new waybill and open the popup
        this.selectedWaybill = waybill;
        this.populateForm(waybill);
        this.openDealerAssignmentPopup(waybill);
    }

    /**
     * Deselect the currently selected waybill
     */
    deselectWaybill(): void {
        this.selectedWaybill = null;
        this.waybillForm.reset();
    }

    /**
     * Handle page change for pagination
     */
    // Vai a pagina
    isPageJumpOpen = false;
    pageJumpValue = '';
    @ViewChild('pageJumpInput') pageJumpInput?: ElementRef<HTMLInputElement>;
    // Vai all'elemento
    isItemJumpOpen = false;
    itemJumpValue = '';
    @ViewChild('itemJumpInput') itemJumpInput?: ElementRef<HTMLInputElement>;

    openItemJump(): void {
        this.itemJumpValue = '';
        this.isItemJumpOpen = true;
        setTimeout(() => this.itemJumpInput?.nativeElement.focus());
    }

    closeItemJump(): void {
        this.isItemJumpOpen = false;
    }

    /** Va alla pagina che contiene l'elemento con il numero digitato (limitato tra 1 e il totale) */
    applyItemJump(): void {
        const n = parseInt(this.itemJumpValue, 10);
        this.closeItemJump();
        if (!isNaN(n)) {
            const item = Math.min(Math.max(n, 1), Math.max(1, this.currentResultCount));
            this.onPageChange(Math.ceil(item / this.itemsPerPage));
        }
    }


    openPageJump(): void {
        this.pageJumpValue = '';
        this.isPageJumpOpen = true;
        setTimeout(() => this.pageJumpInput?.nativeElement.focus());
    }

    closePageJump(): void {
        this.isPageJumpOpen = false;
    }

    /** Va alla pagina digitata (limitata tra 1 e l'ultima) */
    applyPageJump(): void {
        const n = parseInt(this.pageJumpValue, 10);
        this.closePageJump();
        if (!isNaN(n)) {
            this.onPageChange(Math.min(Math.max(n, 1), this.getTotalPages()));
        }
    }

    onPageChange(page: number): void {
        if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
            this.currentPage = page;
            this.loadCurrentView(); // ricarica la pagina dal server
        }
    }

    /**
     * Get array of page numbers for pagination (like shipment-request)
     */
    getPageNumbers(): number[] {
        const totalPages = this.totalPages;
        const currentPage = this.currentPage;
        const pages: number[] = [];
        const maxPagesToShow = 5;

        let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
        let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

        // Adjust start page if we're near the end
        if (endPage - startPage + 1 < maxPagesToShow) {
            startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return pages;
    }

    /**
     * TrackBy function for pagination numbers
     */
    /** Numeri di pagina con le pagine estreme e i puntini (null) quando ce ne sono altre oltre la finestra */
    getPageItems(): (number | null)[] {
      const window = this.getPageNumbers();
      const total = this.getTotalPages();
      if (window.length === 0) { return []; }
      const items: (number | null)[] = [];
      const first = window[0];
      const last = window[window.length - 1];
      if (first > 1) {
        items.push(1);
        if (first > 2) { items.push(null); }
      }
      items.push(...window);
      if (last < total) {
        if (last < total - 1) { items.push(null); }
        items.push(total);
      }
      return items;
    }

    trackByPageItem(index: number, item: number | null): number {
      return item ?? -index - 1;
    }

    trackByPageNumber(index: number, page: number): number {
        return page;
    }

    /**
     * Numero colli per bolla: usa waybillParcelCount fornito dal backend,
     * con fallback sul conteggio dei colli caricati localmente.
     */
    getParcelsCountForWaybill(waybillCode: string): number {
        const waybill = this.waybills.find(w => w.waybillCode === waybillCode);
        if (waybill?.waybillParcelCount != null) {
            return waybill.waybillParcelCount;
        }
        return this.parcels.filter(parcel => parcel.parcelWaybillCode === waybillCode).length;
    }

    /**
     * Get dealer information by dealer code
     */
    getDealerByCode(dealerCode: string | undefined): Dealer | undefined {
        if (!dealerCode) return undefined;
        return this.dealers.find(d => d.dealerCode === dealerCode);
    }

    getClientByCode(clientCode: string | undefined) {
        if (!clientCode) return undefined;
        this.anagraphicService.getOprClient(clientCode).subscribe(client => {
            this.selectedClientForDetailPopup = client;
        })
    }

    /**
     * Get brand acronym by brand code from the brands list
     */
    getBrandAcronymByCode(brandCode: string | undefined): string {
        if (!brandCode) return '-';

        // Cerca il brand nella lista brands
        const brand = this.brands.find(b => b.brandCode === brandCode);
        if (!brand) return brandCode;

        // Restituisci l'acronimo se presente
        if ('brandAcronym' in brand && brand.brandAcronym) {
            return brand.brandAcronym;
        }

        // Altrimenti genera acronimo dal nome
        if (brand.brandName) {
            return brand.brandName
                .split(' ')
                .map(word => word.charAt(0).toUpperCase())
                .slice(0, 3)
                .join('');
        }

        // Fallback: prime 3 lettere del codice
        return brandCode.substring(0, 3).toUpperCase();
    }

    /**
     * Restituisce l'acronimo del client se presente, altrimenti il nome, a partire dal codice client
     */
    getClientNameByCode(clientCode: string | undefined): string {
        if (!clientCode) return '-';

        const client = this.clientsBrands?.find(cb => cb.clientCode === clientCode) as
            { clientName?: string; clientAcronym?: string } | undefined;

        if (client?.clientAcronym) {
            return client.clientAcronym;
        }
        if (client?.clientName) {
            return client.clientName;
        }

        const fallback = this.availableClients.find(c => c.clientCode === clientCode);
        return fallback?.clientName || clientCode;
    }

    /**
     * Mappa il parcel type da codice a descrizione
     */
    /** Collo danneggiato: parcelIsDamaged = true oppure 'Y' */
    isParcelDamaged(parcel: Parcels): boolean {
        const v = parcel.parcelIsDamaged;
        return v === true || (typeof v === 'string' && v.trim().toUpperCase() === 'Y');
    }

    /** Collo con misrouting: parcelHasMisrouting = true oppure 'Y' */
    hasParcelMisrouting(parcel: Parcels): boolean {
        const v = parcel.parcelHasMisrouting;
        return v === true || (typeof v === 'string' && v.trim().toUpperCase() === 'Y');
    }

    /** Variante grafica (pill .pt-type) e icona per lo stato della bolla */
    getWaybillStatusStyle(status: string | undefined): { variant: string; icon: string } {
        switch (status) {
            case 'ACTIVE':
                return {variant: 'primary', icon: 'fa-circle-check'};
            case 'PENDING':
                return {variant: 'pending', icon: 'fa-hourglass-half'};
            case 'UNSENT':
                return {variant: 'info', icon: 'fa-paper-plane'};
            case 'DELIVERED':
                return {variant: 'stock', icon: 'fa-truck-ramp-box'};
            case 'CANCELLED':
                return {variant: 'urgent', icon: 'fa-ban'};
            case 'NOT_DELIVERED':
                return {variant: 'urgent', icon: 'fa-triangle-exclamation'};
            case 'DELETED':
                return {variant: 'deleted', icon: 'fa-trash'};
            default:
                return {variant: 'unknown', icon: 'fa-circle-question'};
        }
    }

    // Opzioni del filtro Tipologia Collo (valore = codice)
    readonly parcelTypeOptions = [
        {value: 'URG', label: 'Urgente'},
        {value: 'STK', label: 'Stock'},
        {value: 'AZP', label: 'Azioni Promozionali'}
    ];

    getParcelTypeLabel(parcelType: string | undefined): string {
        if (!parcelType) return '-';

        switch (this.getParcelTypeVariant(parcelType)) {
            case 'urgent':
                return 'Urgente';
            case 'stock':
                return 'Stock';
            case 'promo':
                return 'Azioni Promozionali';
            default:
                return parcelType;
        }
    }

    /** Variante grafica del badge tipologia collo: urgent | stock | promo | unknown */
    getParcelTypeVariant(parcelType: string | undefined): 'urgent' | 'stock' | 'promo' | 'unknown' {
        // Il valore può arrivare come codice (URG/STK/AZP) o già come testo (Urgente/Stock)
        switch ((parcelType || '').trim().toUpperCase()) {
            case 'URG':
            case 'URGENTE':
            case 'U':
                return 'urgent';
            case 'STK':
            case 'STOCK':
            case 'S':
                return 'stock';
            case 'AZP':
            case 'AZIONI PROMOZIONALI':
                return 'promo';
            default:
                return 'unknown';
        }
    }

    /**
     * Populate form with waybill data
     */
    populateForm(waybill: Waybill): void {
        // Debug log per vedere i dati della waybill
        //console.log('🔍 Populating form with waybill:', {
        //  waybillId: waybill.waybillId,
        //  waybillCode: waybill.waybillCode,
        //  waybillEstimatedDeliveryDate: waybill.waybillEstimatedDeliveryDate
        //});

        // Separare data e ora dal waybillEstimatedDeliveryDate se presente
        let estimatedDate = '';
        let estimatedTime = '';

        if (waybill.waybillEstimatedDeliveryDate) {
            //console.log('✅ Found existing waybillEstimatedDeliveryDate:', waybill.waybillEstimatedDeliveryDate);
            const date = new Date(waybill.waybillEstimatedDeliveryDate);
            if (!isNaN(date.getTime())) {
                // Formato YYYY-MM-DD per evitare problemi di timezone
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                estimatedDate = `${year}-${month}-${day}`;

                estimatedTime = date.toTimeString().slice(0, 5);
                //console.log('📅 Extracted date:', estimatedDate, 'time:', estimatedTime);
            }
        } else {
            //console.log('❌ No waybillEstimatedDeliveryDate found');
        }

        // Salva la data nella proprietà separata
        this.selectedEstimatedDate = estimatedDate;
        //console.log('💾 Set selectedEstimatedDate to:', this.selectedEstimatedDate);

        // Aggiorna il form (senza il campo estimatedDeliveryDate)
        this.waybillForm.patchValue({
            waybillId: waybill.waybillId,
            waybillCode: waybill.waybillCode,
            waybillShipmentCode: waybill.waybillShipmentCode,
            waybillDate: waybill.waybillDate,
            waybillEstimatedDeliveryDate: waybill.waybillEstimatedDeliveryDate,
            waybillDeliveryMethod: waybill.waybillDeliveryMethod,
            waybillDeliveryType: waybill.waybillDeliveryType,
            waybillCarrierCode: waybill.waybillCarrierCode,
            waybillBrandCode: waybill.waybillBrandCode,
            waybillDealerCode: waybill.waybillDealerCode,
            waybillClientCode: waybill.waybillClientCode,
            waybillOriginLocation: waybill.waybillOriginLocation,
            waybillDestinationLocation: waybill.waybillDestinationLocation,
            waybillStatus: waybill.waybillStatus,
            waybillEstimatedDeliveryTime: estimatedTime
        });

        // Aggiorna Flatpickr in modo semplice se esiste
        if (this.flatpickrInstance && estimatedDate) {
            const [year, month, day] = estimatedDate.split('-').map(Number);
            const dateObj = new Date(year, month - 1, day);
            this.flatpickrInstance.setDate(dateObj);
        }

        // Aggiorna automaticamente il campo della bolla dopo aver popolato il form
        this.updateWaybillEstimatedDelivery();
    }

    /**
     * Utility function to convert date and time to ISO format consistent with waybillDate
     */
    private convertDateTimeToISO(dateString: string, timeString?: string): string {
        if (!dateString) return '';

        // Debug log dei valori in input
        //console.log('convertDateTimeToISO input:', { dateString, timeString });

        let year: number, month: number, day: number;

        // Gestione di diversi formati di data
        if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
            // Formato DD/MM/YYYY (dal datepicker italiano)
            const [dayStr, monthStr, yearStr] = dateString.split('/');
            day = parseInt(dayStr);
            month = parseInt(monthStr);
            year = parseInt(yearStr);
        } else if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
            // Formato YYYY-MM-DD (standard ISO)
            const [yearStr, monthStr, dayStr] = dateString.split('-');
            year = parseInt(yearStr);
            month = parseInt(monthStr);
            day = parseInt(dayStr);
        } else {
            console.error('Invalid date format:', dateString, 'Expected DD/MM/YYYY or YYYY-MM-DD');
            return '';
        }

        // Validazione dei valori numerici
        if (isNaN(year) || isNaN(month) || isNaN(day) ||
            year < 1900 || year > 2100 ||
            month < 1 || month > 12 ||
            day < 1 || day > 31) {
            console.error('Invalid date values:', {year, month, day});
            return '';
        }

        if (timeString && timeString.trim()) {
            // Validazione del formato ora
            if (!timeString.match(/^\d{2}:\d{2}$/)) {
                console.error('Invalid time format:', timeString);
                return '';
            }

            const [hours, minutes] = timeString.split(':').map(Number);

            // Validazione dei valori di tempo
            if (isNaN(hours) || isNaN(minutes) ||
                hours < 0 || hours > 23 ||
                minutes < 0 || minutes > 59) {
                console.error('Invalid time values:', {hours, minutes});
                return '';
            }

            const combinedDateTime = new Date(year, month - 1, day, hours, minutes, 0, 0);

            // Validazione della data creata
            if (isNaN(combinedDateTime.getTime())) {
                //console.error('Invalid combined date:', { year, month, day, hours, minutes });
                return '';
            }

            //console.log('Created combined date:', combinedDateTime.toISOString());
            return combinedDateTime.toISOString();
        } else {
            // Solo data, imposta ore a mezzanotte (come fa waybillDate)
            const dateOnly = new Date(year, month - 1, day, 0, 0, 0, 0);

            // Validazione della data creata
            if (isNaN(dateOnly.getTime())) {
                //console.error('Invalid date only:', { year, month, day });
                return '';
            }

            //console.log('Created date only:', dateOnly.toISOString());
            return dateOnly.toISOString();
        }
    }

    /**
     * Submit the waybill form with carrier assignment
     */
    onSubmit(): void {
        if (this.waybillForm.valid && this.selectedWaybill) {
            this.isSubmitting = true;

            const formData = this.waybillForm.value;

            // Usare la nostra proprietà separata per la data
            // Combinare data e ora in estimatedeliverydate utilizzando il formato ISO standard
            if (this.selectedEstimatedDate) {
                const estimatedDeliveryDate = this.convertDateTimeToISO(
                    this.selectedEstimatedDate,
                    formData.waybillEstimatedDeliveryTime
                );

                // Validazione del risultato della conversione
                if (estimatedDeliveryDate) {
                    formData.waybillEstimatedDeliveryDate = estimatedDeliveryDate;
                } else {
                    console.error('Failed to convert date/time, skipping waybillEstimatedDeliveryDate');
                    this.notificationService.error('Errore nel formato della data di consegna stimata');
                    this.isSubmitting = false;
                    return;
                }
            }

            // Rimuovere i campi temporanei prima dell'invio
            delete formData.waybillEstimatedDeliveryTime;

            // Preparare i dati della bolla con tutti i parametri
            const waybillData = {
                ...formData,
                waybillId: this.selectedWaybill.waybillId,
                waybillDate: this.selectedWaybill.waybillDate,
                waybillOriginLocation: this.selectedWaybill.waybillOriginLocation,
                waybillDestinationLocation: this.selectedWaybill.waybillDestinationLocation,
                waybillStatus: this.selectedWaybill.waybillStatus,
                waybillEstimatedDeliveryDate: formData.waybillEstimatedDeliveryDate
            };

            // Log per debug - confronto formati date
            // console.log('Final waybill data being sent:', {
            //   waybillEstimatedDeliveryDate: waybillData.waybillEstimatedDeliveryDate,
            //   waybillDate: this.selectedWaybill.waybillDate,
            //   selectedDate: this.selectedEstimatedDate,
            //   selectedTime: this.waybillForm.get('waybillEstimatedDeliveryTime')?.value,
            //   formatComparison: {
            //     estimated: typeof waybillData.waybillEstimatedDeliveryDate,
            //     waybill: typeof this.selectedWaybill.waybillDate,
            //     estimatedValue: waybillData.waybillEstimatedDeliveryDate,
            //     waybillValue: this.selectedWaybill.waybillDate
            //   }
            // });

            // Chiamata all'endpoint /update-test
            this.shipmentManagementService.updateWaybill(waybillData)
                .pipe(
                    catchError(error => {
                        console.error('Error updating waybill:', error);
                        const message = this.errorHelperService.extractBackendMessage(error);
                        this.notificationService.error(`Errore nell'aggiornamento della bolla: ${message}`);
                        this.isSubmitting = false;
                        return of(null);
                    }),
                    finalize(() => {
                        this.isSubmitting = false;
                    })
                )
                .subscribe(response => {
                    if (response) {
                        // console.log('Waybill updated successfully:', response);

                        // Update the selected waybill in the list
                        const index = this.waybills.findIndex(w => w.waybillId === this.selectedWaybill!.waybillId);
                        if (index !== -1) {
                            this.waybills[index] = {...this.waybills[index], ...waybillData};
                        }

                        // Show success message
                        //this.notificationService.success('Bolla aggiornata con successo');

                        // Close the popup and deselect waybill after successful update
                        this.closeCarrierAssignmentPopup();
                    }
                });
        }
    }

    /**
     * Get filtered parcels based on search term
     */
    /** La colonna "Bolla Originale" compare solo se almeno un collo in elenco ha la bolla originale valorizzata */
    get hasOriginalWaybillCodes(): boolean {
        return this.filteredParcels.some(parcel => !!parcel.parcelOriginalWaybillCode);
    }

    get filteredParcels(): Parcels[] {
        const parcels = this.parcels ?? [];
        if (!this.searchTerm) {
            return parcels;
        }

        const searchTermLower = this.searchTerm.toLowerCase();

        return parcels.filter(parcel => {
            // First, check all the parcel fields
            const parcelMatches = Object.keys(parcel).some(key => {
                const value = (parcel as any)[key];
                // Check if the value is a string and contains the search term
                if (typeof value === 'string') {
                    return value.toLowerCase().includes(searchTermLower);
                }
                // Check if the value is a number and its string representation contains the search term
                if (typeof value === 'number') {
                    return value.toString().includes(searchTermLower);
                }
                return false;
            });

            if (parcelMatches) {
                return true;
            }

            // If not found in parcel fields, check dealer fields (if parcel has a dealer code)
            if (parcel.parcelDealerCode) {
                const dealer = this.getDealerByCode(parcel.parcelDealerCode);
                if (dealer) {
                    // Check common dealer fields that users might search for
                    if (
                        (dealer.dealerName && dealer.dealerName.toLowerCase().includes(searchTermLower)) ||
                        (dealer.dealerLegalName && dealer.dealerLegalName.toLowerCase().includes(searchTermLower)) ||
                        (dealer.dealerVatNumber && dealer.dealerVatNumber.toLowerCase().includes(searchTermLower)) ||
                        // Address fields
                        (dealer.dealerOperatingAddressStreet && dealer.dealerOperatingAddressStreet.toLowerCase().includes(searchTermLower)) ||
                        (dealer.dealerOperatingAddressCity && dealer.dealerOperatingAddressCity.toLowerCase().includes(searchTermLower)) ||
                        (dealer.dealerOperatingAddressProvince && dealer.dealerOperatingAddressProvince.toLowerCase().includes(searchTermLower)) ||
                        (dealer.dealerOperatingAddressZip && dealer.dealerOperatingAddressZip.toLowerCase().includes(searchTermLower)) ||
                        // Legal address fields
                        (dealer.dealerLegalAddressStreet && dealer.dealerLegalAddressStreet.toLowerCase().includes(searchTermLower)) ||
                        (dealer.dealerLegalAddressCity && dealer.dealerLegalAddressCity.toLowerCase().includes(searchTermLower)) ||
                        (dealer.dealerLegalAddressProvince && dealer.dealerLegalAddressProvince.toLowerCase().includes(searchTermLower)) ||
                        (dealer.dealerLegalAddressZip && dealer.dealerLegalAddressZip.toLowerCase().includes(searchTermLower))
                    ) {
                        return true;
                    }
                }
            }

            return false;
        });
    }

    /**
     * Get filtered waybills based on search term
     */
    get filteredWaybills(): Waybill[] {
        const waybills = this.waybills ?? [];
        if (!this.searchTerm) {
            return waybills;
        }

        const searchTermLower = this.searchTerm.toLowerCase();

        return waybills.filter(waybill => {
            // Cerca in tutti i campi di testo della bolla
            const waybillMatches = Object.keys(waybill).some(key => {
                const value = (waybill as any)[key];
                // Verifica se il valore è una stringa e contiene il termine di ricerca
                if (typeof value === 'string') {
                    return value.toLowerCase().includes(searchTermLower);
                }
                // Verifica se il valore è un numero e la sua rappresentazione in stringa contiene il termine di ricerca
                if (typeof value === 'number') {
                    return value.toString().includes(searchTermLower);
                }
                return false;
            });

            if (waybillMatches) {
                return true;
            }

            // Se non trovato nei campi della waybill, controlla i campi dealer (se la waybill ha un dealer code)
            if (waybill.waybillDealerCode) {
                const dealer = this.getDealerByCode(waybill.waybillDealerCode);
                if (dealer) {
                    // Controlla i campi comuni del dealer che gli utenti potrebbero cercare
                    if (
                        (dealer.dealerName && dealer.dealerName.toLowerCase().includes(searchTermLower)) ||
                        (dealer.dealerLegalName && dealer.dealerLegalName.toLowerCase().includes(searchTermLower)) ||
                        (dealer.dealerVatNumber && dealer.dealerVatNumber.toLowerCase().includes(searchTermLower)) ||
                        // Campi indirizzo
                        (dealer.dealerOperatingAddressStreet && dealer.dealerOperatingAddressStreet.toLowerCase().includes(searchTermLower)) ||
                        (dealer.dealerOperatingAddressCity && dealer.dealerOperatingAddressCity.toLowerCase().includes(searchTermLower)) ||
                        (dealer.dealerOperatingAddressProvince && dealer.dealerOperatingAddressProvince.toLowerCase().includes(searchTermLower)) ||
                        (dealer.dealerOperatingAddressZip && dealer.dealerOperatingAddressZip.toLowerCase().includes(searchTermLower)) ||
                        // Campi indirizzo legale
                        (dealer.dealerLegalAddressStreet && dealer.dealerLegalAddressStreet.toLowerCase().includes(searchTermLower)) ||
                        (dealer.dealerLegalAddressCity && dealer.dealerLegalAddressCity.toLowerCase().includes(searchTermLower)) ||
                        (dealer.dealerLegalAddressProvince && dealer.dealerLegalAddressProvince.toLowerCase().includes(searchTermLower)) ||
                        (dealer.dealerLegalAddressZip && dealer.dealerLegalAddressZip.toLowerCase().includes(searchTermLower))
                    ) {
                        return true;
                    }
                }
            }

            return false;
        });
    }

    /**
     * Sort waybills by column (like shipment-request) - 3 states: none, asc, desc
     */
    sortBy(column: string): void {
        if (this.sortColumn === column) {
            // Cycle through: asc -> desc -> none
            if (this.sortDirection === 'asc') {
                this.sortDirection = 'desc';
            } else if (this.sortDirection === 'desc') {
                this.sortDirection = '';
                this.sortColumn = '';
            }
        } else {
            this.sortColumn = column;
            this.sortDirection = 'asc';
        }

        // Sort server-side: ricarica la pagina corrente con i nuovi parametri di ordinamento.
        // currentPageRequest legge già sortColumn/sortDirection.
        this.currentPage = 1;
        this.loadCurrentView();
    }

    /**
     * Ritorna una copia di `waybills` ordinata in base a sortColumn/sortDirection.
     * Se il sort è disattivo, ritorna i dati nell'ordine originale ricevuto.
     */
    private sortWaybillsArray(waybills: Waybill[]): Waybill[] {
        if (!this.sortColumn || !this.sortDirection) {
            return waybills;
        }
        const column = this.sortColumn;
        return [...waybills].sort((a, b) => {
            let aValue: any = '';
            let bValue: any = '';

            switch (column) {
                case 'companyName':
                    const dealerA = this.getDealerByCode(a.waybillDealerCode);
                    const dealerB = this.getDealerByCode(b.waybillDealerCode);
                    aValue = dealerA?.dealerName || '';
                    bValue = dealerB?.dealerName || '';
                    break;
                case 'address':
                    const dealerAddressA = this.getDealerByCode(a.waybillDealerCode);
                    const dealerAddressB = this.getDealerByCode(b.waybillDealerCode);
                    aValue = `${dealerAddressA?.dealerOperatingAddressStreet || ''} ${dealerAddressA?.dealerOperatingAddressNumber || ''}`.trim();
                    bValue = `${dealerAddressB?.dealerOperatingAddressStreet || ''} ${dealerAddressB?.dealerOperatingAddressNumber || ''}`.trim();
                    break;
                case 'city':
                    const dealerCityA = this.getDealerByCode(a.waybillDealerCode);
                    const dealerCityB = this.getDealerByCode(b.waybillDealerCode);
                    aValue = dealerCityA?.dealerOperatingAddressCity || '';
                    bValue = dealerCityB?.dealerOperatingAddressCity || '';
                    break;
                case 'province':
                    const dealerProvinceA = this.getDealerByCode(a.waybillDealerCode);
                    const dealerProvinceB = this.getDealerByCode(b.waybillDealerCode);
                    aValue = dealerProvinceA?.dealerOperatingAddressProvince || '';
                    bValue = dealerProvinceB?.dealerOperatingAddressProvince || '';
                    break;
                case 'parcelsCount':
                    aValue = this.getParcelsCountForWaybill(a.waybillCode);
                    bValue = this.getParcelsCountForWaybill(b.waybillCode);
                    break;
                case 'waybillDate':
                    aValue = a.waybillDate ? new Date(a.waybillDate) : new Date(0);
                    bValue = b.waybillDate ? new Date(b.waybillDate) : new Date(0);
                    break;
                default:
                    aValue = (a as any)[column] || '';
                    bValue = (b as any)[column] || '';
                    break;
            }

            if (column === 'parcelsCount') {
                return this.sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
            } else if (column === 'waybillDate') {
                return this.sortDirection === 'asc' ? aValue.getTime() - bValue.getTime() : bValue.getTime() - aValue.getTime();
            }

            if (this.sortDirection === 'asc') {
                return aValue.toString().localeCompare(bValue.toString(), 'it', {numeric: true});
            } else {
                return bValue.toString().localeCompare(aValue.toString(), 'it', {numeric: true});
            }
        });
    }

    /**
     * Ritorna una copia di `parcels` ordinata in base a sortColumn/sortDirection.
     * Se il sort è disattivo, ritorna i dati nell'ordine originale ricevuto.
     */
    private sortParcelsArray(parcels: Parcels[]): Parcels[] {
        if (!this.sortColumn || !this.sortDirection) {
            return parcels;
        }
        const column = this.sortColumn;
        return [...parcels].sort((a, b) => {
            let aValue: any = '';
            let bValue: any = '';

            switch (column) {
                case 'parcelDate':
                    aValue = a.parcelDate ? new Date(a.parcelDate) : new Date(0);
                    bValue = b.parcelDate ? new Date(b.parcelDate) : new Date(0);
                    break;
                case 'parcelWeight':
                case 'parcelVolume':
                case 'parcelLength':
                case 'parcelWidth':
                case 'parcelHeight':
                    aValue = parseFloat((a as any)[column] || 0);
                    bValue = parseFloat((b as any)[column] || 0);
                    break;
                default:
                    aValue = (a as any)[column] || '';
                    bValue = (b as any)[column] || '';
                    break;
            }

            if (['parcelWeight', 'parcelVolume', 'parcelLength', 'parcelWidth', 'parcelHeight'].includes(column)) {
                return this.sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
            } else if (column === 'parcelDate') {
                return this.sortDirection === 'asc' ? aValue.getTime() - bValue.getTime() : bValue.getTime() - aValue.getTime();
            }

            if (this.sortDirection === 'asc') {
                return aValue.toString().localeCompare(bValue.toString(), 'it', {numeric: true});
            } else {
                return bValue.toString().localeCompare(aValue.toString(), 'it', {numeric: true});
            }
        });
    }

    /**
     * Ricalcola this.waybills e this.parcels sui dati della pagina corrente.
     * Il sort è gestito server-side: i dati arrivano già ordinati da loadCurrentView().
     */
    private refreshTableData(): void {
        this.waybills = [...this.waybills];
        this.parcels = [...this.parcels];
    }

    /**
     * Get sort icon class for column (like shipment-request)
     */
    getSortIcon(column: string): string {
        if (this.sortColumn !== column) {
            return 'fas fa-sort';
        }
        return this.sortDirection === 'asc' ? 'fas fa-sort-up text-primary' : 'fas fa-sort-down text-primary';
    }

    /**
     * Resoconto aggregato (popolato dall'endpoint /summary sull'intero set filtrato)
     */
    summaryReport: ShipmentSummary = {
        totalWaybills: 0,
        totalParcels: 0,
        totalDealers: 0,
        totalWeight: 0,
        totalVolume: 0,
        totalRoutings: 0
    };

    /** True mentre la richiesta /summary è in corso */
    isLoadingSummaryReport = false;

    /**
     * Parte intera di un numero formattato (locale 'it'), senza separatore decimale
     */
    getIntegerPart(value: number, digitsInfo: string): string {
        const formatted = formatNumber(value || 0, 'it', digitsInfo);
        const separatorIndex = formatted.lastIndexOf(',');
        return separatorIndex === -1 ? formatted : formatted.substring(0, separatorIndex);
    }

    /**
     * Parte decimale di un numero formattato (locale 'it'), separatore incluso
     */
    getDecimalPart(value: number, digitsInfo: string): string {
        const formatted = formatNumber(value || 0, 'it', digitsInfo);
        const separatorIndex = formatted.lastIndexOf(',');
        return separatorIndex === -1 ? '' : formatted.substring(separatorIndex);
    }

    /**
     * Check if field is invalid
     */
    isFieldInvalid(fieldName: string): boolean {
        const field = this.waybillForm.get(fieldName);
        return !!(field && field.invalid && (field.dirty || field.touched));
    }

    /**
     * Get field error message
     */
    getFieldError(fieldName: string): string {
        const field = this.waybillForm.get(fieldName);
        if (field && field.errors) {
            if (field.errors['required']) {
                return this.translate.instant('VALIDATION.REQUIRED');
            }
            // Add more validation messages as needed
        }
        return '';
    }

    /**
     * Get field error message for parcel form
     */
    getParcelFieldError(fieldName: string): string {
        const field = this.parcelForm.get(fieldName);
        if (!field || !field.errors || !field.touched) return '';

        // Check if it's a dimension field (length, width, height)
        const isDimensionField = ['parcelLength', 'parcelWidth', 'parcelHeight'].includes(fieldName);

        if (field.errors['required']) {
            return this.translate.instant('VALIDATION.REQUIRED');
        }

        if (field.errors['invalidNumber']) {
            return this.translate.instant('VALIDATION.INVALID_NUMBER');
        }

        if (field.errors['maxValue']) {
            return this.translate.instant('VALIDATION.MAX_VALUE_EXCEEDED');
        }

        if (field.errors['maxValueDimension']) {
            return this.translate.instant(isDimensionField ?
                'VALIDATION.DIMENSION.MAX_VALUE_EXCEEDED' : 'VALIDATION.MAX_VALUE_EXCEEDED');
        }

        if (field.errors['minWeightVolume']) {
            return this.translate.instant('VALIDATION.MIN_VALUE');
        }

        if (field.errors['minValue']) {
            return this.translate.instant(isDimensionField ?
                'VALIDATION.DIMENSION.NEGATIVE_NOT_ALLOWED' : 'VALIDATION.NEGATIVE_NOT_ALLOWED');
        }

        if (field.errors['maxDecimals'] || field.errors['maxDecimalsVolume']) {
            return this.translate.instant('VALIDATION.MAX_DECIMALS');
        }

        if (field.errors['maxDecimalsDimension']) {
            return this.translate.instant('VALIDATION.DIMENSION.MAX_DECIMALS');
        }

        return '';
    }

    // Custom validator for weight with minimum 0.001
    private weightVolumeValidator(control: any) {
        if (!control.value && control.value !== 0) {
            return null; // Let required validator handle empty values
        }

        const value = parseFloat(control.value);

        // Check if it's a valid number
        if (isNaN(value)) {
            return {invalidNumber: true};
        }

        // Check maximum value
        if (value > 99999.999) {
            return {maxValue: true};
        }

        // Check minimum value (must be at least 0.001)
        if (value < 0.001) {
            return {minWeightVolume: true};
        }

        // Check decimal places (max 3)
        const stringValue = control.value.toString();
        if (stringValue.includes('.')) {
            const decimalPart = stringValue.split('.')[1];
            if (decimalPart && decimalPart.length > 3) {
                return {maxDecimals: true};
            }
        }

        return null;
    }

    // Custom validator specifically for volume with 4 decimal places
    private volumeValidator(control: any) {
        if (!control.value && control.value !== 0) {
            return null; // Let required validator handle empty values
        }

        const value = parseFloat(control.value);

        // Check if it's a valid number
        if (isNaN(value)) {
            return {invalidNumber: true};
        }

        // Check maximum value for volume (99999.9999)
        if (value > 99999.9999) {
            return {maxValue: true};
        }

        // Check minimum value (must be at least 0.0001)
        if (value < 0.0001) {
            return {minWeightVolume: true};
        }

        // Check decimal places (max 4 for volume)
        const stringValue = control.value.toString();
        if (stringValue.includes('.')) {
            const decimalPart = stringValue.split('.')[1];
            if (decimalPart && decimalPart.length > 4) {
                return {maxDecimalsVolume: true};
            }
        }

        return null;
    }

    // Custom validator for dimensions (length, width, height) with max 1 decimal place
    private dimensionValidator(control: any) {
        if (!control.value && control.value !== 0) {
            return null; // Allow empty values since dimensions are optional
        }

        const value = parseFloat(control.value);

        // Check if it's a valid number
        if (isNaN(value)) {
            return {invalidNumber: true};
        }

        // Check maximum value
        if (value > 99999.99) {
            return {maxValueDimension: true};
        }

        // Check minimum value (must be positive or zero)
        if (value < 0) {
            return {minValue: true};
        }

        // Check decimal places (max 1 for dimensions)
        const stringValue = control.value.toString();
        if (stringValue.includes('.')) {
            const decimalPart = stringValue.split('.')[1];
            if (decimalPart && decimalPart.length > 1) {
                return {maxDecimalsDimension: true};
            }
        }

        return null;
    }

    /**
     * Get the currently selected carrier object
     */
    getSelectedCarrier(): Carriers | null {
        const selectedCarrierCode = this.waybillForm.get('waybillCarrierCode')?.value;
        if (!selectedCarrierCode) {
            return null;
        }
        return this.carriers.find(carrier => carrier.carrierCode === selectedCarrierCode) || null;
    }

    /**
     * Select a carrier and update the form
     */
    selectCarrier(carrier: Carriers | null, event: Event): void {
        event.preventDefault();
        const carrierCode = carrier ? carrier.carrierCode : '';
        this.waybillForm.patchValue({waybillCarrierCode: carrierCode});

        // Close the dropdown manually
        const dropdown = document.getElementById('carrierDropdown');
        if (dropdown) {
            const bsDropdown = (window as any).bootstrap?.Dropdown?.getInstance(dropdown);
            if (bsDropdown) {
                bsDropdown.hide();
            }
        }
    }

    /**
     * Clear search term
     */
    clearSearch(): void {
        this.searchTerm = '';
    }

    /**
     * Costruisce l'oggetto filtri dai campi della prima sezione (con date già risolte
     * in dateFrom/dateTo). Questi filtri vengono inviati a tutte e 3 le richieste.
     */
    private buildSearchFilters(): any {
        const isAdvancedSearch = !this.isAdvancedFiltersCollapsed;

        // Tutti gli input testuali dei filtri vengono ripuliti dagli spazi (anche nel campo mostrato)
        this.filterParcelCode = (this.filterParcelCode || '').trim();
        this.filterWaybillCode = (this.filterWaybillCode || '').trim();
        this.filterDealerCode = (this.filterDealerCode || '').trim();
        this.filterCompanyName = (this.filterCompanyName || '').trim();
        this.filterAddress = (this.filterAddress || '').trim();
        this.filterCity = (this.filterCity || '').trim();
        this.filterProvince = (this.filterProvince || '').trim();
        this.filterWeight = (this.filterWeight || '').trim();
        this.filterVolume = (this.filterVolume || '').trim();

        const filters: any = {
            parcelCode: this.filterParcelCode || null,
            waybillCode: this.filterWaybillCode || null,
            dealerCode: this.filterDealerCode || null
        };

        if (this.filterWaybillDeliveryMethod && this.filterWaybillDeliveryMethod.trim() !== '') {
            filters.filterWaybillDeliveryMethod = this.filterWaybillDeliveryMethod;
        }
        if (this.filterStatus && this.filterStatus.trim() !== '') {
            filters.status = this.filterStatus;
        }

        // Data: intervallo dai Filtri Aggiuntivi se presente, altrimenti la data singola
        if (isAdvancedSearch && (this.selectedFilterDateFrom || this.selectedFilterDateTo)) {
            if (this.selectedFilterDateFrom) filters.dateFrom = this.selectedFilterDateFrom;
            if (this.selectedFilterDateTo) filters.dateTo = this.selectedFilterDateTo;
        } else if (this.selectedFilterDate) {
            filters.date = this.selectedFilterDate;
        }

        if (isAdvancedSearch) {
            if (this.filterWarehouse.length > 0) filters.warehouseCodes = [...this.filterWarehouse];
            if (this.filterCarrier.length > 0) filters.carrierCodes = [...this.filterCarrier];
            if (this.filterCustomer.length > 0) filters.clientCodes = [...this.filterCustomer];
            if (this.filterBrand.length > 0) filters.brandCodes = [...this.filterBrand];
            if (this.filterRouting.length > 0) filters.zones = [...this.filterRouting];
            if (this.selectedFilterEstimatedDelivery) filters.estimatedDelivery = this.selectedFilterEstimatedDelivery;
            if (this.selectedFilterActualDelivery) filters.actualDelivery = this.selectedFilterActualDelivery;
            if (this.filterCompanyName && this.filterCompanyName.trim() !== '') filters.companyName = this.filterCompanyName.trim();
            if (this.filterAddress && this.filterAddress.trim() !== '') filters.address = this.filterAddress.trim();
            if (this.filterCity && this.filterCity.trim() !== '') filters.city = this.filterCity.trim();
            if (this.filterProvince && this.filterProvince.trim() !== '') filters.province = this.filterProvince.trim();
            if (this.filterParcelType && this.filterParcelType.trim() !== '') filters.parcelType = this.filterParcelType.trim();
            if (this.filterWeight && this.filterWeight.trim() !== '') {
                filters.weight = this.filterWeight.trim();
                filters.weightOperator = this.filterWeightOperator;
            }
            if (this.filterVolume && this.filterVolume.trim() !== '') {
                filters.volume = this.filterVolume.trim();
                filters.volumeOperator = this.filterVolumeOperator;
            }
            // Presente solo da attivo: spento significa "nessun filtro sui danneggiati"
            if (this.filterDamagedParcels) {
                filters.damagedParcels = true;
            }
            // Presente solo con "con immagine" (true) o "senza immagine" (false)
            if (this.filterParcelImages !== 'ALL') {
                filters.hasImages = this.filterParcelImages === 'WITH';
            }
            // Presente solo con "inviate" (true) o "non inviate" (false)
            if (this.filterAxylogSent !== 'ALL') {
                filters.sentToAxylog = this.filterAxylogSent === 'SENT';
            }
            // Presente solo da attivo: spento significa "nessun filtro sul misrouting"
            if (this.filterMisroutingParcels) {
                filters.misroutingParcels = true;
            }
        }

        return this.resolveDateFilters(filters);
    }

    /**
     * Search: applica i filtri della prima sezione, riparte da pagina 1 e carica
     * la vista corrente (paginata) + il resoconto.
     */
    searchWaybills(): void {
        const isAdvancedSearch = !this.isAdvancedFiltersCollapsed;
        if (isAdvancedSearch) {
            this.isAdvancedSearchLoading = true;
        } else {
            this.isSearchLoading = true;
        }

        this.currentSearchFilters = this.buildSearchFilters();
        console.log(
            `[ShipmentManagement] ${isAdvancedSearch ? 'Ricerca avanzata' : 'Ricerca'} - parametri inviati al backend:`,
            JSON.parse(JSON.stringify(this.currentSearchFilters))
        );
        this.currentPage = 1;
        this.loadCurrentView();
        this.loadSummary();
    }

    /**
     * Raccoglie i filtri attualmente valorizzati con etichetta e valore leggibile.
     */
    private collectActiveFilters(): { label: string; value: string }[] {
        const result: { label: string; value: string }[] = [];
        const add = (label: string, value: string | null | undefined) => {
            if (value !== null && value !== undefined && String(value).trim() !== '') {
                result.push({label, value: String(value).trim()});
            }
        };
        const labelOf = (options: SearchableSelectOption[], val: string): string => {
            const found = options.find(o => o.value === val);
            return found ? found.label : val;
        };
        const fmtDate = (d: Date | null): string =>
            d ? d.toLocaleDateString('it-IT', {day: '2-digit', month: '2-digit', year: 'numeric'}) : '';

        // Filtri principali
        add('Codice Collo', this.filterParcelCode);
        add('Codice Bolla', this.filterWaybillCode);
        add('Codice Dealer', this.filterDealerCode);
        if (this.filterStatus) {
            add('Stato', labelOf(this.statusOptions, this.filterStatus));
        }

        // Filtri aggiuntivi (solo se la ricerca avanzata è attiva)
        if (!this.isAdvancedFiltersCollapsed) {
            if (this.selectedFilterDateFrom) add('Data Affido da', fmtDate(this.selectedFilterDateFrom));
            if (this.selectedFilterDateTo) add('Data Affido a', fmtDate(this.selectedFilterDateTo));
            if (this.selectedFilterEstimatedDelivery) add('Data Consegna stimata', fmtDate(this.selectedFilterEstimatedDelivery));
            if (this.selectedFilterActualDelivery) add('Data Consegna effettiva', fmtDate(this.selectedFilterActualDelivery));
            const labelsOf = (options: SearchableSelectOption[], vals: string[]): string =>
                vals.map(v => labelOf(options, v)).join(', ');
            add('Magazzino', labelsOf(this.warehouseSelectOptions, this.filterWarehouse));
            add('Corriere', labelsOf(this.carrierSelectOptions, this.filterCarrier));
            add('Cliente', labelsOf(this.clientSelectOptions, this.filterCustomer));
            add('Brand', labelsOf(this.brandSelectOptions, this.filterBrand));
            add('Instradamento', this.filterRouting.join(', '));
            add('Ragione Sociale', this.filterCompanyName);
            add('Indirizzo', this.filterAddress);
            add('Città', this.filterCity);
            add('Provincia', this.filterProvince);
            add('Tipologia Collo', this.filterParcelType ? this.getParcelTypeLabel(this.filterParcelType) : '');
            if (this.filterWeight && this.filterWeight.trim() !== '') {
                add('Peso', `${this.filterWeightOperator} ${this.filterWeight.trim()} kg`);
            }
            if (this.filterVolume && this.filterVolume.trim() !== '') {
                add('Volume', `${this.filterVolumeOperator} ${this.filterVolume.trim()} m³`);
            }
            if (this.filterDamagedParcels) {
                add('Colli Danneggiati', 'Solo danneggiati');
            }
            if (this.filterParcelImages !== 'ALL') {
                add('Immagini Colli', this.parcelImagesFilterLabel);
            }
            if (this.filterAxylogSent !== 'ALL') {
                add('Inviato in Axylog', this.axylogSentOption.label);
            }
            if (this.filterMisroutingParcels) {
                add('Colli in Misrouting', 'Solo in misrouting');
            }
        }

        return result;
    }

    /**
     * Salva subito i filtri attualmente valorizzati, senza chiedere un nome:
     * il nome è facoltativo e si assegna dopo, dalla matita sulla card.
     * Un insieme di filtri identico a uno già salvato non viene duplicato:
     * la card esistente (con il suo eventuale nome) viene portata in cima.
     */
    saveCurrentSearch(): void {
        const filters = this.collectActiveFilters();
        if (filters.length === 0) {
            this.notificationService.warning('Imposta almeno un filtro prima di salvarlo');
            return;
        }

        const signature = this.savedSearchSignature(filters);
        const duplicate = this.savedSearches.find(
            s => this.savedSearchSignature(s.filters) === signature
        );
        const entry: SavedSearch = {
            name: duplicate?.name,
            timestamp: Date.now(),
            filters,
            state: this.buildSavedSearchState()
        };

        const withoutDuplicate = this.savedSearches.filter(
            s => this.savedSearchSignature(s.filters) !== signature
        );
        this.savedSearches = [entry, ...withoutDuplicate].slice(0, this.MAX_SAVED_SEARCHES);
        this.persistSavedSearches();

        const label = this.savedSearchDisplayName(entry);
        this.notificationService.success(
            duplicate
                ? `Filtri "${label}" già salvati: spostati in prima posizione`
                : `Filtri "${label}" salvati`
        );
    }

    /**
     * Apre il popup per dare o cambiare il nome di una card salvata.
     */
    openRenameSavedSearch(search: SavedSearch, event: MouseEvent): void {
        event.stopPropagation();
        this.editingSavedSearch = search;
        this.saveSearchName = (search.name || '').trim();
        this.isSaveSearchPopupVisible = true;
        this.focusSavedSearchNameInput();
    }

    closeSaveSearchPopup(): void {
        this.isSaveSearchPopupVisible = false;
        this.editingSavedSearch = null;
        this.saveSearchName = '';
    }

    private focusSavedSearchNameInput(): void {
        // L'input è dentro un *ngIf: esiste solo dopo il prossimo ciclo di change detection
        setTimeout(() => {
            const el = this.savedSearchNameInput?.nativeElement;
            if (el) {
                el.focus();
                el.select();
            }
        });
    }

    /**
     * Conferma del popup di rinomina. Nome vuoto = la card torna senza nome
     * e mostra la data del salvataggio.
     */
    confirmRenameSavedSearch(): void {
        const search = this.editingSavedSearch;
        if (!search) {
            this.closeSaveSearchPopup();
            return;
        }

        const name = this.saveSearchName.trim();
        if (name) {
            search.name = name;
        } else {
            delete search.name;
        }
        this.persistSavedSearches();
        this.closeSaveSearchPopup();
        this.notificationService.success(
            name ? `Filtri rinominati in "${name}"` : 'Nome rimosso: la card mostra la data del salvataggio'
        );
    }

    private persistSavedSearches(): void {
        try {
            localStorage.setItem(this.SAVED_SEARCHES_KEY, JSON.stringify(this.savedSearches));
        } catch (e) {
            console.error('Errore nel salvataggio dei filtri salvati:', e);
        }
    }

    /** Nome della card: quello dato dall'utente o, se non ne ha uno, la data del salvataggio */
    savedSearchDisplayName(search: SavedSearch): string {
        const name = (search.name || '').trim();
        return name || `Filtri del ${this.formatSavedSearchDate(search.timestamp)}`;
    }
    /**
     * Firma canonica di un insieme di filtri (indipendente dall'ordine),
     * usata per confrontare due ricerche.
     */
    private savedSearchSignature(filters: { label: string; value: string }[]): string {
        return filters
            .map(f => `${f.label}=${f.value}`)
            .sort()
            .join('|');
    }

    /**
     * Crea uno snapshot grezzo dei filtri correnti (per riapplicare la ricerca).
     */
    private buildSavedSearchState(): SavedSearchState {
        const iso = (d: Date | null): string | null => (d ? d.toISOString() : null);
        return {
            filterParcelCode: this.filterParcelCode,
            filterWaybillCode: this.filterWaybillCode,
            filterDealerCode: this.filterDealerCode,
            filterStatus: this.filterStatus,
            filterWarehouse: [...this.filterWarehouse],
            filterCarrier: [...this.filterCarrier],
            filterCustomer: [...this.filterCustomer],
            filterBrand: [...this.filterBrand],
            filterRouting: [...this.filterRouting],
            filterCompanyName: this.filterCompanyName,
            filterAddress: this.filterAddress,
            filterCity: this.filterCity,
            filterProvince: this.filterProvince,
            filterParcelType: this.filterParcelType,
            filterWeight: this.filterWeight,
            filterWeightOperator: this.filterWeightOperator,
            filterVolume: this.filterVolume,
            filterVolumeOperator: this.filterVolumeOperator,
            filterDamagedParcels: this.filterDamagedParcels,
            filterParcelImages: this.filterParcelImages,
            filterAxylogSent: this.filterAxylogSent,
            filterMisroutingParcels: this.filterMisroutingParcels,
            selectedFilterDateFrom: iso(this.selectedFilterDateFrom),
            selectedFilterDateTo: iso(this.selectedFilterDateTo),
            selectedFilterEstimatedDelivery: iso(this.selectedFilterEstimatedDelivery),
            selectedFilterActualDelivery: iso(this.selectedFilterActualDelivery),
            isAdvancedFiltersCollapsed: this.isAdvancedFiltersCollapsed
        };
    }

    /**
     * Applica i filtri di un insieme di filtri salvato ai campi del form (senza avviare la ricerca).
     */
    applySavedSearch(search: SavedSearch): void {
        const s = search.state;
        if (!s) {
            this.notificationService.warning('Questa ricerca non contiene i filtri da riapplicare');
            return;
        }

        const toDate = (v: string | null): Date | null => (v ? new Date(v) : null);
        // Retro-compatibilità: le ricerche salvate prima della selezione multipla contengono una stringa
        const toArray = (v: string[] | string | null | undefined): string[] =>
            Array.isArray(v) ? [...v] : (v ? [v] : []);

        this.filterParcelCode = s.filterParcelCode;
        this.filterWaybillCode = s.filterWaybillCode;
        this.filterDealerCode = s.filterDealerCode;
        this.filterStatus = s.filterStatus;
        this.filterWarehouse = toArray(s.filterWarehouse);
        this.filterCarrier = toArray(s.filterCarrier);
        this.filterCustomer = toArray(s.filterCustomer);
        this.filterBrand = toArray(s.filterBrand);
        this.filterRouting = toArray(s.filterRouting);
        this.filterCompanyName = s.filterCompanyName;
        this.filterAddress = s.filterAddress;
        this.filterCity = s.filterCity;
        this.filterProvince = s.filterProvince;
        this.filterParcelType = s.filterParcelType;
        this.filterWeight = s.filterWeight;
        this.filterWeightOperator = s.filterWeightOperator || '=';
        this.filterVolume = s.filterVolume;
        this.filterVolumeOperator = s.filterVolumeOperator || '=';
        this.filterDamagedParcels = s.filterDamagedParcels ?? false;
        this.filterParcelImages = s.filterParcelImages ?? 'ALL';
        this.filterAxylogSent = s.filterAxylogSent ?? 'ALL';
        this.filterMisroutingParcels = s.filterMisroutingParcels ?? false;

        this.selectedFilterDateFrom = toDate(s.selectedFilterDateFrom);
        this.selectedFilterDateTo = toDate(s.selectedFilterDateTo);
        this.selectedFilterEstimatedDelivery = toDate(s.selectedFilterEstimatedDelivery);
        this.selectedFilterActualDelivery = toDate(s.selectedFilterActualDelivery);

        // Espandi i filtri aggiuntivi se la ricerca li usava
        this.isAdvancedFiltersCollapsed = s.isAdvancedFiltersCollapsed;
        this.disableFilterDate = !this.isAdvancedFiltersCollapsed;

        // Aggiorna i flatpickr se già inizializzati
        this.syncDateFlatpickr(this.filterDateFromFlatpickrInstance, this.selectedFilterDateFrom);
        this.syncDateFlatpickr(this.filterDateToFlatpickrInstance, this.selectedFilterDateTo);
        this.syncDateFlatpickr(this.filterEstimatedDeliveryFlatpickrInstance, this.selectedFilterEstimatedDelivery);
        this.syncDateFlatpickr(this.filterActualDeliveryFlatpickrInstance, this.selectedFilterActualDelivery);

        this.notificationService.success(`Filtri "${this.savedSearchDisplayName(search)}" applicati`);
    }

    /**
     * Allinea un'istanza flatpickr alla data fornita (o la pulisce).
     */
    private syncDateFlatpickr(instance: FlatpickrInstance | null, date: Date | null): void {
        if (!instance) return;
        if (date) {
            instance.setDate(date);
        } else {
            instance.clear();
        }
    }

    /**
     * Rimuove una singola ricerca dallo storico.
     */
    removeSavedSearch(search: SavedSearch, event: MouseEvent): void {
        event.stopPropagation();
        this.savedSearches = this.savedSearches.filter(s => s !== search);
        try {
            localStorage.setItem(this.SAVED_SEARCHES_KEY, JSON.stringify(this.savedSearches));
        } catch (e) {
            console.error('Errore nel salvataggio delle ultime ricerche:', e);
        }
    }

    /**
     * Svuota tutto lo storico delle ricerche.
     */
    clearSavedSearches(): void {
        this.savedSearches = [];
        try {
            localStorage.removeItem(this.SAVED_SEARCHES_KEY);
        } catch (e) {
            console.error('Errore nella pulizia delle ultime ricerche:', e);
        }
    }

    /**
     * Carica le ultime ricerche da localStorage.
     */
    private loadSavedSearches(): void {
        try {
            const raw = localStorage.getItem(this.SAVED_SEARCHES_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) {
                    this.savedSearches = parsed.slice(0, this.MAX_SAVED_SEARCHES);
                }
            }
        } catch (e) {
            console.error('Errore nel caricamento delle ultime ricerche:', e);
            this.savedSearches = [];
        }
    }

    /**
     * Formatta data e ora di un insieme di filtri salvato.
     */
    formatSavedSearchDate(timestamp: number): string {
        const d = new Date(timestamp);
        return d.toLocaleDateString('it-IT', {day: '2-digit', month: '2-digit', year: 'numeric'}) +
            ' ' + d.toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'});
    }

    /**
     * Apre il popup con il dettaglio di un insieme di filtri salvato.
     */
    openSavedSearchDetails(search: SavedSearch): void {
        this.selectedSavedSearch = search;
        this.isSavedSearchPopupVisible = true;
    }

    /**
     * Chiude il popup dei dettagli dei filtri salvati.
     */
    closeSavedSearchPopup(): void {
        this.isSavedSearchPopupVisible = false;
        this.selectedSavedSearch = null;
    }

    /**
     * Reset all filter fields without reloading data
     */
    resetFilters(): void {
        // Reset filter fields
        this.filterParcelCode = '';
        this.filterWaybillCode = '';
        this.filterDealerCode = '';
        this.filterStatus = '';
        this.selectedFilterDate = null;
        this.filterCarrier = [];
        this.filterCustomer = [];
        this.filterBrand = [];
        this.filterRouting = [];
        this.filterWarehouse = [];

        // Reset filtri avanzati aggiuntivi
        this.selectedFilterEstimatedDelivery = null;
        if (this.filterEstimatedDeliveryFlatpickrInstance) {
            this.filterEstimatedDeliveryFlatpickrInstance.clear();
        }
        this.selectedFilterActualDelivery = null;
        if (this.filterActualDeliveryFlatpickrInstance) {
            this.filterActualDeliveryFlatpickrInstance.clear();
        }
        this.filterCompanyName = '';
        this.filterAddress = '';
        this.filterCity = '';
        this.filterProvince = '';
        this.filterParcelType = '';
        this.filterWeight = '';
        this.filterWeightOperator = '=';
        this.filterVolume = '';
        this.filterVolumeOperator = '=';
        this.filterDamagedParcels = false;
        this.filterParcelImages = 'ALL';
        this.filterAxylogSent = 'ALL';
        this.filterMisroutingParcels = false;

        // Reset advanced date filters
        this.selectedFilterDateFrom = null;
        this.selectedFilterDateTo = null;

        // Reset date picker if it exists
        if (this.filterDateFlatpickrInstance) {
            this.filterDateFlatpickrInstance.clear();
        }

        // Reset advanced date pickers if they exist
        if (this.filterDateFromFlatpickrInstance) {
            this.filterDateFromFlatpickrInstance.clear();
        }
        if (this.filterDateToFlatpickrInstance) {
            this.filterDateToFlatpickrInstance.clear();
        }

        // Non richiamare loadWaybills, si limita a resettare i campi
    }

    /**
     * Handle changes in unified filter controls
     */
    onUnifiedFilterChange(): void {
        // This method can be called when any of the advanced filters change
        // Currently just logs the change, can be extended to trigger automatic search
        //console.log('Unified filter changed - Carrier:', this.filterCarrier, 'Customer:', this.filterCustomer, 'Brand:', this.filterBrand, 'Routing:', this.filterRouting);
    }

    /**
     * Hook invocato quando cambia uno dei filtri principali.
     * Mantenuto per i binding nel template; nessun vincolo obbligatorio da gestire.
     */
    onRequiredFilterChange(): void {
        // Nessuna validazione obbligatoria sui filtri principali
    }

    /**
     * Export dei dati in Excel.
     * - Modalità selezione con elementi selezionati: export client-side dei soli selezionati.
     * - Altrimenti: export server-side con i filtri di ricerca correnti
     *   (/waybills/export o /parcels/export in base alla vista attiva).
     */
    exportToXLSX(): void {
        const currentDate = new Date().toLocaleDateString('it-IT').replace(/\//g, '-');

        // Export client-side dei soli elementi selezionati
        if (this.isSelectionModeActive && this.showParcelsView && this.selectedParcelCodes.size > 0) {
            this.isExporting = true;
            try {
                const parcelsData = this.filteredParcels.filter(parcel =>
                    parcel.parcelCode && this.selectedParcelCodes.has(parcel.parcelCode)
                );
                const worksheetData = this.prepareParcelsDataForExport(parcelsData);
                this.createAndDownloadExcel(worksheetData, `colli_selezionati_${currentDate}.xlsx`, 'Colli');
                this.notificationService.success(`Esportati ${parcelsData.length} colli selezionati in Excel`);
            } catch (error) {
                console.error('Errore durante l\'esportazione Excel:', error);
                this.notificationService.error('Errore durante l\'esportazione del file Excel');
            } finally {
                this.isExporting = false;
            }
            return;
        }
        if (this.isSelectionModeActive && !this.showParcelsView && this.selectedWaybillCodes.size > 0) {
            this.isExporting = true;
            try {
                const waybillsData = this.filteredWaybills.filter(waybill =>
                    waybill.waybillCode && this.selectedWaybillCodes.has(waybill.waybillCode)
                );
                const worksheetData = this.prepareWaybillsDataForExport(waybillsData);
                this.createAndDownloadExcel(worksheetData, `bolle_selezionate_${currentDate}.xlsx`, 'Bolle');
                this.notificationService.success(`Esportate ${waybillsData.length} bolle selezionate in Excel`);
            } catch (error) {
                console.error('Errore durante l\'esportazione Excel:', error);
                this.notificationService.error('Errore durante l\'esportazione del file Excel');
            } finally {
                this.isExporting = false;
            }
            return;
        }

        // Export server-side con i filtri correnti
        this.isExporting = true;
        const cb = this.getClientsBrandsSelected();
        const filters = this.currentSearchFilters || {};

        const export$ = this.showParcelsView
            ? this.shipmentManagementService.exportParcelsExcel(cb, filters)
            : this.shipmentManagementService.exportWaybillsExcel(cb, filters);
        const baseName = `${this.showParcelsView ? 'Colli' : 'Bolle'}_${currentDate}`;

        export$.pipe(
            catchError(error => {
                console.error('Errore durante l\'esportazione Excel:', error);
                this.notificationService.error('Errore durante l\'esportazione del file Excel');
                return of(null);
            }),
            finalize(() => {
                this.isExporting = false;
            })
        ).subscribe(blob => {
            if (blob && blob.size > 0) {
                // Oltre 1.000.000 di colli il backend risponde con uno ZIP di più file Excel
                const isZip = (blob.type || '').includes('zip');
                this.downloadBlobAsFile(blob, `${baseName}.${isZip ? 'zip' : 'xlsx'}`);
                this.notificationService.success(
                    this.showParcelsView ? 'Colli esportati in Excel' : 'Bolle esportate in Excel'
                );
            }
        });
    }

    /** Scarica un blob come file */
    private downloadBlobAsFile(blob: Blob, filename: string): void {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    /**
     * Format date for Excel export in dd/mm/yyyy format
     */
    private formatDateForExport(dateString?: string): string {
        if (!dateString) return '';

        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;

            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();

            return `${day}/${month}/${year}`;
        } catch (error) {
            return dateString;
        }
    }

    /**
     * Get full formatted address (public for template usage)
     */
    getFullAddress(dealer: any): string {
        if (!dealer) return '';

        const zip = dealer.dealerOperatingAddressZip ?? dealer.dealerOperatingAddressZipCode;
        const parts = [
            dealer.dealerOperatingAddressStreet,
            dealer.dealerOperatingAddressStreetNumber,
            zip,
            dealer.dealerOperatingAddressCity,
            dealer.dealerOperatingAddressProvince,
            dealer.dealerOperatingAddressCountry
        ].filter(part => typeof part === 'string' ? part.trim() !== '' : !!part);

        return parts.join(', ');
    }

    /**
     * Prepare waybills data for Excel export
     */
    private prepareWaybillsDataForExport(waybills: Waybill[]): any[][] {
        const headers = [
            'Codice Bolla',
            'Data Bolla',
            'Data Creazione',
            'Data Ultimo Aggiornamento',
            'Ragione Sociale',
            'Codice Dealer',
            'Nome Dealer',
            'Codice Brand',
            'Codice Corriere',
            'Codice Tracking',
            'Peso Totale (kg)',
            'Volume Totale (m³)',
            'Metodo Consegna',
            'Tipo Consegna',
            'Ore Transito',
            'Indirizzo Completo',
            'Via/Strada',
            'Città',
            'CAP',
            'Provincia',
            'Stato/Paese',
            'Ubicazione Origine',
            'Ubicazione Destinazione',
            'Ultima Ubicazione Nota',
            'Latitudine',
            'Longitudine',
            // 'Latitudine Originale',
            // 'Longitudine Originale',
            'Fragile',
            'Materiale Pericoloso',
            'Sdoganamento Richiesto',
            'Data Consegna Stimata',
            'Data Consegna Effettiva',
            // 'Data Consegna Originale',
            'Status',
            // 'Note',
            // 'File Allegato',
            // 'Versione',
            // 'Ultimo Aggiornamento Da'
        ];

        const data = [headers];

        waybills.forEach(waybill => {
            const dealer = this.getDealerByCode(waybill.waybillDealerCode || '');

            const row: any[] = [
                waybill.waybillCode || '',
                this.formatDateForExport(waybill.waybillDate),
                this.formatDateForExport(waybill.waybillCreatedAt),
                this.formatDateForExport(waybill.waybillUpdatedAt),
                dealer?.dealerName || '',
                waybill.waybillDealerCode || '',
                dealer?.dealerName || '',
                waybill.waybillBrandCode || '',
                waybill.waybillCarrierCode || '',
                waybill.waybillTrackingNumber || '',
                waybill.waybillTotalWeight || 0,
                waybill.waybillTotalVolume || 0,
                waybill.waybillDeliveryMethod || '',
                waybill.waybillDeliveryType || '',
                waybill.waybillTransitTimeHours || 0,
                this.getFullAddress(dealer),
                dealer?.dealerOperatingAddressStreet || '',
                dealer?.dealerOperatingAddressCity || '',
                dealer?.dealerOperatingAddressZip || '',
                dealer?.dealerOperatingAddressProvince || '',
                dealer?.dealerOperatingAddressCountry || '',
                waybill.waybillOriginLocation || '',
                waybill.waybillDestinationLocation || '',
                waybill.waybillLastKnownLocation || '',
                waybill.waybillLatitude || '',
                waybill.waybillLongitude || '',
                // waybill.waybillOriginalLatitude || '',
                // waybill.waybillOriginalLongitude || '',
                waybill.waybillFragile ? 'Sì' : 'No',
                waybill.waybillHazardousMaterial ? 'Sì' : 'No',
                waybill.waybillCustomsClearanceRequired ? 'Sì' : 'No',
                this.formatDateForExport(waybill.waybillEstimatedDeliveryDate),
                this.formatDateForExport(waybill.waybillActualDeliveryDate),
                // this.formatDateForExport(waybill.waybillOriginalDeliveryDate),
                this.getStatusText(waybill.waybillStatus),
                // waybill.waybillNote || '',
                // waybill.waybillFile || '',
                // waybill.waybillVersion || 0,
                // waybill.waybillUpdatedBy || ''
            ];
            data.push(row);
        });

        return data;
    }

    /**
     * Prepare parcels data for Excel export
     */
    private prepareParcelsDataForExport(parcels: Parcels[]): any[][] {
        const headers = [
            'Codice Collo',
            'Codice Bolla',
            'Codice Pallet',
            'Codice Spedizione',
            'Codice Brand',
            'Codice Dealer',
            'Codice Barcode',
            'Numero Tracking',
            'Data Collo',
            'Data Consegna',
            'Data Consegna Stimata',
            'Data Consegna Originale',
            'Data Ultimo Evento',
            'Data Presa in Carico',
            'Data Creazione',
            'Data Ultimo Aggiornamento',
            'Ragione Sociale',
            'Via/Strada',
            'Città',
            'CAP',
            'Provincia',
            'Stato/Paese',
            'Status Consegna',
            'Metodo Consegna',
            'Tipo Consegna',
            'Materiale Consegna',
            'Motivo Consegna',
            'Motivo Presa in Carico',
            'Descrizione',
            'Peso (kg)',
            'Volume (m³)',
            'Lunghezza (cm)',
            'Larghezza (cm)',
            'Altezza (cm)',
            'Tipo Collo',
            'Ubicazione',
            'Latitudine',
            'Longitudine',
            // 'Latitudine Originale',
            // 'Longitudine Originale',
            // 'Fragile',
            // 'Materiale Pericoloso',
            // 'Sensibile Temperatura',
            // 'Articolo Alto Valore',
            // 'Articolo Ristretto',
            // 'Sdoganamento Richiesto',
            // 'Data Consegna Forzata',
            // 'Data Consegna Aggiornata',
            // 'Coordinate Forzate',
            // 'Coordinate Aggiornate',
            // 'Valore Assicurato',
            'Status',
            // 'Versione',
            // 'Ultimo Aggiornamento Da'
        ];

        const data = [headers];

        parcels.forEach(parcel => {
            const dealer = this.getDealerByCode(parcel.parcelDealerCode || '');

            const row: any[] = [
                parcel.parcelCode || '',
                parcel.parcelWaybillCode || '',
                parcel.parcelPalletCode || '',
                parcel.parcelShipmentCode || '',
                parcel.parcelBrandCode || '',
                parcel.parcelDealerCode || '',
                parcel.parcelBarcode || '',
                parcel.parcelTrackingNumber || '',
                this.formatDateForExport(parcel.parcelDate),
                this.formatDateForExport(parcel.parcelDeliveryDate),
                this.formatDateForExport(parcel.parcelEstimatedDeliveryDate),
                this.formatDateForExport(parcel.parcelOriginalDeliveryDate),
                this.formatDateForExport(parcel.parcelLastEventDate),
                this.formatDateForExport(parcel.parcelTakeInChargeDate),
                this.formatDateForExport(parcel.parcelCreatedAt),
                this.formatDateForExport(parcel.parcelUpdatedAt),
                dealer?.dealerName || '',
                dealer?.dealerOperatingAddressStreet || '',
                dealer?.dealerOperatingAddressCity || '',
                dealer?.dealerOperatingAddressZip || '',
                dealer?.dealerOperatingAddressProvince || '',
                dealer?.dealerOperatingAddressCountry || '',
                parcel.parcelDeliveryStatus || '',
                parcel.parcelDeliveryMethod || '',
                parcel.parcelDeliveryType || '',
                parcel.parcelDeliveryMaterial || '',
                parcel.parcelDeliveryReason || '',
                parcel.parcelTakeInChargeReason || '',
                parcel.parcelDescription || '',
                parcel.parcelWeight || 0,
                parcel.parcelVolume || 0,
                parcel.parcelLength || 0,
                parcel.parcelWidth || 0,
                parcel.parcelHeight || 0,
                parcel.parcelType ? this.getParcelTypeLabel(parcel.parcelType) : '',
                parcel.parcelLocation || '',
                parcel.parcelLatitude || '',
                parcel.parcelLongitude || '',
                // parcel.parcelOriginalLatitude || '',
                // parcel.parcelOriginalLongitude || '',
                // parcel.parcelFragile === 'Y' ? 'Sì' : 'No',
                // parcel.parcelHazardousMaterial === 'Y' ? 'Sì' : 'No',
                // parcel.parcelTemperatureSensitive === 'Y' ? 'Sì' : 'No',
                // parcel.parcelHighValueItem === 'Y' ? 'Sì' : 'No',
                // parcel.parcelRestrictedItem === 'Y' ? 'Sì' : 'No',
                // parcel.parcelCustomsClearanceRequired === 'Y' ? 'Sì' : 'No',
                // parcel.parcelDeliveryDateIsForced === 'Y' ? 'Sì' : 'No',
                // parcel.parcelDeliveryDateIsUpdated === 'Y' ? 'Sì' : 'No',
                // parcel.parcelDeliveryCoordinatesIsForced === 'Y' ? 'Sì' : 'No',
                // parcel.parcelDeliveryCoordinatesIsUpdated === 'Y' ? 'Sì' : 'No',
                // parcel.parcelInsuredValue || 0,
                parcel.parcelStatus || '',
                // parcel.parcelVersion || 0,
                // parcel.parcelUpdatedBy || ''
            ];
            data.push(row);
        });

        return data;
    }

    /**
     * Create and download Excel file using xlsx library
     */
    private createAndDownloadExcel(data: any[][], filename: string, sheetName: string): void {
        // Crea un nuovo workbook
        const workbook = XLSX.utils.book_new();

        // Crea un worksheet con i dati
        const worksheet = XLSX.utils.aoa_to_sheet(data);

        // Aggiungi stili alle intestazioni (prima riga)
        const headerRange = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
        for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
            const cellAddress = XLSX.utils.encode_cell({r: 0, c: col});
            if (!worksheet[cellAddress]) continue;

            // Stile per le intestazioni
            worksheet[cellAddress].s = {
                font: {bold: true, color: {rgb: "FFFFFF"}},
                fill: {fgColor: {rgb: "107C41"}}, // Verde Excel
                alignment: {horizontal: "center"}
            };
        }

        // Auto-size delle colonne
        const maxWidth = 50;
        const colWidths: any[] = [];

        // Calcola la larghezza ottimale per ogni colonna
        for (let col = 0; col < data[0].length; col++) {
            let maxLength = 0;
            for (let row = 0; row < data.length; row++) {
                const cellValue = data[row][col]?.toString() || '';
                maxLength = Math.max(maxLength, cellValue.length);
            }
            colWidths.push({width: Math.min(maxLength + 2, maxWidth)});
        }
        worksheet['!cols'] = colWidths;

        // Aggiungi il worksheet al workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

        // Scrivi e scarica il file
        XLSX.writeFile(workbook, filename);
    }

    /**
     * Convert waybills data to XLSX format
     */
    private convertWaybillsToXLSX(waybills: Waybill[]): any {
        const headers = [
            'Codice Bolla',
            'Data',
            'Ragione Sociale',
            'Codice Dealer',
            'Codice Cliente',
            'Codice Brand',
            'Peso Totale (kg)',
            'Volume Totale (m³)',
            'Instradamento',
            'Indirizzo',
            'Città',
            'Provincia',
            'Stato',
            'Status'
        ];

        const data = waybills.map(waybill => {
            const dealer = this.getDealerByCode(waybill.waybillDealerCode || '');

            return [
                waybill.waybillCode || '',
                waybill.waybillDate || '',
                dealer?.dealerName || '',
                waybill.waybillDealerCode || '',
                waybill.waybillClientCode || '',
                waybill.waybillBrandCode || '',
                waybill.waybillTotalWeight || 0,
                waybill.waybillTotalVolume || 0,
                waybill.waybillDeliveryType || '',
                dealer?.dealerOperatingAddressStreet || '',
                dealer?.dealerOperatingAddressCity || '',
                dealer?.dealerOperatingAddressProvince || '',
                dealer?.dealerOperatingAddressCountry || '',
                this.getStatusText(waybill.waybillStatus)
            ];
        });

        return this.createWorkbook([headers, ...data], 'Bolle');
    }

    /**
     * Convert parcels data to XLSX format
     */
    private convertParcelsToXLSX(parcels: Parcels[]): any {
        const headers = [
            'Codice Collo',
            'Codice Bolla',
            'Data',
            'Ragione Sociale',
            'Codice Dealer',
            'Codice Cliente',
            'Codice Brand',
            'Peso (kg)',
            'Volume (m³)',
            'Lunghezza (cm)',
            'Larghezza (cm)',
            'Altezza (cm)',
            'Instradamento',
            'Indirizzo',
            'Città',
            'Provincia',
            'Stato'
        ];

        const data = parcels.map(parcel => {
            const dealer = this.getDealerByCode(parcel.parcelDealerCode || '');

            return [
                parcel.parcelCode || '',
                parcel.parcelWaybillCode || '',
                parcel.parcelDate || '',
                dealer?.dealerName || '',
                parcel.parcelDealerCode || '',
                parcel.parcelClientCode || '',
                parcel.parcelBrandCode || '',
                parcel.parcelWeight || 0,
                parcel.parcelVolume || 0,
                parcel.parcelLength || 0,
                parcel.parcelWidth || 0,
                parcel.parcelHeight || 0,
                parcel.parcelDeliveryType || '',
                dealer?.dealerOperatingAddressStreet || '',
                dealer?.dealerOperatingAddressCity || '',
                dealer?.dealerOperatingAddressProvince || '',
                dealer?.dealerOperatingAddressCountry || ''
            ];
        });

        return this.createWorkbook([headers, ...data], 'Colli');
    }

    /**
     * Create a workbook using a simple XLSX structure without external dependencies
     */
    private createWorkbook(data: any[][], sheetName: string): any {
        // Create a simple workbook structure that can be converted to XLSX
        const workbook = {
            SheetNames: [sheetName],
            Sheets: {} as any
        };

        // Convert data to worksheet format
        const worksheet: any = {};

        // Calculate range
        const range = {
            s: {c: 0, r: 0},
            e: {c: data[0].length - 1, r: data.length - 1}
        };

        // Add data to worksheet
        for (let R = 0; R < data.length; R++) {
            for (let C = 0; C < data[R].length; C++) {
                const cellAddress = this.encodeCell({c: C, r: R});
                const cellValue = data[R][C];

                worksheet[cellAddress] = {
                    v: cellValue,
                    t: typeof cellValue === 'number' ? 'n' : 's'
                };
            }
        }

        worksheet['!ref'] = this.encodeRange(range);
        workbook.Sheets[sheetName] = worksheet;

        return workbook;
    }

    /**
     * Encode cell address (e.g., A1, B2)
     */
    private encodeCell(cell: { c: number, r: number }): string {
        return this.encodeCol(cell.c) + this.encodeRow(cell.r);
    }

    /**
     * Encode column number to letter (e.g., 0 -> A, 1 -> B)
     */
    private encodeCol(col: number): string {
        let result = '';
        while (col >= 0) {
            result = String.fromCharCode(65 + (col % 26)) + result;
            col = Math.floor(col / 26) - 1;
        }
        return result;
    }

    /**
     * Encode row number (1-based)
     */
    private encodeRow(row: number): string {
        return String(row + 1);
    }

    /**
     * Encode range (e.g., A1:C3)
     */
    private encodeRange(range: { s: { c: number, r: number }, e: { c: number, r: number } }): string {
        return this.encodeCell(range.s) + ':' + this.encodeCell(range.e);
    }

    /**
     * Download XLSX file
     */
    private downloadXLSX(workbook: any, filename: string): void {
        try {
            // Convert workbook to binary string
            const xlsxContent = this.workbookToXLSX(workbook);

            // Create blob and download
            const blob = new Blob([xlsxContent], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            const link = document.createElement('a');
            if (link.download !== undefined) {
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', filename);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }
        } catch (error) {
            console.error('Error creating XLSX file:', error);
            this.notificationService.error('Errore durante l\'esportazione del file Excel.');
        }
    }

    /**
     * Convert workbook to XLSX binary format
     * This is a simplified XLSX generator without external dependencies
     */
    private workbookToXLSX(workbook: any): ArrayBuffer {
        // This is a very basic XLSX implementation
        // For production use, consider using the 'xlsx' library

        // Create a minimal XLSX structure
        const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
</Types>`;

        const relationships = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

        const workbookXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets>
<sheet name="${workbook.SheetNames[0]}" sheetId="1" r:id="rId1"/>
</sheets>
</workbook>`;

        const worksheetXml = this.createWorksheetXml(workbook.Sheets[workbook.SheetNames[0]]);

        // Create a simple ZIP structure (minimal XLSX)
        // Note: This is a very basic implementation
        // For full compatibility, use a proper XLSX library

        const encoder = new TextEncoder();
        const contentTypesData = encoder.encode(contentTypes);
        const relationshipsData = encoder.encode(relationships);
        const workbookData = encoder.encode(workbookXml);
        const worksheetData = encoder.encode(worksheetXml);

        // Create a simple binary format that Excel can read
        // This is a fallback that creates a CSV-like structure in XML format
        const totalSize = contentTypesData.length + relationshipsData.length + workbookData.length + worksheetData.length + 1000;
        const buffer = new ArrayBuffer(totalSize);

        // For simplicity, we'll create a CSV with .xlsx extension
        // This ensures compatibility even without a full ZIP implementation
        const csvContent = this.convertWorkbookToCSV(workbook);
        const csvBuffer = encoder.encode(csvContent);

        return csvBuffer.buffer;
    }

    /**
     * Convert workbook to CSV as fallback
     */
    private convertWorkbookToCSV(workbook: any): string {
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const range = this.decodeRange(sheet['!ref']);
        const rows: string[] = [];

        for (let R = range.s.r; R <= range.e.r; R++) {
            const row: string[] = [];
            for (let C = range.s.c; C <= range.e.c; C++) {
                const cellAddress = this.encodeCell({c: C, r: R});
                const cell = sheet[cellAddress];
                const value = cell ? String(cell.v) : '';
                row.push(value);
            }
            rows.push(row.join(','));
        }

        return rows.join('\n');
    }

    /**
     * Create worksheet XML content
     */
    private createWorksheetXml(worksheet: any): string {
        const range = this.decodeRange(worksheet['!ref']);
        let sheetData = '';

        for (let R = range.s.r; R <= range.e.r; R++) {
            let rowXml = `<row r="${R + 1}">`;
            for (let C = range.s.c; C <= range.e.c; C++) {
                const cellAddress = this.encodeCell({c: C, r: R});
                const cell = worksheet[cellAddress];
                if (cell) {
                    const cellType = cell.t === 'n' ? 'n' : 'inlineStr';
                    const cellValue = cell.t === 'n' ? cell.v : `<is><t>${this.escapeXml(String(cell.v))}</t></is>`;
                    rowXml += `<c r="${cellAddress}" t="${cellType}">${cellType === 'n' ? `<v>${cellValue}</v>` : cellValue}</c>`;
                }
            }
            rowXml += '</row>';
            sheetData += rowXml;
        }

        return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<sheetData>
${sheetData}
</sheetData>
</worksheet>`;
    }

    /**
     * Decode range string (e.g., A1:C3)
     */
    private decodeRange(rangeStr: string): { s: { c: number, r: number }, e: { c: number, r: number } } {
        const [start, end] = rangeStr.split(':');
        return {
            s: this.decodeCell(start),
            e: this.decodeCell(end)
        };
    }

    /**
     * Decode cell address (e.g., A1 -> {c: 0, r: 0})
     */
    private decodeCell(cellStr: string): { c: number, r: number } {
        const match = cellStr.match(/^([A-Z]+)([0-9]+)$/);
        if (!match) throw new Error('Invalid cell address');

        const colStr = match[1];
        const rowStr = match[2];

        let col = 0;
        for (let i = 0; i < colStr.length; i++) {
            col = col * 26 + (colStr.charCodeAt(i) - 64);
        }
        col--;

        const row = parseInt(rowStr) - 1;

        return {c: col, r: row};
    }

    /**
     * Escape XML special characters
     */
    private escapeXml(text: string): string {
        return text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&apos;');
    }

    /**
     * Get status text for display
     */
    private getStatusText(status: string | undefined): string {
        if (!status) return '';

        switch (status) {
            case 'PENDING':
                return 'In Attesa';
            case 'UNSENT':
                return 'Non Inviata';
            case 'ACTIVE':
                return 'Attiva';
            case 'SENT':
                return 'Inviata';
            case 'DELIVERED':
                return 'Consegnata';
            case 'CANCELLED':
                return 'Annullata';
            case 'NOT_DELIVERED':
                return 'Non Consegnata';
            case 'DELETED':
                return 'Eliminata';
            default:
                return String(status);
        }
    }

    /**
     * Set items per page and reset to first page
     */
    setItemsPerPage(items: number): void {
        this.itemsPerPage = items;
        this.currentPage = 1;
        this.saveUserPreferences(); // Save preferences
        this.loadCurrentView(); // ricarica con la nuova dimensione pagina
    }

    /**
     * Show waybills view (default view)
     */
    showWaybillsView(): void {
        this.resetSortIfViewChanges(false, false);
        this.showParcelsView = false;
        this.showRoutingView = false;
        this.currentPage = 1; // Reset pagination when switching views
        this.saveUserPreferences(); // Save preferences
        this.loadCurrentView();
    }

    /**
     * Show parcels view (toggle view)
     */
    showParcelsViewMode(): void {
        this.resetSortIfViewChanges(true, false);
        this.showParcelsView = true;
        this.showRoutingView = false;
        this.currentPage = 1; // Reset pagination when switching views
        this.saveUserPreferences(); // Save preferences
        this.loadCurrentView();
    }

    /**
     * Show routing view (new view)
     */
    showRoutingViewMode(): void {
        this.resetSortIfViewChanges(false, true);
        this.showParcelsView = false;
        this.showRoutingView = true;
        this.currentPage = 1; // Reset pagination when switching views
        this.saveUserPreferences(); // Save preferences
        this.loadCurrentView();
    }

    /**
     * L'ordinamento vale per le colonne della tabella in cui è stato scelto: passando a un'altra
     * tabella va azzerato, altrimenti la ricerca partirebbe ordinata su un campo che lì non esiste.
     */
    private resetSortIfViewChanges(showParcels: boolean, showRouting: boolean): void {
        if (this.showParcelsView !== showParcels || this.showRoutingView !== showRouting) {
            this.sortColumn = '';
            this.sortDirection = '';
        }
    }

    /**
     * Toggle filters section collapsed state
     */
    toggleFiltersCollapsed(): void {
        this.isFiltersCollapsed = !this.isFiltersCollapsed;
        this.saveUserPreferences(); // Save preferences
    }

    /**
     * Toggle report section collapsed state
     */
    toggleReportCollapsed(): void {
        this.isReportCollapsed = !this.isReportCollapsed;
        this.saveUserPreferences(); // Save preferences
    }

    // ==================== STRUMENTARIO (TOOLBOX) ====================

    /**
     * Strumentario: true se filtri e resoconto sono entrambi compressi
     */
    get areAllSectionsCollapsed(): boolean {
        return this.isFiltersCollapsed && this.isReportCollapsed;
    }

    /**
     * Strumentario: comprime/espande insieme le sezioni filtri e resoconto
     */
    toggleAllSections(): void {
        const collapse = !this.areAllSectionsCollapsed;
        this.isFiltersCollapsed = collapse;
        this.isReportCollapsed = collapse;
        this.saveUserPreferences(); // Save preferences
    }

    /**
     * Strumentario: ri-esegue la ricerca con i filtri correnti
     */
    refreshData(): void {
        if (this.isSearchLoading || this.isAdvancedSearchLoading) {
            return;
        }
        this.searchWaybills();
    }

    /**
     * Strumentario: toggle densità compatta della tabella risultati
     */
    toggleCompactTable(): void {
        this.isCompactTable = !this.isCompactTable;
        this.saveUserPreferences(); // Save preferences
    }

    /**
     * Strumentario: apre/chiude il dropdown delle colonne visibili
     */
    toggleColumnsDropdown(): void {
        // Nascondi il tooltip prima di aprire il dropdown
        this.hideTooltips();
        this.isColumnsDropdownOpen = !this.isColumnsDropdownOpen;
    }

    /**
     * Opzioni colonne (ordine di default) per la vista corrente (Bolle o Colli)
     */
    get currentColumnOptions(): TableColumnOption[] {
        return this.showParcelsView ? this.parcelColumnOptions : this.waybillColumnOptions;
    }

    /** Chiave della vista corrente per l'ordine colonne */
    private get currentViewKey(): 'waybill' | 'parcel' {
        return this.showParcelsView ? 'parcel' : 'waybill';
    }

    /**
     * Colonne della vista corrente nell'ordine scelto dall'utente
     * (le chiavi non presenti nell'ordine salvato vengono accodate).
     */
    get orderedColumnOptions(): TableColumnOption[] {
        const options = this.currentColumnOptions;
        const order = this.columnOrder[this.currentViewKey] || [];
        const byKey = new Map(options.map(o => [o.key, o]));
        const ordered: TableColumnOption[] = [];
        for (const key of order) {
            const opt = byKey.get(key);
            if (opt && !ordered.includes(opt)) ordered.push(opt);
        }
        for (const opt of options) {
            if (!ordered.includes(opt)) ordered.push(opt);
        }
        return ordered;
    }

    /** Colonne visibili della vista corrente, nell'ordine scelto (usate dalle tabelle) */
    get visibleOrderedColumns(): TableColumnOption[] {
        return this.orderedColumnOptions.filter(c => this.isColumnVisible(c.key));
    }

    /** Etichetta tradotta di una colonna */
    getColumnLabel(col: TableColumnOption): string {
        return col.labelKey ? this.translate.instant(col.labelKey) : (col.label || col.key);
    }

    /** True se l'ordine delle colonne della vista corrente è quello di default */
    get isColumnOrderDefault(): boolean {
        const defaults = this.currentColumnOptions.map(o => o.key);
        const current = this.orderedColumnOptions.map(o => o.key);
        return defaults.every((k, i) => current[i] === k);
    }

    /** True se visibilità o ordine differiscono dal default */
    get isColumnLayoutCustomized(): boolean {
        return this.hiddenColumns.size > 0 || !this.isColumnOrderDefault;
    }

    /**
     * True se la colonna è visibile
     */
    isColumnVisible(key: string): boolean {
        return !this.hiddenColumns.has(key);
    }

    /**
     * Mostra/nasconde una colonna della tabella
     */
    toggleColumnVisibility(key: string): void {
        if (this.hiddenColumns.has(key)) {
            this.hiddenColumns.delete(key);
        } else {
            this.hiddenColumns.add(key);
        }
        this.saveUserPreferences(); // Save preferences
    }

    /**
     * Ripristina la visibilità di tutte le colonne
     */
    resetColumnVisibility(): void {
        this.hiddenColumns.clear();
        this.saveUserPreferences(); // Save preferences
    }

    /** Ripristina visibilità e ordine di default delle colonne della vista corrente */
    resetColumnLayout(): void {
        this.hiddenColumns.clear();
        this.columnOrder[this.currentViewKey] = [];
        this.saveUserPreferences();
    }

    /** Pannello "Colonne" nell'header della sezione risultati */
    toggleColumnsPanel(): void {
        this.hideTooltips();
        this.isColumnsPanelOpen = !this.isColumnsPanelOpen;
        this.isColumnsDropdownOpen = false;
    }

    /** Apre il pannello colonne dallo Strumentario, portando in vista la sezione risultati */
    openColumnsPanelFromToolbox(): void {
        this.hideTooltips();
        this.isColumnsPanelOpen = true;
        document.getElementById('results-table')?.scrollIntoView({behavior: 'smooth', block: 'start'});
    }

    private persistColumnOrder(keys: string[]): void {
        this.columnOrder[this.currentViewKey] = keys;
        this.saveUserPreferences();
    }

    /** Sposta una colonna di `delta` posizioni (negativo = verso sinistra/alto) */
    moveColumn(key: string, delta: number): void {
        const keys = this.orderedColumnOptions.map(o => o.key);
        const from = keys.indexOf(key);
        const to = from + delta;
        if (from < 0 || to < 0 || to >= keys.length) return;
        keys.splice(from, 1);
        keys.splice(to, 0, key);
        this.persistColumnOrder(keys);
    }

    /** Sposta la colonna `key` nella posizione occupata da `targetKey` */
    moveColumnTo(key: string, targetKey: string): void {
        if (!key || !targetKey || key === targetKey) return;
        const keys = this.orderedColumnOptions.map(o => o.key);
        const from = keys.indexOf(key);
        const to = keys.indexOf(targetKey);
        if (from < 0 || to < 0) return;
        keys.splice(from, 1);
        keys.splice(to, 0, key);
        this.persistColumnOrder(keys);
    }

    // ---- Drag & drop colonne (intestazione tabella e pannello) ----

    onColumnDragStart(event: DragEvent, key: string): void {
        this.draggedColumnKey = key;
        this.dragOverColumnKey = null;
        if (event.dataTransfer) {
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', key);
        }
    }

    onColumnDragOver(event: DragEvent, key: string): void {
        if (!this.draggedColumnKey || this.draggedColumnKey === key) return;
        event.preventDefault();
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
        this.dragOverColumnKey = key;
    }

    onColumnDragLeave(key: string): void {
        if (this.dragOverColumnKey === key) this.dragOverColumnKey = null;
    }

    onColumnDrop(event: DragEvent, targetKey: string): void {
        event.preventDefault();
        const key = this.draggedColumnKey || event.dataTransfer?.getData('text/plain') || '';
        this.moveColumnTo(key, targetKey);
        this.onColumnDragEnd();
    }

    onColumnDragEnd(): void {
        this.draggedColumnKey = null;
        this.dragOverColumnKey = null;
    }

    /**
     * Print complete tour for all delivery routes
     */
    printTour(): void {
        this.isPrintTourPopupVisible = true;
    }

    /**
     * Close print tour popup
     */
    closePrintTourPopup(): void {
        this.isPrintTourPopupVisible = false;
    }

    /**
     * Update parcel volume when dimensions change
     */
    updateParcelVolume(): void {
        const length = parseFloat(this.parcelForm.get('parcelLength')?.value) || 0;
        const width = parseFloat(this.parcelForm.get('parcelWidth')?.value) || 0;
        const height = parseFloat(this.parcelForm.get('parcelHeight')?.value) || 0;

        // Check if all dimensions are valid
        if (length > 0 && width > 0 && height > 0) {
            const calculatedVolume = this.calculateVolumeFromDimensions(length, width, height);
            // Set volume with 4 decimal places
            this.parcelForm.get('parcelVolume')?.setValue(calculatedVolume.toFixed(4));
        }
    }

    /**
     * Calculate volume from dimensions
     */
    calculateVolumeFromDimensions(length: number, width: number, height: number): number {
        // Convert from cm to m³: (cm³ / 1,000,000)
        // Round to 4 decimal places for volume precision
        const volume = (length * width * height) / 1000000;
        return Math.round(volume * 10000) / 10000;
    }

    /**
     * Get volume validation result
     */
    getVolumeValidation(): any {
        // Get dimensions and volume
        const length = parseFloat(this.parcelForm.get('parcelLength')?.value) || 0;
        const width = parseFloat(this.parcelForm.get('parcelWidth')?.value) || 0;
        const height = parseFloat(this.parcelForm.get('parcelHeight')?.value) || 0;
        const volume = parseFloat(this.parcelForm.get('parcelVolume')?.value) || 0;

        // Create a cache key based on current values
        const cacheKey = `${length}-${width}-${height}-${volume}`;

        // Check if result is already in cache
        if (this._volumeValidationCache.has(cacheKey)) {
            return this._volumeValidationCache.get(cacheKey);
        }

        // Calculate validation
        const result = this.calculateVolumeValidation(length, width, height, volume);

        // Save in cache
        this._volumeValidationCache.set(cacheKey, result);

        // Limit cache size (keep only last 50 calculations)
        if (this._volumeValidationCache.size > 50) {
            const firstKey = this._volumeValidationCache.keys().next().value;
            if (firstKey !== undefined) {
                this._volumeValidationCache.delete(firstKey);
            }
        }

        return result;
    }

    /**
     * Extract volume validation logic
     */
    private calculateVolumeValidation(length: number, width: number, height: number, enteredVolume: number): any {
        // Round dimensions to 1 decimal place
        const roundedLength = Math.round(length * 10) / 10;
        const roundedWidth = Math.round(width * 10) / 10;
        const roundedHeight = Math.round(height * 10) / 10;
        const roundedVolume = Math.round(enteredVolume * 10000) / 10000;

        // Check if we have all three dimensions
        const hasDimensions = roundedLength > 0 && roundedWidth > 0 && roundedHeight > 0;
        const hasVolume = roundedVolume > 0;

        if (!hasDimensions && !hasVolume) {
            return null; // No validation needed
        }

        if (!hasDimensions && hasVolume) {
            return null; // Volume entered but no dimensions - no validation
        }

        const calculatedVolume = this.calculateVolumeFromDimensions(roundedLength, roundedWidth, roundedHeight);

        if (!hasVolume) {
            return {
                type: 'missing',
                calculatedVolume: calculatedVolume,
                message: 'SHIPMENT_MANAGEMENT.VOLUME_VALIDATION.VOLUME_MISSING'
            };
        }

        // Has both dimensions and volume - compare them
        const tolerance = 0.0001;
        const volumeDifference = Math.abs(calculatedVolume - roundedVolume);

        if (volumeDifference <= tolerance) {
            return {
                type: 'match',
                calculatedVolume: calculatedVolume,
                enteredVolume: roundedVolume,
                message: 'SHIPMENT_MANAGEMENT.VOLUME_VALIDATION.VOLUME_MATCH'
            };
        } else {
            return {
                type: 'mismatch',
                calculatedVolume: calculatedVolume,
                enteredVolume: roundedVolume,
                message: 'SHIPMENT_MANAGEMENT.VOLUME_VALIDATION.VOLUME_MISMATCH'
            };
        }
    }

    /**
     * Use calculated volume from dimensions
     */
    useCalculatedVolume(): void {
        const validation = this.getVolumeValidation();
        if (validation && validation.calculatedVolume) {
            // Set volume with 4 decimal places
            this.parcelForm.get('parcelVolume')?.setValue(validation.calculatedVolume.toFixed(4));

            // Clear the cache to trigger re-validation
            this._volumeValidationCache.clear();
        }
    }

    /**
     * Toggle advanced filters section collapsed state
     */
    toggleAdvancedFilters(): void {
        this.isAdvancedFiltersCollapsed = !this.isAdvancedFiltersCollapsed;

        // Le date "Data Da" e "Data A" NON vengono istanziate all'apertura:
        // restano vuote finché l'utente non le seleziona.

        // Disattiva il campo Data quando i filtri aggiuntivi sono aperti, riattivalo alla chiusura
        this.disableFilterDate = !this.isAdvancedFiltersCollapsed;
    }

    // Metodi per il dropdown rimossi perché utilizziamo Bootstrap nativo

    // HostListener rimosso perché utilizziamo Bootstrap nativo per gestire il dropdown

    /**
     * Initialize Flatpickr for filter date field
     */
    private initializeFilterDateFlatpickr(): void {
        if (this.filterDateInput?.nativeElement) {
            const currentLang = this.translate.currentLang || 'it';

            // Destroy previous instance if exists
            if (this.filterDateFlatpickrInstance) {
                this.filterDateFlatpickrInstance.destroy();
            }

            this.filterDateFlatpickrInstance = flatpickr(this.filterDateInput.nativeElement, {
                dateFormat: 'd/m/Y',
                allowInput: false,
                clickOpens: false, // Disable automatic opening on click
                locale: currentLang === 'it' ? Italian : undefined,
                defaultDate: this.selectedFilterDate || undefined,
                onChange: (selectedDates: Date[], dateStr: string) => {
                    if (selectedDates.length > 0) {
                        this.isUpdatingFilterDate = true;
                        this.selectedFilterDate = selectedDates[0];
                        // Close the picker after selection
                        setTimeout(() => {
                            this.filterDateFlatpickrInstance?.close();
                            this.isUpdatingFilterDate = false;
                        }, 100);
                        // TODO: Apply filters when date changes
                        // this.applyFilters();
                    }
                },
                onClose: () => {
                    // Ensure the input loses focus after closing
                    if (this.filterDateInput?.nativeElement) {
                        this.filterDateInput.nativeElement.blur();
                    }
                    // Reset the updating flag
                    setTimeout(() => {
                        this.isUpdatingFilterDate = false;
                    }, 150);
                }
            });
        }
    }

    /**
     * Open filter date picker
     */
    openFilterDatePicker(): void {
        // Prevent opening if we're currently updating the date
        if (this.isUpdatingFilterDate) {
            return;
        }

        if (!this.filterDateFlatpickrInstance) {
            this.initializeFilterDateFlatpickr();
        }

        if (this.filterDateFlatpickrInstance && !this.filterDateFlatpickrInstance.isOpen) {
            if (this.selectedFilterDate) {
                this.filterDateFlatpickrInstance.setDate(this.selectedFilterDate);
            }
            this.filterDateFlatpickrInstance.open();
        }
    }

    /**
     * Decrement filter date by one day
     */
    decrementFilterDate(): void {
        if (!this.selectedFilterDate) {
            this.selectedFilterDate = new Date();
        }
        const newDate = new Date(this.selectedFilterDate);
        newDate.setDate(newDate.getDate() - 1);
        this.selectedFilterDate = newDate;

        // Update flatpickr instance if exists
        if (this.filterDateFlatpickrInstance) {
            this.filterDateFlatpickrInstance.setDate(this.selectedFilterDate);
        }

        // TODO: Apply filters
        // this.applyFilters();
    }

    /**
     * Increment filter date by one day
     */
    incrementFilterDate(): void {
        if (!this.selectedFilterDate) {
            this.selectedFilterDate = new Date();
        }
        const newDate = new Date(this.selectedFilterDate);
        newDate.setDate(newDate.getDate() + 1);
        this.selectedFilterDate = newDate;

        // Update flatpickr instance if exists
        if (this.filterDateFlatpickrInstance) {
            this.filterDateFlatpickrInstance.setDate(this.selectedFilterDate);
        }

        // TODO: Apply filters
        // this.applyFilters();
    }

    /**
     * Set filter date to today
     */
    setFilterDateToday(): void {
        this.selectedFilterDate = new Date();
        if (this.filterDateFlatpickrInstance) {
            this.filterDateFlatpickrInstance.setDate(this.selectedFilterDate);
        }
    }

    /**
     * Clear filter date
     */
    clearFilterDate(): void {
        this.selectedFilterDate = null;
        if (this.filterDateFlatpickrInstance) {
            this.filterDateFlatpickrInstance.clear();
        }
    }

    /**
     * Format date for display
     */
    getFormattedFilterDate(): string {
        if (!this.selectedFilterDate) return '';
        return this.selectedFilterDate.toLocaleDateString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    }

    /**
     * Get formatted filter date from
     */
    getFormattedFilterDateFrom(): string {
        if (!this.selectedFilterDateFrom) return '';

        const day = this.selectedFilterDateFrom.getDate().toString().padStart(2, '0');
        const month = (this.selectedFilterDateFrom.getMonth() + 1).toString().padStart(2, '0');
        const year = this.selectedFilterDateFrom.getFullYear();
        const hours = this.selectedFilterDateFrom.getHours().toString().padStart(2, '0');
        const minutes = this.selectedFilterDateFrom.getMinutes().toString().padStart(2, '0');

        return `${day}/${month}/${year} ${hours}:${minutes}`;
    }

    /**
     * Get formatted filter date to
     */
    getFormattedFilterDateTo(): string {
        if (!this.selectedFilterDateTo) return '';

        const day = this.selectedFilterDateTo.getDate().toString().padStart(2, '0');
        const month = (this.selectedFilterDateTo.getMonth() + 1).toString().padStart(2, '0');
        const year = this.selectedFilterDateTo.getFullYear();
        const hours = this.selectedFilterDateTo.getHours().toString().padStart(2, '0');
        const minutes = this.selectedFilterDateTo.getMinutes().toString().padStart(2, '0');

        return `${day}/${month}/${year} ${hours}:${minutes}`;
    }

    /**
     * Initialize Flatpickr for filter date from field
     */
    private initializeFilterDateFromFlatpickr(): void {
        if (this.filterDateFromInput?.nativeElement) {
            const currentLang = this.translate.currentLang || 'it';

            if (this.filterDateFromFlatpickrInstance) {
                this.filterDateFromFlatpickrInstance.destroy();
            }

            this.filterDateFromFlatpickrInstance = flatpickr(this.filterDateFromInput.nativeElement, {
                dateFormat: 'd/m/Y H:i',
                enableTime: true,
                time_24hr: true,
                allowInput: false,
                clickOpens: false,
                locale: currentLang === 'it' ? Italian : undefined,
                defaultDate: this.selectedFilterDateFrom || undefined,
                defaultHour: 0,
                defaultMinute: 0,
                onChange: (selectedDates: Date[], dateStr: string) => {
                    if (selectedDates.length > 0) {
                        const selectedDate = new Date(selectedDates[0]);

                        // Check if user is selecting a different day
                        const isDifferentDay = this.selectedFilterDateFrom &&
                            this.selectedFilterDateFrom.toDateString() !== selectedDate.toDateString();

                        // If it's a different day, apply the default time (00:00)
                        // If it's the same day, keep the user's selected time
                        if (!this.selectedFilterDateFrom || isDifferentDay) {
                            selectedDate.setHours(0, 0, 0, 0);
                        }
                        // Note: if same day and time already exists, selectedDate already has the correct time from flatpickr

                        this.selectedFilterDateFrom = selectedDate;
                        this.filterDateFromFlatpickrInstance?.close();
                    }
                },
                onClose: () => {
                    if (this.filterDateFromInput?.nativeElement) {
                        this.filterDateFromInput.nativeElement.blur();
                    }
                }
            });
        }
    }

    /**
     * Initialize Flatpickr for filter date to field
     */
    /**
     * Initialize Flatpickr for filter date to field
     */
    private initializeFilterDateToFlatpickr(): void {
        if (this.filterDateToInput?.nativeElement) {
            const currentLang = this.translate.currentLang || 'it';

            if (this.filterDateToFlatpickrInstance) {
                this.filterDateToFlatpickrInstance.destroy();
            }

            this.filterDateToFlatpickrInstance = flatpickr(this.filterDateToInput.nativeElement, {
                dateFormat: 'd/m/Y H:i',
                enableTime: true,
                time_24hr: true,
                allowInput: false,
                clickOpens: false,
                locale: currentLang === 'it' ? Italian : undefined,
                defaultDate: this.selectedFilterDateTo || undefined,
                defaultHour: 23,
                defaultMinute: 59,
                onChange: (selectedDates: Date[], dateStr: string) => {
                    if (selectedDates.length > 0) {
                        const selectedDate = new Date(selectedDates[0]);

                        // Check if user is selecting a different day
                        const isDifferentDay = this.selectedFilterDateTo &&
                            this.selectedFilterDateTo.toDateString() !== selectedDate.toDateString();

                        // If it's a different day, apply the default time (23:59)
                        // If it's the same day, keep the user's selected time
                        if (!this.selectedFilterDateTo || isDifferentDay) {
                            selectedDate.setHours(23, 59, 59, 999);
                        }
                        // Note: if same day and time already exists, selectedDate already has the correct time from flatpickr

                        this.selectedFilterDateTo = selectedDate;
                        this.filterDateToFlatpickrInstance?.close();
                    }
                },
                onClose: () => {
                    if (this.filterDateToInput?.nativeElement) {
                        this.filterDateToInput.nativeElement.blur();
                    }
                }
            });
        }
    }

    /**
     * Open filter date from picker
     */
    openFilterDateFromPicker(): void {
        if (!this.filterDateFromFlatpickrInstance) {
            this.initializeFilterDateFromFlatpickr();
        }

        if (this.filterDateFromFlatpickrInstance && !this.filterDateFromFlatpickrInstance.isOpen) {
            if (this.selectedFilterDateFrom) {
                this.filterDateFromFlatpickrInstance.setDate(this.selectedFilterDateFrom);
            }
            this.filterDateFromFlatpickrInstance.open();
        }
    }

    /**
     * Decrement filter date from by one day
     */
    decrementFilterDateFrom(): void {
        if (!this.selectedFilterDateFrom) {
            // Crea una nuova data corrispondente all'inizio della giornata corrente (00:00:00)
            const startOfDay = new Date();
            startOfDay.setHours(0, 0, 0, 0);
            this.selectedFilterDateFrom = startOfDay;
        }

        const newDate = new Date(this.selectedFilterDateFrom);
        newDate.setDate(newDate.getDate() - 1);
        // Mantieni l'ora a 00:00:00
        newDate.setHours(0, 0, 0, 0);
        this.selectedFilterDateFrom = newDate;

        // Update flatpickr instance if exists
        if (this.filterDateFromFlatpickrInstance) {
            this.filterDateFromFlatpickrInstance.setDate(this.selectedFilterDateFrom);
        }
    }

    /**
     * Increment filter date from by one day
     */
    incrementFilterDateFrom(): void {
        if (!this.selectedFilterDateFrom) {
            // Crea una nuova data corrispondente all'inizio della giornata corrente (00:00:00)
            const startOfDay = new Date();
            startOfDay.setHours(0, 0, 0, 0);
            this.selectedFilterDateFrom = startOfDay;
        }

        const newDate = new Date(this.selectedFilterDateFrom);
        newDate.setDate(newDate.getDate() + 1);
        // Mantieni l'ora a 00:00:00
        newDate.setHours(0, 0, 0, 0);
        this.selectedFilterDateFrom = newDate;

        // Update flatpickr instance if exists
        if (this.filterDateFromFlatpickrInstance) {
            this.filterDateFromFlatpickrInstance.setDate(this.selectedFilterDateFrom);
        }
    }

    /**
     * Set filter date from to today
     */
    setFilterDateFromToday(): void {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        this.selectedFilterDateFrom = today;
        if (this.filterDateFromFlatpickrInstance) {
            this.filterDateFromFlatpickrInstance.setDate(this.selectedFilterDateFrom);
        }
    }

    /**
     * Clear filter date from
     */
    clearFilterDateFrom(): void {
        this.selectedFilterDateFrom = null;
        if (this.filterDateFromFlatpickrInstance) {
            this.filterDateFromFlatpickrInstance.clear();
        }
    }

    /**
     * Open filter date to picker
     */
    openFilterDateToPicker(): void {
        if (!this.filterDateToFlatpickrInstance) {
            this.initializeFilterDateToFlatpickr();
        }

        if (this.filterDateToFlatpickrInstance && !this.filterDateToFlatpickrInstance.isOpen) {
            if (this.selectedFilterDateTo) {
                this.filterDateToFlatpickrInstance.setDate(this.selectedFilterDateTo);
            }
            this.filterDateToFlatpickrInstance.open();
        }
    }

    /**
     * Decrement filter date to by one day
     */
    decrementFilterDateTo(): void {
        if (!this.selectedFilterDateTo) {
            // Crea una nuova data corrispondente alla fine della giornata corrente (23:59:59)
            const endOfDay = new Date();
            endOfDay.setHours(23, 59, 59, 999);
            this.selectedFilterDateTo = endOfDay;
        }

        const newDate = new Date(this.selectedFilterDateTo);
        newDate.setDate(newDate.getDate() - 1);
        // Mantieni l'ora a 23:59:59
        newDate.setHours(23, 59, 59, 999);
        this.selectedFilterDateTo = newDate;

        // Update flatpickr instance if exists
        if (this.filterDateToFlatpickrInstance) {
            this.filterDateToFlatpickrInstance.setDate(this.selectedFilterDateTo);
        }
    }

    /**
     * Increment filter date to by one day
     */
    incrementFilterDateTo(): void {
        if (!this.selectedFilterDateTo) {
            // Crea una nuova data corrispondente alla fine della giornata corrente (23:59:59)
            const endOfDay = new Date();
            endOfDay.setHours(23, 59, 59, 999);
            this.selectedFilterDateTo = endOfDay;
        }

        const newDate = new Date(this.selectedFilterDateTo);
        newDate.setDate(newDate.getDate() + 1);
        // Mantieni l'ora a 23:59:59
        newDate.setHours(23, 59, 59, 999);
        this.selectedFilterDateTo = newDate;

        // Update flatpickr instance if exists
        if (this.filterDateToFlatpickrInstance) {
            this.filterDateToFlatpickrInstance.setDate(this.selectedFilterDateTo);
        }
    }

    /**
     * Set filter date to to today
     */
    setFilterDateToToday(): void {
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        this.selectedFilterDateTo = today;
        if (this.filterDateToFlatpickrInstance) {
            this.filterDateToFlatpickrInstance.setDate(this.selectedFilterDateTo);
        }
    }

    /**
     * Clear filter date to
     */
    clearFilterDateTo(): void {
        this.selectedFilterDateTo = null;
        if (this.filterDateToFlatpickrInstance) {
            this.filterDateToFlatpickrInstance.clear();
        }
    }

    /**
     * Get formatted estimated delivery date
     */
    getFormattedFilterEstimatedDelivery(): string {
        if (!this.selectedFilterEstimatedDelivery) return '';

        const day = this.selectedFilterEstimatedDelivery.getDate().toString().padStart(2, '0');
        const month = (this.selectedFilterEstimatedDelivery.getMonth() + 1).toString().padStart(2, '0');
        const year = this.selectedFilterEstimatedDelivery.getFullYear();

        return `${day}/${month}/${year}`;
    }

    /**
     * Initialize Flatpickr for estimated delivery date field
     */
    private initializeFilterEstimatedDeliveryFlatpickr(): void {
        if (this.filterEstimatedDeliveryInput?.nativeElement) {
            const currentLang = this.translate.currentLang || 'it';

            if (this.filterEstimatedDeliveryFlatpickrInstance) {
                this.filterEstimatedDeliveryFlatpickrInstance.destroy();
            }

            this.filterEstimatedDeliveryFlatpickrInstance = flatpickr(this.filterEstimatedDeliveryInput.nativeElement, {
                dateFormat: 'd/m/Y',
                allowInput: false,
                clickOpens: false,
                locale: currentLang === 'it' ? Italian : undefined,
                defaultDate: this.selectedFilterEstimatedDelivery || undefined,
                onChange: (selectedDates: Date[]) => {
                    if (selectedDates.length > 0) {
                        const selectedDate = new Date(selectedDates[0]);

                        const isDifferentDay = this.selectedFilterEstimatedDelivery &&
                            this.selectedFilterEstimatedDelivery.toDateString() !== selectedDate.toDateString();

                        if (!this.selectedFilterEstimatedDelivery || isDifferentDay) {
                            selectedDate.setHours(0, 0, 0, 0);
                        }

                        this.selectedFilterEstimatedDelivery = selectedDate;
                        this.filterEstimatedDeliveryFlatpickrInstance?.close();
                    }
                },
                onClose: () => {
                    if (this.filterEstimatedDeliveryInput?.nativeElement) {
                        this.filterEstimatedDeliveryInput.nativeElement.blur();
                    }
                }
            });
        }
    }

    /**
     * Open estimated delivery date picker
     */
    openFilterEstimatedDeliveryPicker(): void {
        if (!this.filterEstimatedDeliveryFlatpickrInstance) {
            this.initializeFilterEstimatedDeliveryFlatpickr();
        }

        if (this.filterEstimatedDeliveryFlatpickrInstance && !this.filterEstimatedDeliveryFlatpickrInstance.isOpen) {
            if (this.selectedFilterEstimatedDelivery) {
                this.filterEstimatedDeliveryFlatpickrInstance.setDate(this.selectedFilterEstimatedDelivery);
            }
            this.filterEstimatedDeliveryFlatpickrInstance.open();
        }
    }

    /**
     * Decrement estimated delivery date by one day
     */
    decrementFilterEstimatedDelivery(): void {
        if (!this.selectedFilterEstimatedDelivery) {
            const startOfDay = new Date();
            startOfDay.setHours(0, 0, 0, 0);
            this.selectedFilterEstimatedDelivery = startOfDay;
        }

        const newDate = new Date(this.selectedFilterEstimatedDelivery);
        newDate.setDate(newDate.getDate() - 1);
        newDate.setHours(0, 0, 0, 0);
        this.selectedFilterEstimatedDelivery = newDate;

        if (this.filterEstimatedDeliveryFlatpickrInstance) {
            this.filterEstimatedDeliveryFlatpickrInstance.setDate(this.selectedFilterEstimatedDelivery);
        }
    }

    /**
     * Increment estimated delivery date by one day
     */
    incrementFilterEstimatedDelivery(): void {
        if (!this.selectedFilterEstimatedDelivery) {
            const startOfDay = new Date();
            startOfDay.setHours(0, 0, 0, 0);
            this.selectedFilterEstimatedDelivery = startOfDay;
        }

        const newDate = new Date(this.selectedFilterEstimatedDelivery);
        newDate.setDate(newDate.getDate() + 1);
        newDate.setHours(0, 0, 0, 0);
        this.selectedFilterEstimatedDelivery = newDate;

        if (this.filterEstimatedDeliveryFlatpickrInstance) {
            this.filterEstimatedDeliveryFlatpickrInstance.setDate(this.selectedFilterEstimatedDelivery);
        }
    }

    /**
     * Set estimated delivery date to today
     */
    setFilterEstimatedDeliveryToday(): void {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        this.selectedFilterEstimatedDelivery = today;
        if (this.filterEstimatedDeliveryFlatpickrInstance) {
            this.filterEstimatedDeliveryFlatpickrInstance.setDate(this.selectedFilterEstimatedDelivery);
        }
    }

    /**
     * Clear estimated delivery date
     */
    clearFilterEstimatedDelivery(): void {
        this.selectedFilterEstimatedDelivery = null;
        if (this.filterEstimatedDeliveryFlatpickrInstance) {
            this.filterEstimatedDeliveryFlatpickrInstance.clear();
        }
    }

    /**
     * Get formatted actual delivery date
     */
    getFormattedFilterActualDelivery(): string {
        if (!this.selectedFilterActualDelivery) return '';

        const day = this.selectedFilterActualDelivery.getDate().toString().padStart(2, '0');
        const month = (this.selectedFilterActualDelivery.getMonth() + 1).toString().padStart(2, '0');
        const year = this.selectedFilterActualDelivery.getFullYear();

        return `${day}/${month}/${year}`;
    }

    /**
     * Initialize Flatpickr for actual delivery date field
     */
    private initializeFilterActualDeliveryFlatpickr(): void {
        if (this.filterActualDeliveryInput?.nativeElement) {
            const currentLang = this.translate.currentLang || 'it';

            if (this.filterActualDeliveryFlatpickrInstance) {
                this.filterActualDeliveryFlatpickrInstance.destroy();
            }

            this.filterActualDeliveryFlatpickrInstance = flatpickr(this.filterActualDeliveryInput.nativeElement, {
                dateFormat: 'd/m/Y',
                allowInput: false,
                clickOpens: false,
                locale: currentLang === 'it' ? Italian : undefined,
                defaultDate: this.selectedFilterActualDelivery || undefined,
                onChange: (selectedDates: Date[]) => {
                    if (selectedDates.length > 0) {
                        const selectedDate = new Date(selectedDates[0]);

                        const isDifferentDay = this.selectedFilterActualDelivery &&
                            this.selectedFilterActualDelivery.toDateString() !== selectedDate.toDateString();

                        if (!this.selectedFilterActualDelivery || isDifferentDay) {
                            selectedDate.setHours(0, 0, 0, 0);
                        }

                        this.selectedFilterActualDelivery = selectedDate;
                        this.filterActualDeliveryFlatpickrInstance?.close();
                    }
                },
                onClose: () => {
                    if (this.filterActualDeliveryInput?.nativeElement) {
                        this.filterActualDeliveryInput.nativeElement.blur();
                    }
                }
            });
        }
    }

    /**
     * Open actual delivery date picker
     */
    openFilterActualDeliveryPicker(): void {
        if (!this.filterActualDeliveryFlatpickrInstance) {
            this.initializeFilterActualDeliveryFlatpickr();
        }

        if (this.filterActualDeliveryFlatpickrInstance && !this.filterActualDeliveryFlatpickrInstance.isOpen) {
            if (this.selectedFilterActualDelivery) {
                this.filterActualDeliveryFlatpickrInstance.setDate(this.selectedFilterActualDelivery);
            }
            this.filterActualDeliveryFlatpickrInstance.open();
        }
    }

    /**
     * Decrement actual delivery date by one day
     */
    decrementFilterActualDelivery(): void {
        if (!this.selectedFilterActualDelivery) {
            const startOfDay = new Date();
            startOfDay.setHours(0, 0, 0, 0);
            this.selectedFilterActualDelivery = startOfDay;
        }

        const newDate = new Date(this.selectedFilterActualDelivery);
        newDate.setDate(newDate.getDate() - 1);
        newDate.setHours(0, 0, 0, 0);
        this.selectedFilterActualDelivery = newDate;

        if (this.filterActualDeliveryFlatpickrInstance) {
            this.filterActualDeliveryFlatpickrInstance.setDate(this.selectedFilterActualDelivery);
        }
    }

    /**
     * Increment actual delivery date by one day
     */
    incrementFilterActualDelivery(): void {
        if (!this.selectedFilterActualDelivery) {
            const startOfDay = new Date();
            startOfDay.setHours(0, 0, 0, 0);
            this.selectedFilterActualDelivery = startOfDay;
        }

        const newDate = new Date(this.selectedFilterActualDelivery);
        newDate.setDate(newDate.getDate() + 1);
        newDate.setHours(0, 0, 0, 0);
        this.selectedFilterActualDelivery = newDate;

        if (this.filterActualDeliveryFlatpickrInstance) {
            this.filterActualDeliveryFlatpickrInstance.setDate(this.selectedFilterActualDelivery);
        }
    }

    /**
     * Set actual delivery date to today
     */
    setFilterActualDeliveryToday(): void {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        this.selectedFilterActualDelivery = today;
        if (this.filterActualDeliveryFlatpickrInstance) {
            this.filterActualDeliveryFlatpickrInstance.setDate(this.selectedFilterActualDelivery);
        }
    }

    /**
     * Clear actual delivery date
     */
    clearFilterActualDelivery(): void {
        this.selectedFilterActualDelivery = null;
        if (this.filterActualDeliveryFlatpickrInstance) {
            this.filterActualDeliveryFlatpickrInstance.clear();
        }
    }

    /**
     * Get status class for parcel
     */
    getParcelStatusClass(status: string): string {
        switch (status?.toUpperCase()) {
            case 'DELIVERED':
                return 'bg-success';
            case 'IN_TRANSIT':
                return 'bg-info';
            case 'PENDING':
                return 'bg-warning';
            case 'CANCELLED':
                return 'bg-danger';
            case 'NOT_DELIVERED':
                return 'bg-danger';
            default:
                return 'bg-secondary';
        }
    }

    /**
     * Get status text for parcel
     */
    getParcelStatusText(status: string): string {
        switch (status?.toUpperCase()) {
            case 'ACTIVE':
                return 'SHIPMENT_MANAGEMENT.STATUS_ACTIVE';
            case 'PENDING':
                return 'SHIPMENT_MANAGEMENT.STATUS_PENDING';
            case 'UNSENT':
                return 'SHIPMENT_MANAGEMENT.STATUS_UNSENT';
            case 'SENT':
                return 'SHIPMENT_MANAGEMENT.STATUS_SENT';
            case 'DELIVERED':
                return 'SHIPMENT_MANAGEMENT.STATUS_DELIVERED';
            case 'CANCELLED':
                return 'SHIPMENT_MANAGEMENT.STATUS_CANCELLED';
            case 'NOT_DELIVERED':
                return 'SHIPMENT_MANAGEMENT.STATUS_NOT_DELIVERED';
            case 'DELETED':
                return 'SHIPMENT_MANAGEMENT.STATUS_DELETED';
            case 'IN_TRANSIT':
                return 'SHIPMENT_MANAGEMENT.STATUS_IN_TRANSIT';
            default:
                return 'SHIPMENT_MANAGEMENT.STATUS_UNKNOWN';
        }
    }


    // Methods for carrier assignment popup
    openCarrierAssignmentPopup(): void {
        // Distruggi il datepicker principale per evitare conflitti con quello del popup
        if (this.flatpickrInstance) {
            this.flatpickrInstance.destroy();
            this.flatpickrInstance = null;
            //console.log('🚫 Destroyed main datepicker to avoid conflicts with popup');
        }
        this.isCarrierPopupVisible = true;
    }

    closeCarrierAssignmentPopup(): void {
        this.isCarrierPopupVisible = false;
        // Clear selected waybill to ensure clean state
        this.selectedWaybill = null;
        // Reinizializza il datepicker principale dopo aver chiuso il popup
        setTimeout(() => {
            this.initializeFlatpickr();
            //console.log('✅ Reinitialized main datepicker after popup close');
        }, 100);
        // Deselect the waybill when closing the popup
        this.deselectWaybill();
    }

    onCarrierPopupSubmit(): void {
        // The popup now handles the submission directly
        // This method is called when the popup emits the completed event
        // We just need to handle the response and update the local waybill list
        //console.log('Carrier popup submission completed');
    }

    onCarrierPopupDateChanged(newDate: string): void {
        this.selectedEstimatedDate = newDate;
    }

    onCarrierSelected(carrier: Carriers | null): void {
        // Questo metodo è chiamato dal popup quando viene selezionato un corriere
        // Il form viene già aggiornato dal popup stesso
    }

    /**
     * Select a parcel for editing
     * @param parcel The parcel to select
     */
    selectParcel(parcel: Parcels): void {
        this.selectedParcel = parcel;
        this.parcelForm.patchValue({
            parcelId: parcel.parcelId || '',
            parcelWeight: parcel.parcelWeight || 0,
            parcelVolume: parcel.parcelVolume || 0,
            parcelLength: parcel.parcelLength || 0,
            parcelWidth: parcel.parcelWidth || 0,
            parcelHeight: parcel.parcelHeight || 0
        });
        this.isParcelEditPopupVisible = true;
    }

    openDealerAssignmentPopup(waybill: Waybill): void {
        if (!waybill) return;
        this.selectedWaybill = waybill;
        this.dealerSearchCode = '';
        this.dealerSearchName = '';
        this.dealerSearchCity = '';
        this.dealerSearchAddress = '';
        this.dealerSearchResults = [];
        this.selectedDealerForAssignment = null;
        this.dealerSearchPerformed = false;
        this.isDealerAssignmentPopupVisible = true;
    }

    closeDealerAssignmentPopup(): void {
        this.isDealerAssignmentPopupVisible = false;
        this.dealerSearchCode = '';
        this.dealerSearchName = '';
        this.dealerSearchCity = '';
        this.dealerSearchAddress = '';
        this.dealerSearchResults = [];
        this.selectedDealerForAssignment = null;
    }

    onDealerSearchChange(field: 'code' | 'name' | 'city' | 'address', value: string): void {
        switch (field) {
            case 'code':
                this.dealerSearchCode = value;
                break;
            case 'name':
                this.dealerSearchName = value;
                break;
            case 'city':
                this.dealerSearchCity = value;
                break;
            case 'address':
                this.dealerSearchAddress = value;
                break;
        }
    }

    performDealerSearch(): void {
        this.dealerSearchPerformed = true;
        const clientCode = this.selectedWaybill?.waybillClientCode;
        if (!clientCode) return;
        this.isSearchLoading = true;
        this.shipmentManagementService.searchDealersByClient(clientCode, {
            code: this.dealerSearchCode || undefined,
            name: this.dealerSearchName || undefined,
            city: this.dealerSearchCity || undefined,
            address: this.dealerSearchAddress || undefined
        })
            .pipe(finalize(() => this.isSearchLoading = false))
            .subscribe((list) => this.dealerSearchResults = list || [], () => this.dealerSearchResults = []);
    }


    selectDealerForAssignment(dealer: Dealer): void {
        this.selectedDealerForAssignment = dealer;
    }

    confirmAssignDealer(): void {
        if (!this.selectedWaybill || !this.selectedDealerForAssignment) {
            this.notificationService.warning(this.translate.instant('SHIPMENT_MANAGEMENT.SELECT_DEALER_FIRST'));
            return;
        }

        this.isSubmitting = true;
        this.shipmentManagementService.assignDealer(this.selectedWaybill.waybillId!, this.selectedDealerForAssignment.dealerCode, this.selectedWaybill.waybillClientCode, this.selectedWaybill.waybillAddressAlias)
            .pipe(finalize(() => this.isSubmitting = false))
            .subscribe({
                next: (response: any) => {
                    const message =
                        response?.message ??
                        response?.msg ??
                        response?.Message ??
                        this.translate.instant('SHIPMENT_MANAGEMENT.ASSIGN_DEALER_SUCCESS');
                    this.notificationService.success(message);
                    this.closeDealerAssignmentPopup();
                    this.searchWaybills();
                },
                error: (error: any) => {
                    const errMessage =
                        error?.error?.message ??
                        error?.error?.msg ??
                        error?.message ??
                        this.translate.instant('SHIPMENT_MANAGEMENT.ASSIGN_DEALER_ERROR');
                    this.notificationService.error(errMessage);
                    this.closeDealerAssignmentPopup();
                }
            });
    }

    onClickAssignCarrier(waybill: Waybill): void {
        if (!waybill) return;
        this.selectedWaybill = waybill;
        this.populateForm(waybill);
        this.openCarrierAssignmentPopup();
    }

    /**
     * Close the parcel edit popup
     */
    closeParcelEditPopup(): void {
        this.isParcelEditPopupVisible = false;
        this.selectedParcel = null;
        this.parcelForm.reset();
    }

    // Methods for detail popup
    openDetailPopup(type: string, code: string): void {
        this.detailPopupData = {type, code};
        this.isDetailPopupVisible = true;

        // Reset previous selections
        this.selectedParcelForDetail = null;
        this.selectedWaybillForDetail = null;
        this.selectedDealerForDetailPopup = null;
        this.selectedClientForDetailPopup = null;
        this.selectedWaybillParcels = [];

        // Fetch full data based on type
        if (type === 'collo') {
            // Find parcel by code
            const parcel = this.parcels.find(p => p.parcelCode === code);
            if (parcel) {
                this.selectedParcelForDetail = parcel;
                // Find associated waybill: preferisce quella embedded nel collo
                if (parcel.parcelWaybillCode) {
                    this.selectedWaybillForDetail = parcel.waybill
                        || this.waybills.find(w => w.waybillCode === parcel.parcelWaybillCode)
                        || null;
                    // Find all parcels of the same waybill
                    this.selectedWaybillParcels = this.parcels.filter(p => p.parcelWaybillCode === parcel.parcelWaybillCode);
                }
                // Find associated dealer: preferisce quello embedded nel collo
                if (parcel.parcelDealerCode) {
                    this.selectedDealerForDetailPopup = parcel.dealer || this.getDealerByCode(parcel.parcelDealerCode);
                }
                if (parcel.parcelClientCode) {
                    this.getClientByCode(parcel.parcelClientCode);
                }
            }
        } else if (type === 'waybill' || type === 'ragione-sociale' || type === 'lettera-vettura') {
            // Preferisce la bolla embedded nel collo (completa di dealer e altri dettagli),
            // con fallback sulla lista bolle della vista corrente
            const waybill = this.parcels.find(p => p.parcelWaybillCode === code)?.waybill
                || this.waybills.find(w => w.waybillCode === code);
            if (waybill) {
                this.applyWaybillDetailSelection(waybill);
            } else if (code) {
                // Bolla non disponibile localmente: la recupera dal backend
                this.shipmentManagementService.searchWaybillsPaged(
                    this.getClientsBrandsSelected(),
                    {waybillCode: code},
                    {page: 0, size: 1}
                ).pipe(
                    catchError(() => of(this.emptyPage<Waybill>()))
                ).subscribe(res => {
                    const fetched = res?.content?.[0];
                    if (fetched) {
                        this.applyWaybillDetailSelection(fetched);
                    }
                });
            }
        }
    }

    /** Imposta bolla, colli correlati, dealer e client per il popup di dettaglio */
    private applyWaybillDetailSelection(waybill: Waybill): void {
        this.selectedWaybillForDetail = waybill;
        this.selectedWaybillParcels = this.parcels.filter(p => p.parcelWaybillCode === waybill.waybillCode);
        if (waybill.waybillDealerCode) {
            this.selectedDealerForDetailPopup = waybill.dealer || this.getDealerByCode(waybill.waybillDealerCode);
        }
        if (waybill.waybillClientCode) {
            this.getClientByCode(waybill.waybillClientCode);
        }
    }

    closeDetailPopup(): void {
        this.isDetailPopupVisible = false;
        this.detailPopupData = null;
        this.selectedParcelForDetail = null;
        this.selectedWaybillForDetail = null;
        this.selectedDealerForDetailPopup = null;
        this.selectedWaybillParcels = [];
    }

    /**
     * Apre il popup Axylog dai popup di dettaglio (parcel o waybill)
     * @param waybillCodes Array di codici bolla da inviare ad Axylog
     */
    openAxylogPopupFromDetail(waybillCodes: string[]): void {
        if (!waybillCodes || waybillCodes.length === 0) {
            this.notificationService.warning('Nessuna bolla selezionata per l\'invio in Axylog');
            return;
        }

        // Imposta le bolle selezionate
        this.selectedWaybillCodes.clear();
        waybillCodes.forEach(code => this.selectedWaybillCodes.add(code));

        // Apri il popup Axylog
        this.isAxylogDetailPopupVisible = true;
    }

    /**
     * Apre il popup Mail dai popup di dettaglio (parcel o waybill)
     * @param waybillCodes Array di codici bolla da inviare via email
     */
    openMailPopupFromDetail(waybillCodes: string[]): void {
        if (!waybillCodes || waybillCodes.length === 0) {
            this.notificationService.warning('Nessuna bolla selezionata per l\'invio per mail');
            return;
        }

        // Imposta le bolle selezionate
        this.selectedWaybillCodes.clear();
        waybillCodes.forEach(code => this.selectedWaybillCodes.add(code));

        // Apri il popup Mail
        this.isMailDetailPopupVisible = true;
    }

    // Popup dettaglio cliente: i dati vengono richiesti al backend dal popup stesso
    isClientPopupVisible: boolean = false;
    selectedClientCodeForDetail: string | null = null;

    openClientDetailPopup(clientCode: string | undefined): void {
        if (!clientCode) return;
        this.selectedClientCodeForDetail = clientCode;
        this.isClientPopupVisible = true;
    }

    closeClientDetailPopup(): void {
        this.isClientPopupVisible = false;
        this.selectedClientCodeForDetail = null;
    }

    // Popup dettaglio vettore: i dati vengono richiesti al backend dal popup stesso
    isCarrierDetailsPopupVisible: boolean = false;
    selectedCarrierCodeForDetail: string | null = null;

    openCarrierDetailPopup(carrierCode: string | undefined): void {
        if (!carrierCode) return;
        this.selectedCarrierCodeForDetail = carrierCode;
        this.isCarrierDetailsPopupVisible = true;
    }

    closeCarrierDetailPopup(): void {
        this.isCarrierDetailsPopupVisible = false;
        this.selectedCarrierCodeForDetail = null;
    }

    // Methods for dealer detail popup
    openDealerDetailPopup(dealerCode: string): void {
        const dealer = this.getDealerByCode(dealerCode);
        if (dealer) {
            this.selectedDealerForDetail = dealer;
            this.dealerPopupActiveTab = 'details';
            this.isDealerPopupVisible = true;
        }
    }

    closeDealerDetailPopup(): void {
        this.isDealerPopupVisible = false;
        this.selectedDealerForDetail = null;
        this.dealerPopupActiveTab = 'details';
    }

    onDealerPopupTabChanged(tab: string): void {
        this.dealerPopupActiveTab = tab;
    }

    setDealerPopupTab(tab: string): void {
        this.dealerPopupActiveTab = tab;
    }

    // Methods for parcel completion popup
    openParcelCompletePopup(parcel: Parcels): void {
        //console.log('Opening parcel complete popup for parcel:', parcel);
        this.selectedParcelForCompletion = parcel;
        this.isParcelCompletePopupVisible = true;
        //console.log('isParcelCompletePopupVisible set to:', this.isParcelCompletePopupVisible);
    }

    closeParcelCompletePopup(): void {
        this.isParcelCompletePopupVisible = false;
        this.selectedParcelForCompletion = null;
    }

    onParcelCompleted(completionData: any): void {
        //console.log('Parcel completion data:', completionData);

        // Here you would typically call a service to complete the parcel
        // this.parcelService.completeParcel(completionData).subscribe({
        //   next: (result) => {
        //     this.notificationService.showSuccess('Collo completato con successo');
        //     this.closeParcelCompletePopup();
        //     this.loadData(); // Refresh data
        //   },
        //   error: (error) => {
        //     this.notificationService.showError('Errore nel completamento del collo');
        //   }
        // });

        // For now, just close the popup and show success message
        //this.notificationService.show('Collo completato con successo', 'success');
        this.closeParcelCompletePopup();
    }

    // Methods for waybill completion popup
    openWaybillCompletePopup(waybill: Waybill): void {
        this.selectedWaybillForCompletion = waybill;
        this.isWaybillCompletePopupVisible = true;
    }

    closeWaybillCompletePopup(): void {
        this.isWaybillCompletePopupVisible = false;
        this.selectedWaybillForCompletion = null;
    }

    onWaybillCompleted(completionData: any): void {
        //console.log('Waybill completion data:', completionData);

        // Check if the completion was successful (new format from popup)
        if (completionData.success && completionData.data) {
            //console.log('Waybill completion event received:', completionData);

            // Update the selected waybill in the list with the new data
            if (this.selectedWaybill) {
                const index = this.waybills.findIndex(w => w.waybillId === this.selectedWaybill!.waybillId);
                if (index !== -1) {
                    this.waybills[index] = {...this.waybills[index], ...completionData.data};
                }
            }
            const baseFilters: any = {
                parcelCode: this.searchTerm, // Codice Collo
                waybillCode: this.searchTerm, // Codice Bolla
                dealerCode: this.searchTerm, // Codice Dealer
                date: this.selectedFilterDate // Data (Date object or formatted string)
            };

            // Aggiungi filterWaybillDeliveryMethod solo se valorizzato
            if (this.filterWaybillDeliveryMethod && this.filterWaybillDeliveryMethod.trim() !== '') {
                baseFilters.filterWaybillDeliveryMethod = this.filterWaybillDeliveryMethod;
            }

            this.loadWaybills(baseFilters);

            // The popup handles its own closing and notifications
            // We just need to clean up
            this.selectedWaybill = null;
        } else {
            // Legacy format or direct completion data (fallback)
            // Here you would typically call a service to complete the waybill
            // For now, just close the popup and show success message
            this.closeWaybillCompletePopup();
        }
    }

    // Methods for routing detail popup
    openRoutingDetailPopup(routingCode: string): void {
        const routingSummary = this.routingSummaries.find(r => r.routingCode === routingCode);
        if (routingSummary) {
            this.selectedRoutingSummary = routingSummary;
            this.routingDetailActiveTab = 'summary';
            this.isRoutingDetailPopupVisible = true;
            // Load related waybills and parcels for this routing
            this.loadRoutingDetailData(routingCode);
        }
    }

    closeRoutingDetailPopup(): void {
        this.isRoutingDetailPopupVisible = false;
        this.selectedRoutingSummary = null;
        this.routingDetailWaybills = [];
        this.routingDetailParcels = [];
    }

    onRoutingDetailTabChanged(tab: string): void {
        this.routingDetailActiveTab = tab;
    }

    /**
     * Load routing data for routing view
     */
    loadRoutingData(): void {
        console.log('Loading routing data...');
        // For now, generate mock routing data based on existing waybills and parcels
        // In a real implementation, this would call a service to get actual routing data
        this.generateMockRoutingData();
        console.log('Routing data loaded:', this.routingSummaries.length, 'routings found');
    }

    /**
     * Generate mock routing data based on existing waybills and parcels
     */
    private generateMockRoutingData(): void {
        // Group waybills by date and destination to simulate routing
        const routingMap = new Map<string, {
            waybills: Waybill[];
            parcels: Parcels[];
            destinations: Set<string>;
        }>();

        // Process waybills
        this.waybills.forEach(waybill => {
            if (waybill.waybillDate) {
                const routingDate = waybill.waybillDate.split('T')[0]; // Get date part only
                const routingKey = `ROUTING-${routingDate}`;

                if (!routingMap.has(routingKey)) {
                    routingMap.set(routingKey, {
                        waybills: [],
                        parcels: [],
                        destinations: new Set()
                    });
                }

                const routingData = routingMap.get(routingKey)!;
                routingData.waybills.push(waybill);

                if (waybill.waybillDestinationLocation) {
                    routingData.destinations.add(waybill.waybillDestinationLocation);
                }
            }
        });

        // Process parcels
        this.parcels.forEach(parcel => {
            if (parcel.parcelWaybillCode) {
                // Find the corresponding waybill
                const waybill = this.waybills.find(w => w.waybillCode === parcel.parcelWaybillCode);
                if (waybill && waybill.waybillDate) {
                    const routingDate = waybill.waybillDate.split('T')[0];
                    const routingKey = `ROUTING-${routingDate}`;

                    const routingData = routingMap.get(routingKey);
                    if (routingData) {
                        routingData.parcels.push(parcel);
                    }
                }
            }
        });

        // Convert to RoutingSummary array
        this.routingSummaries = Array.from(routingMap.entries()).map(([routingKey, data]) => {
            const totalWeight = data.waybills.reduce((sum, w) => sum + (w.waybillTotalWeight || 0), 0);
            const totalVolume = data.waybills.reduce((sum, w) => sum + (w.waybillTotalVolume || 0), 0);

            return {
                routingCode: routingKey,
                routingDate: data.waybills[0]?.waybillDate || new Date().toISOString(),
                waybillsCount: data.waybills.length,
                parcelsCount: data.parcels.length,
                destinationsCount: data.destinations.size,
                totalWeight: totalWeight,
                totalVolume: totalVolume,
                totalValue: 0, // Could be calculated if waybills had value field
                routingDescription: `Instradamento automatico per ${data.destinations.size} destinazioni`,
                carrierName: 'Bertani Autotrasporti',
                status: 'ACTIVE'
            } as RoutingSummary;
        });

        // Apply current filters to routing summaries
        this.applyFiltersToRoutingData();
        console.log('Generated routing data:', this.routingSummaries);
    }

    /**
     * Apply filters to routing data
     */
    private applyFiltersToRoutingData(): void {
        this.filteredRoutingSummaries = this.routingSummaries.filter(routing => {
            // Apply search filter
            if (this.unifiedFilters['globalSearch']) {
                const searchTerm = this.unifiedFilters['globalSearch'].toLowerCase();
                const searchableText = [
                    routing.routingCode,
                    routing.routingDescription,
                    routing.carrierName
                ].join(' ').toLowerCase();

                if (!searchableText.includes(searchTerm)) {
                    return false;
                }
            }

            // Apply date filters
            if (this.unifiedFilters['dateFrom'] || this.unifiedFilters['dateTo']) {
                const routingDate = new Date(routing.routingDate);

                if (this.unifiedFilters['dateFrom']) {
                    const fromDate = new Date(this.unifiedFilters['dateFrom']);
                    if (routingDate < fromDate) return false;
                }

                if (this.unifiedFilters['dateTo']) {
                    const toDate = new Date(this.unifiedFilters['dateTo']);
                    toDate.setHours(23, 59, 59, 999); // Include the entire end date
                    if (routingDate > toDate) return false;
                }
            }

            return true;
        });
    }

    /**
     * Load detailed data for a specific routing
     */
    private loadRoutingDetailData(routingCode: string): void {
        // Filter waybills and parcels for this routing
        const routingDate = routingCode.replace('ROUTING-', '');

        this.routingDetailWaybills = this.waybills.filter(waybill =>
            waybill.waybillDate && waybill.waybillDate.startsWith(routingDate)
        );

        this.routingDetailParcels = this.parcels.filter(parcel => {
            const waybill = this.waybills.find(w => w.waybillCode === parcel.parcelWaybillCode);
            return waybill && waybill.waybillDate && waybill.waybillDate.startsWith(routingDate);
        });
    }


    // Method for copying to clipboard
    copyToClipboard(text: string, type: string): void {
        if (navigator.clipboard && window.isSecureContext) {
            // Use the Clipboard API for secure contexts
            navigator.clipboard.writeText(text).then(() => {
                this.showCopySuccessToast(type, text);
            }).catch(err => {
                console.error('Failed to copy text: ', err);
                this.fallbackCopyTextToClipboard(text, type);
            });
        } else {
            // Fallback for older browsers or non-secure contexts
            this.fallbackCopyTextToClipboard(text, type);
        }
    }

    private fallbackCopyTextToClipboard(text: string, type: string): void {
        const textArea = document.createElement("textarea");
        textArea.value = text;

        // Avoid scrolling to bottom
        textArea.style.top = "0";
        textArea.style.left = "0";
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";

        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
            const successful = document.execCommand('copy');
            if (successful) {
                this.showCopySuccessToast(type, text);
            } else {
                console.error('Fallback: Unable to copy');
                this.notificationService.error('Errore durante la copia negli appunti');
            }
        } catch (err) {
            console.error('Fallback: Unable to copy', err);
            this.notificationService.error('Errore durante la copia negli appunti');
        }

        document.body.removeChild(textArea);
    }

    private showCopySuccessToast(type: string, text: string): void {
        // Tipi "storici" con etichetta dedicata; per gli altri campi il tipo è già
        // l'etichetta leggibile passata dal template (es. 'Peso', 'Stima Consegna')
        const knownLabels: Record<string, string> = {
            'waybill': 'Codice Bolla',
            'ragione-sociale': 'Ragione Sociale',
            'instradamento': 'Instradamento',
            'collo': 'Codice Collo'
        };
        const typeLabel = knownLabels[type] ?? type;
        this.notificationService.success(`${typeLabel} "${text}" copiato negli appunti!`);
    }

    /** Valore data formattato come in tabella, per la copia negli appunti */
    formatDateForCopy(value: string | undefined | null): string {
        if (!value) return '';
        try {
            return formatDate(value, 'dd/MM/yy HH:mm', 'it');
        } catch {
            return String(value);
        }
    }

    /**
     * Puntualità della consegna: confronta la Data Consegna con la Stima Consegna (data massima
     * di consegna), al minuto come mostrate in tabella. Null se manca una delle due date.
     */
    getDeliveryPunctuality(actual: string | undefined | null, estimated: string | undefined | null): 'on-time' | 'late' | null {
        if (!actual || !estimated) return null;
        const actualMinutes = Math.floor(new Date(actual).getTime() / 60000);
        const estimatedMinutes = Math.floor(new Date(estimated).getTime() / 60000);
        if (isNaN(actualMinutes) || isNaN(estimatedMinutes)) return null;
        return actualMinutes <= estimatedMinutes ? 'on-time' : 'late';
    }

    /** Etichetta tradotta dello stato bolla, per la copia negli appunti */
    getWaybillStatusLabelForCopy(status: string | undefined | null): string {
        const key = this.getParcelStatusText(status || '');
        return key ? this.translate.instant(key) : '';
    }

    /** Valore numerico formattato come in tabella, per la copia negli appunti */
    formatNumberForCopy(value: number | undefined | null, digitsInfo: string): string {
        return formatNumber(value || 0, 'it', digitsInfo);
    }

    // ==================== STAMPA ETICHETTA COLLO ====================

    /** Codice del collo la cui etichetta è in generazione (per lo spinner sul pulsante) */
    printingLabelParcelCode: string | null = null;

    isPrintingParcelLabel(parcel: Parcels): boolean {
        return !!parcel.parcelCode && this.printingLabelParcelCode === parcel.parcelCode;
    }

    /**
     * Stampa l'etichetta del singolo collo dalla tabella colli.
     * Stesso flusso del popup di dettaglio collo: recupera bolla e dealer
     * (preferendo quelli embedded nel collo) e genera il PDF con LabelGenerationService.
     */
    printParcelLabel(parcel: Parcels): void {
        if (!parcel?.parcelCode) {
            this.notificationService.error('Nessun collo selezionato');
            return;
        }
        if (this.printingLabelParcelCode) return;

        const dealer = parcel.dealer
            || (parcel.parcelDealerCode ? this.getDealerByCode(parcel.parcelDealerCode) : undefined)
            || null;

        const localWaybill = parcel.waybill
            || (parcel.parcelWaybillCode
                ? this.waybills.find(w => w.waybillCode === parcel.parcelWaybillCode)
                : undefined);

        if (localWaybill || !parcel.parcelWaybillCode) {
            this.generateParcelLabel(parcel, localWaybill ?? null, dealer);
            return;
        }

        // Bolla non disponibile localmente: la recupera dal backend
        this.printingLabelParcelCode = parcel.parcelCode;
        this.shipmentManagementService.searchWaybillsPaged(
            this.getClientsBrandsSelected(),
            {waybillCode: parcel.parcelWaybillCode},
            {page: 0, size: 1}
        ).pipe(
            catchError(() => of(this.emptyPage<Waybill>()))
        ).subscribe(res => {
            const fetched = res?.content?.[0] ?? null;
            this.printingLabelParcelCode = null;
            this.generateParcelLabel(parcel, fetched, dealer ?? fetched?.dealer ?? null);
        });
    }

    private generateParcelLabel(parcel: Parcels, waybill: Waybill | null, dealer: any): void {
        if (!waybill) {
            this.notificationService.error(`Bolla non disponibile per il collo ${parcel.parcelCode}`);
            return;
        }
        this.printingLabelParcelCode = parcel.parcelCode;
        this.labelGenerationService.generateWaybillLabels([parcel as any], waybill as any, dealer)
            .pipe(finalize(() => this.printingLabelParcelCode = null))
            .subscribe({
                next: (success) => {
                    if (success) {
                        this.notificationService.success(`Etichetta generata per collo ${parcel.parcelCode}`);
                    } else {
                        this.notificationService.error('Errore nella generazione dell\'etichetta');
                    }
                },
                error: (error) => {
                    console.error('Error generating label:', error);
                    this.notificationService.error('Errore nella generazione dell\'etichetta');
                }
            });
    }

    /**
     * Submit the parcel form
     */
    onParcelSubmit(): void {
        if (this.parcelForm.valid && this.selectedParcel) {
            const formData = this.parcelForm.value;
            const updatedParcel: Parcels = {
                ...this.selectedParcel,
                parcelWeight: formData.parcelWeight,
                parcelVolume: formData.parcelVolume,
                parcelLength: formData.parcelLength,
                parcelWidth: formData.parcelWidth,
                parcelHeight: formData.parcelHeight
            };

            this.shipmentManagementService.updateParcel(updatedParcel.parcelCode!, updatedParcel).subscribe({
                next: () => {
                    this.notificationService.success('Collo aggiornato con successo');

                    // Aggiorna il collo anche nell'array originale
                    const index = this.originalParcels.findIndex(p => p.parcelCode === updatedParcel.parcelCode);
                    if (index !== -1) {
                        this.originalParcels[index] = {...updatedParcel};
                    }

                    this.closeParcelEditPopup();
                    this.refreshResults(); // Ricarica la vista corrente + resoconto
                },
                error: (error: any) => {
                    console.error('Error updating parcel:', error);
                    this.notificationService.error('Errore durante l\'aggiornamento del collo');
                }
            });
        }
    }

    /**
     * Download waybill document as PDF
     * @param waybillId ID of the waybill to download
     */
    downloadWaybillDocument(waybillId: number): void {
        //console.log('Downloading waybill document for waybill:', waybillId);

        // Find the waybill
        const waybill = this.waybills.find(w => w.waybillId === waybillId);
        if (!waybill) {
            this.notificationService.error('Bolla non trovata');
            return;
        }

    // Use the WaybillService to download PDF from backend
    this.waybillService.downloadPdf(waybill.waybillCode, waybill.waybillShipmentCode);

        // Show success message
        this.notificationService.success(`Download PDF avviato per bolla ${waybill.waybillCode}`);
    }

    /**
     * Download POD document for a waybill
     */
    downloadPodDocument(waybillId: number): void {
        const waybill = this.waybills.find(w => w.waybillId === waybillId);
        if (!waybill) {
            this.notificationService.error('Bolla non trovata');
            return;
        }

        this.shipmentManagementService.downloadPod(waybill.waybillCode, waybill.waybillShipmentCode);

        this.notificationService.success(`Download POD avviato per bolla ${waybill.waybillCode}`);
    }

    /**
     * Toggle column filters visibility
     */
    toggleColumnFilters(): void {
        this.isColumnFiltersVisible = !this.isColumnFiltersVisible;
        if (this.isColumnFiltersVisible) {
            // Aggiorna i valori unici quando si aprono i filtri
            this.populateUniqueUnifiedValues();
        } else {
            // Reimposta i filtri quando si chiudono
            this.resetUnifiedFilters();
        }
        this.saveUserPreferences(); // Save preferences
    }


    /**
     * Inizializza i filtri unificati
     */
    initializeUnifiedFilters() {
        // Chiavi dei filtri
        const filterKeys = [
            'waybillCode', 'parcelCode', 'date', 'estimatedDelivery', 'deliveryType', 'companyName',
            'dealerCode', 'clientCode', 'brandCode', 'address', 'city', 'province',
            'status', 'parcelType', 'weight', 'volume'
        ];

        // Inizializza i filtri con stringhe vuote
        filterKeys.forEach(key => {
            this.unifiedFilters[key] = '';
            this.showUnifiedSuggestions[key] = false;
            this.filteredUnifiedValues[key] = [];
        });

        // Popola i valori unici da bolle, colli e dealer
        this.populateUniqueUnifiedValues();
    }

    /**
     * Formatta una data nel formato dd/mm/yyyy
     */
    private formatDateToDDMMYYYY(dateValue: any): string {
        if (!dateValue) return '';

        try {
            let date: Date;

            // Se è già una stringa nel formato dd/mm/yyyy, la restituisce così com'è
            if (typeof dateValue === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(dateValue)) {
                return dateValue;
            }

            // Se è una stringa, prova a parsarla
            if (typeof dateValue === 'string') {
                // Prova formato ISO (yyyy-mm-dd o yyyy-mm-ddThh:mm:ss)
                if (dateValue.includes('-')) {
                    date = new Date(dateValue);
                }
                // Prova altri formati
                else {
                    date = new Date(dateValue);
                }
            } else {
                date = new Date(dateValue);
            }

            // Verifica che la data sia valida
            if (isNaN(date.getTime())) {
                return String(dateValue);
            }

            // Formatta nel formato dd/mm/yyyy
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear().toString();

            return `${day}/${month}/${year}`;
        } catch (error) {
            console.warn('Error formatting date:', dateValue, error);
            return String(dateValue);
        }
    }

    /**
     * Confronta una data formattata (dd/mm/yyyy) con una data originale
     */
    private dateMatches(originalDate: any, formattedDateFilter: string): boolean {
        if (!originalDate || !formattedDateFilter) return false;

        try {
            // Formatta la data originale nello stesso formato del filtro
            const formattedOriginal = this.formatDateToDDMMYYYY(originalDate);

            // Confronta le stringhe formattate
            return formattedOriginal.toLowerCase().includes(formattedDateFilter.toLowerCase());
        } catch (error) {
            console.warn('Error comparing dates:', originalDate, formattedDateFilter, error);
            return false;
        }
    }

    /**
     * Popola i valori unici per i dropdown dei suggerimenti
     */
    populateUniqueUnifiedValues() {
        // Valori unici per waybill
        const waybillKeys: Record<string, keyof Waybill> = {
            'waybillCode': 'waybillCode',
            'date': 'waybillDate',
            'estimatedDelivery': 'waybillEstimatedDeliveryDate',
            'deliveryType': 'waybillDeliveryType',
            'status': 'waybillStatus',
            'dealerCode': 'waybillDealerCode'
        };

        // Popola i valori unici dalle bolle
        for (const [filterKey, waybillKey] of Object.entries(waybillKeys)) {
            if (filterKey === 'date' || filterKey === 'estimatedDelivery') {
                // Formatta le date nel formato dd/mm/yyyy
                this.uniqueUnifiedValues[filterKey] = Array.from(new Set(
                    this.waybills
                        .map(waybill => this.formatDateToDDMMYYYY(waybill[waybillKey]))
                        .filter(value => value !== '')
                )).sort();
            } else {
                this.uniqueUnifiedValues[filterKey] = Array.from(new Set(
                    this.waybills
                        .map(waybill => String(waybill[waybillKey] || ''))
                        .filter(value => value !== '')
                )).sort();
            }
        }

        // Valori unici per parcel
        const parcelKeys: Record<string, keyof Parcels> = {
            'parcelCode': 'parcelCode',
            'date': 'parcelDate',
            'dealerCode': 'parcelDealerCode',
            'clientCode': 'parcelClientCode',
            'brandCode': 'parcelBrandCode',
            'weight': 'parcelWeight',
            'volume': 'parcelVolume',
            'parcelType': 'parcelType'
        };

        // Popola i valori unici dai colli
        for (const [filterKey, parcelKey] of Object.entries(parcelKeys)) {
            let uniqueValues: string[];

            if (filterKey === 'date') {
                // Formatta le date nel formato dd/mm/yyyy
                uniqueValues = Array.from(new Set(
                    this.parcels
                        .map(parcel => this.formatDateToDDMMYYYY(parcel[parcelKey]))
                        .filter(value => value !== '')
                )).sort();
            } else if (filterKey === 'parcelType') {
                // Mappa i valori di parcelType: U -> Urgente, S -> Stock
                uniqueValues = Array.from(new Set(
                    this.parcels
                        .map(parcel => {
                            const type = parcel[parcelKey] as string;
                            return type ? this.getParcelTypeLabel(type) : '';
                        })
                        .filter(value => value !== '')
                )).sort();
            } else {
                uniqueValues = Array.from(new Set(
                    this.parcels
                        .map(parcel => String(parcel[parcelKey] || ''))
                        .filter(value => value !== '')
                )).sort();
            }

            // Per alcuni campi, unisce i valori di bolle e colli
            if (filterKey === 'date' || filterKey === 'dealerCode') {
                this.uniqueUnifiedValues[filterKey] = Array.from(new Set([
                    ...this.uniqueUnifiedValues[filterKey] || [],
                    ...uniqueValues
                ])).sort();
            } else {
                this.uniqueUnifiedValues[filterKey] = uniqueValues;
            }
        }

        // Popola i valori unici dai dealer
        this.uniqueUnifiedValues['companyName'] = Array.from(new Set(
            this.dealers
                .map(dealer => String(dealer.dealerName || dealer.dealerLegalName || ''))
                .filter(value => value !== '')
        )).sort();

        this.uniqueUnifiedValues['address'] = Array.from(new Set(
            this.dealers
                .map(dealer => {
                    // Prova diverse combinazioni di campi indirizzo
                    let address = '';

                    // Combinazione preferita: strada + numero
                    if (dealer.dealerOperatingAddressStreet && dealer.dealerOperatingAddressNumber) {
                        address = `${dealer.dealerOperatingAddressStreet} ${dealer.dealerOperatingAddressNumber}`;
                    }
                    // Solo strada
                    else if (dealer.dealerOperatingAddressStreet) {
                        address = dealer.dealerOperatingAddressStreet;
                    }
                    // Prova altri possibili campi indirizzo
                    else if (dealer.dealerOperatingAddressCity) {
                        address = dealer.dealerOperatingAddressCity;
                    }
                    // Fallback su qualsiasi campo che contenga "address"
                    else {
                        const dealerKeys = Object.keys(dealer);
                        const addressKey = dealerKeys.find(key =>
                            key.toLowerCase().includes('address') &&
                            dealer[key as keyof typeof dealer]
                        );
                        if (addressKey) {
                            address = String(dealer[addressKey as keyof typeof dealer]);
                        }
                    }

                    return address.trim();
                })
                .filter(value => value !== '')
        )).sort();

        console.log(`Populated ${this.uniqueUnifiedValues['address'].length} unique addresses from ${this.dealers.length} dealers`);
        //console.log('Sample addresses:', this.uniqueUnifiedValues['address'].slice(0, 5));

        // Log anche i primi dealers per debug
        if (this.dealers.length > 0) {
            //console.log('Sample dealer for address debug:', {
            //  dealerOperatingAddressStreet: this.dealers[0].dealerOperatingAddressStreet,
            //  dealerOperatingAddressNumber: this.dealers[0].dealerOperatingAddressNumber,
            //  dealerOperatingAddressCity: this.dealers[0].dealerOperatingAddressCity,
            //  allKeys: Object.keys(this.dealers[0])
            //});
        }

        this.uniqueUnifiedValues['city'] = Array.from(new Set(
            this.dealers
                .map(dealer => String(dealer.dealerOperatingAddressCity || ''))
                .filter(value => value !== '')
        )).sort();

        this.uniqueUnifiedValues['province'] = Array.from(new Set(
            this.dealers
                .map(dealer => String(dealer.dealerOperatingAddressProvince || ''))
                .filter(value => value !== '')
        )).sort();
    }

    /**
     * Gestione dell'input sui filtri unificati
     */
    onUnifiedFilterInput(event: Event, filterKey: string) {
        const inputValue = (event.target as HTMLInputElement).value;
        this.unifiedFilters[filterKey] = inputValue;

        if (this.uniqueUnifiedValues[filterKey]) {
            this.filteredUnifiedValues[filterKey] = this.uniqueUnifiedValues[filterKey].filter(
                value => value.toLowerCase().includes(inputValue.toLowerCase())
            );
        }

        this.showUnifiedSuggestions[filterKey] = true;

        // Applica immediatamente i filtri mentre si scrive
        this.applyUnifiedFilters();
    }

    /**
     * Gestione del focus sui filtri unificati - mostra immediatamente tutti i suggerimenti
     */
    onUnifiedFilterFocus(filterKey: string) {
        // Assicuriamoci che i valori unici siano popolati
        if (!this.uniqueUnifiedValues[filterKey] || this.uniqueUnifiedValues[filterKey].length === 0) {
            console.log(`Populating unique values for ${filterKey}. Current dealers count: ${this.dealers.length}, waybills count: ${this.waybills.length}, parcels count: ${this.parcels.length}`);
            this.populateUniqueUnifiedValues();
        }

        if (this.uniqueUnifiedValues[filterKey]) {
            console.log(`Unique values for ${filterKey}:`, this.uniqueUnifiedValues[filterKey]);
            // Se c'è già del testo, filtra i suggerimenti, altrimenti mostra tutto
            const inputValue = this.unifiedFilters[filterKey] || '';
            if (inputValue.trim() === '') {
                // Se l'input è vuoto, mostra tutti i valori disponibili
                this.filteredUnifiedValues[filterKey] = [...this.uniqueUnifiedValues[filterKey]];
            } else {
                // Se c'è del testo, filtra in base al testo esistente
                this.filteredUnifiedValues[filterKey] = this.uniqueUnifiedValues[filterKey].filter(
                    value => value.toLowerCase().includes(inputValue.toLowerCase())
                );
            }
        } else {
            console.log(`No unique values found for ${filterKey}`);
        }

        this.showUnifiedSuggestions[filterKey] = true;
    }

    /**
     * Nasconde i suggerimenti per un filtro specifico
     */
    hideUnifiedFilterSuggestions(filterKey: string) {
        setTimeout(() => {
            this.showUnifiedSuggestions[filterKey] = false;
        }, 200);
    }

    /**
     * Seleziona un valore dal dropdown suggerimenti
     */
    selectUnifiedFilterValue(filterKey: string, value: string) {
        this.unifiedFilters[filterKey] = value;
        this.showUnifiedSuggestions[filterKey] = false;

        // Applica immediatamente i filtri quando si seleziona un valore
        this.applyUnifiedFilters();
    }

    /**
     * Reimposta un singolo filtro unificato
     */
    resetSingleUnifiedFilter(filterKey: string) {
        this.unifiedFilters[filterKey] = '';
        this.showUnifiedSuggestions[filterKey] = false;

        // Resetta anche l'operatore se è un campo numerico
        if (filterKey === 'weight') {
            this.unifiedWeightOperator = '=';
        } else if (filterKey === 'volume') {
            this.unifiedVolumeOperator = '=';
        }

        // Applica immediatamente i filtri
        this.applyUnifiedFilters();
    }

    /**
     * Imposta l'operatore di confronto per campi numerici
     */
    setUnifiedComparisonOperator(field: string) {
        // Applica immediatamente i filtri dopo il cambio operatore
        this.applyUnifiedFilters();
    }

    /**
     * Reimposta tutti i filtri unificati
     */
    resetUnifiedFilters() {
        // Reimposta tutti i filtri a stringa vuota
        Object.keys(this.unifiedFilters).forEach(key => {
            this.unifiedFilters[key] = '';
        });

        // Reimposta gli operatori
        this.unifiedWeightOperator = '=';
        this.unifiedVolumeOperator = '=';

        // Ricalcola: senza filtri attivi prende tutti i dati originali,
        // applicando comunque il sort corrente se presente.
        this.refreshTableData();
        this.currentPage = 1; // Reset to first page
    }

    /**
     * Applica i filtri unificati a bolle e colli mantenendo la coerenza
     */
    applyUnifiedFilters() {
        // Imposta loading state
        this.isUnifiedFilterLoading = true;

        // Riapplica filtri + sort sui dati originali in un unico passaggio
        this.refreshTableData();
        this.currentPage = 1; // Reset to first page after filtering

        // Reset loading state dopo un breve delay
        setTimeout(() => {
            this.isUnifiedFilterLoading = false;
        }, 1000);
    }

    /**
     * Calcola bolle e colli filtrati a partire dai dati originali, in base ai
     * filtri unificati correnti. Funzione pura: non modifica lo stato del
     * componente, ritorna due nuovi array.
     */
    private computeFilteredData(): { waybills: Waybill[]; parcels: Parcels[] } {
        // Prepara le variabili per i risultati filtrati
        let filteredWaybills = [...this.originalWaybills];
        let filteredParcels = [...this.originalParcels];

        // Filtra le bolle
        if (this.unifiedFilters['waybillCode']) {
            filteredWaybills = filteredWaybills.filter(waybill =>
                waybill.waybillCode && waybill.waybillCode.toLowerCase().includes(this.unifiedFilters['waybillCode'].toLowerCase())
            );
        }

        if (this.unifiedFilters['date']) {
            filteredWaybills = filteredWaybills.filter(waybill => {
                if (!waybill.waybillDate) return false;
                return this.dateMatches(waybill.waybillDate, this.unifiedFilters['date']);
            });
        }

        if (this.unifiedFilters['estimatedDelivery']) {
            filteredWaybills = filteredWaybills.filter(waybill => {
                if (!waybill.waybillEstimatedDeliveryDate) return false;
                return this.dateMatches(waybill.waybillEstimatedDeliveryDate, this.unifiedFilters['estimatedDelivery']);
            });
        }

        if (this.unifiedFilters['deliveryType']) {
            filteredWaybills = filteredWaybills.filter(waybill =>
                waybill.waybillDeliveryType && waybill.waybillDeliveryType.toLowerCase().includes(this.unifiedFilters['deliveryType'].toLowerCase())
            );
        }

        if (this.unifiedFilters['status']) {
            filteredWaybills = filteredWaybills.filter(waybill =>
                waybill.waybillStatus && waybill.waybillStatus.toLowerCase().includes(this.unifiedFilters['status'].toLowerCase())
            );
        }

        if (this.unifiedFilters['dealerCode']) {
            filteredWaybills = filteredWaybills.filter(waybill =>
                waybill.waybillDealerCode && waybill.waybillDealerCode.toLowerCase().includes(this.unifiedFilters['dealerCode'].toLowerCase())
            );
        }

        // Filtri basati sulle informazioni del dealer
        if (this.unifiedFilters['companyName'] || this.unifiedFilters['address'] ||
            this.unifiedFilters['city'] || this.unifiedFilters['province']) {

            filteredWaybills = filteredWaybills.filter(waybill => {
                const dealer = this.getDealerByCode(waybill.waybillDealerCode);
                if (!dealer) return false;

                // Filtra per ragione sociale
                if (this.unifiedFilters['companyName']) {
                    const companyName = (dealer.dealerName || dealer.dealerLegalName || '').toLowerCase();
                    if (!companyName.includes(this.unifiedFilters['companyName'].toLowerCase())) {
                        return false;
                    }
                }

                // Filtra per indirizzo
                if (this.unifiedFilters['address']) {
                    const address = `${dealer.dealerOperatingAddressStreet || ''} ${dealer.dealerOperatingAddressNumber || ''}`.toLowerCase();
                    if (!address.includes(this.unifiedFilters['address'].toLowerCase())) {
                        return false;
                    }
                }

                // Filtra per città
                if (this.unifiedFilters['city']) {
                    const city = (dealer.dealerOperatingAddressCity || '').toLowerCase();
                    if (!city.includes(this.unifiedFilters['city'].toLowerCase())) {
                        return false;
                    }
                }

                // Filtra per provincia
                if (this.unifiedFilters['province']) {
                    const province = (dealer.dealerOperatingAddressProvince || '').toLowerCase();
                    if (!province.includes(this.unifiedFilters['province'].toLowerCase())) {
                        return false;
                    }
                }

                return true;
            });
        }

        // Filtra i colli
        if (this.unifiedFilters['parcelCode']) {
            filteredParcels = filteredParcels.filter(parcel =>
                parcel.parcelCode && parcel.parcelCode.toLowerCase().includes(this.unifiedFilters['parcelCode'].toLowerCase())
            );
        }

        if (this.unifiedFilters['date']) {
            filteredParcels = filteredParcels.filter(parcel => {
                if (!parcel.parcelDate) return false;
                return this.dateMatches(parcel.parcelDate, this.unifiedFilters['date']);
            });
        }

        if (this.unifiedFilters['dealerCode']) {
            filteredParcels = filteredParcels.filter(parcel =>
                parcel.parcelDealerCode && parcel.parcelDealerCode.toLowerCase().includes(this.unifiedFilters['dealerCode'].toLowerCase())
            );
        }

        if (this.unifiedFilters['clientCode']) {
            filteredParcels = filteredParcels.filter(parcel =>
                parcel.parcelClientCode && parcel.parcelClientCode.toLowerCase().includes(this.unifiedFilters['clientCode'].toLowerCase())
            );
        }

        if (this.unifiedFilters['brandCode']) {
            filteredParcels = filteredParcels.filter(parcel =>
                parcel.parcelBrandCode && parcel.parcelBrandCode.toLowerCase().includes(this.unifiedFilters['brandCode'].toLowerCase())
            );
        }

        if (this.unifiedFilters['parcelType']) {
            filteredParcels = filteredParcels.filter(parcel => {
                if (!parcel.parcelType) return false;
                return this.getParcelTypeLabel(parcel.parcelType).toLowerCase().includes(this.unifiedFilters['parcelType'].toLowerCase());
            });
        }

        // Filtri numerici con operatori di confronto
        if (this.unifiedFilters['weight']) {
            const weightValue = parseFloat(this.unifiedFilters['weight']);
            if (!isNaN(weightValue)) {
                filteredParcels = filteredParcels.filter(parcel => {
                    if (parcel.parcelWeight === undefined || parcel.parcelWeight === null) return false;
                    const parcelWeight = parseFloat(parcel.parcelWeight.toString());

                    switch (this.unifiedWeightOperator) {
                        case '>':
                            return parcelWeight > weightValue;
                        case '<':
                            return parcelWeight < weightValue;
                        case '=':
                        default:
                            return Math.abs(parcelWeight - weightValue) < 0.001; // Tolleranza per confronto numeri float
                    }
                });
            }
        }

        if (this.unifiedFilters['volume']) {
            const volumeValue = parseFloat(this.unifiedFilters['volume']);
            if (!isNaN(volumeValue)) {
                filteredParcels = filteredParcels.filter(parcel => {
                    if (parcel.parcelVolume === undefined || parcel.parcelVolume === null) return false;
                    const parcelVolume = parseFloat(parcel.parcelVolume.toString());

                    switch (this.unifiedVolumeOperator) {
                        case '>':
                            return parcelVolume > volumeValue;
                        case '<':
                            return parcelVolume < volumeValue;
                        case '=':
                        default:
                            return Math.abs(parcelVolume - volumeValue) < 0.0001; // Tolleranza più piccola per volumi
                    }
                });
            }
        }

        // FILTRI INCROCIATI OBBLIGATORI - Mantieni sempre la coerenza tra bolle e colli

        // 1. Filtra bolle per mostrare solo quelle che hanno colli tra quelli filtrati
        const validWaybillCodes = new Set(filteredParcels.map(p => p.parcelWaybillCode).filter(code => code));
        filteredWaybills = filteredWaybills.filter(waybill =>
            waybill.waybillCode && validWaybillCodes.has(waybill.waybillCode)
        );

        // 2. Filtra colli per mostrare solo quelli appartenenti alle bolle filtrate
        const validWaybillCodesFromBills = new Set(filteredWaybills.map(w => w.waybillCode).filter(code => code));
        filteredParcels = filteredParcels.filter(parcel =>
            parcel.parcelWaybillCode && validWaybillCodesFromBills.has(parcel.parcelWaybillCode)
        );

        // 3. Coerenza dealer - assicurati che i dealer dei colli corrispondano a quelli delle bolle
        const validDealerCodes = new Set(filteredWaybills.map(w => w.waybillDealerCode).filter(code => code));
        filteredParcels = filteredParcels.filter(parcel =>
            !parcel.parcelDealerCode || validDealerCodes.has(parcel.parcelDealerCode)
        );

        // 4. Aggiorna nuovamente le bolle per mantenere la coerenza dopo il filtro dealer
        const finalValidWaybillCodes = new Set(filteredParcels.map(p => p.parcelWaybillCode).filter(code => code));
        filteredWaybills = filteredWaybills.filter(waybill =>
            waybill.waybillCode && finalValidWaybillCodes.has(waybill.waybillCode)
        );

        return { waybills: filteredWaybills, parcels: filteredParcels };
    }

    // Tour Guide functionality
    startTour(): void {
        // Esporre il componente al window per consentire al tour di accedere ai metodi
        (window as any).shipmentManagementComponent = this;
        this.tourGuideService.startTour(SHIPMENT_MANAGEMENT_TOUR_CONFIG);
    }

    // Multiple selection methods
    toggleSelectionMode(): void {
        this.isSelectionModeActive = !this.isSelectionModeActive;
        if (!this.isSelectionModeActive) {
            // Clear all selections when disabling selection mode
            this.selectedWaybillCodes.clear();
            this.selectedParcelCodes.clear();
        }
    }

    toggleWaybillSelection(waybillCode: string): void {
        if (this.selectedWaybillCodes.has(waybillCode)) {
            this.selectedWaybillCodes.delete(waybillCode);
        } else {
            this.selectedWaybillCodes.add(waybillCode);
        }
    }

    toggleParcelSelection(parcelCode: string): void {
        if (this.selectedParcelCodes.has(parcelCode)) {
            this.selectedParcelCodes.delete(parcelCode);
        } else {
            this.selectedParcelCodes.add(parcelCode);
        }
    }

    isWaybillSelected(waybillCode: string): boolean {
        return this.selectedWaybillCodes.has(waybillCode);
    }

    isParcelSelected(parcelCode: string): boolean {
        return this.selectedParcelCodes.has(parcelCode);
    }

    selectAllWaybills(): void {
        this.waybills.forEach(waybill => {
            if (waybill.waybillCode) {
                this.selectedWaybillCodes.add(waybill.waybillCode);
            }
        });
    }

    deselectAllWaybills(): void {
        this.selectedWaybillCodes.clear();
    }

    selectAllParcels(): void {
        this.parcels.forEach(parcel => {
            if (parcel.parcelCode) {
                this.selectedParcelCodes.add(parcel.parcelCode);
            }
        });
    }

    deselectAllParcels(): void {
        this.selectedParcelCodes.clear();
    }

    areAllWaybillsSelected(): boolean {
        return this.waybills.length > 0 && this.waybills.every(waybill =>
            waybill.waybillCode && this.selectedWaybillCodes.has(waybill.waybillCode)
        );
    }

    areAllParcelsSelected(): boolean {
        return this.parcels.length > 0 && this.parcels.every(parcel =>
            parcel.parcelCode && this.selectedParcelCodes.has(parcel.parcelCode)
        );
    }

    // Routing selection methods
    areAllRoutingsSelected(): boolean {
        return this.routingSummaries.length > 0 && this.routingSummaries.every(routing =>
            this.selectedRoutingCodes.has(routing.routingCode)
        );
    }

    isRoutingSelected(routingCode: string): boolean {
        return this.selectedRoutingCodes.has(routingCode);
    }

    toggleRoutingSelection(routingCode: string): void {
        if (this.selectedRoutingCodes.has(routingCode)) {
            this.selectedRoutingCodes.delete(routingCode);
        } else {
            this.selectedRoutingCodes.add(routingCode);
        }
    }

    selectAllRoutings(): void {
        this.routingSummaries.forEach(routing => {
            this.selectedRoutingCodes.add(routing.routingCode);
        });
    }

    deselectAllRoutings(): void {
        this.selectedRoutingCodes.clear();
    }

    // Get waybills for a specific routing
    getWaybillsForRouting(routingCode: string): Waybill[] {
        if (!routingCode) return [];
        // In a real implementation, this would filter waybills by routing code
        // For now, return a subset of waybills as example
        return this.waybills.filter(waybill =>
            waybill.waybillDeliveryType === routingCode ||
            waybill.waybillCode?.includes(routingCode.slice(-3))
        );
    }

    // Get parcels for a specific routing
    getParcelsForRouting(routingCode: string): Parcels[] {
        if (!routingCode) return [];
        // In a real implementation, this would filter parcels by routing code
        // Get waybills for this routing first, then get their parcels
        const routingWaybills = this.getWaybillsForRouting(routingCode);
        const waybillCodes = routingWaybills.map(w => w.waybillCode);
        return this.parcels.filter(parcel =>
            parcel.parcelWaybillCode && waybillCodes.includes(parcel.parcelWaybillCode)
        );
    }

    // Close routing detail popup
    onRoutingDetailClosed(): void {
        this.isRoutingDetailPopupVisible = false;
        this.selectedRoutingSummary = null;
    }

    // Metodi per i nuovi pulsanti di azione
    printSelectedPDF(): void {
        const selectedWaybillCodes = Array.from(this.selectedWaybillCodes);

        if (selectedWaybillCodes.length === 0) {
            this.notificationService.warning('Nessuna bolla selezionata per la stampa PDF');
            return;
        }

        if (selectedWaybillCodes.length > 100) {
            this.notificationService.warning('Puoi stampare al massimo 100 bolle alla volta');
            return;
        }

        // Verifica lo status delle bolle selezionate
        this.validateWaybillStatusAndProceed('pdf', selectedWaybillCodes);
    }

    sendToAxylog(): void {
        // Nascondi il tooltip prima di aprire il popup
        this.hideTooltips();

        const selectedWaybillCodes = Array.from(this.selectedWaybillCodes);

        if (selectedWaybillCodes.length === 0) {
            this.notificationService.warning('Nessuna bolla selezionata per l\'invio in Axylog');
            return;
        }

        // Verifica il limite di 100 elementi
        if (selectedWaybillCodes.length > 100) {
            this.notificationService.warning('Non è possibile elaborare più di 100 bolle contemporaneamente');
            return;
        }

        // Apri il popup per raccogliere dettagli
        this.isAxylogDetailPopupVisible = true;
    }

    sendByMail(): void {
        // Nascondi il tooltip prima di aprire il popup
        this.hideTooltips();

        const selectedWaybillCodes = Array.from(this.selectedWaybillCodes);

        if (selectedWaybillCodes.length === 0) {
            this.notificationService.warning('Nessuna bolla selezionata per l\'invio per mail');
            return;
        }

        // Controlla se sono selezionate più di 100 bolle
        if (selectedWaybillCodes.length > 100) {
            this.notificationService.warning('Non è possibile inviare via mail più di 100 bolle. Selezionate un numero minore di bolle.');
            return;
        }

        // Apri il popup per raccogliere dettagli
        this.isMailDetailPopupVisible = true;
    }

    // Metodo per validare lo status delle bolle e procedere con l'operazione
    private validateWaybillStatusAndProceed(operation: 'pdf' | 'axylog', selectedWaybillCodes: string[]): void {
        // Trova le bolle selezionate nei dati
        const selectedWaybills = this.waybills.filter(waybill =>
            waybill.waybillCode && selectedWaybillCodes.includes(waybill.waybillCode)
        );

        // Separa le bolle per status: la stampa PDF ammette ACTIVE e DELIVERED,
        // l'invio in Axylog solo ACTIVE
        const allowed = this.getEligibleStatusesForOperation(operation);
        const activeWaybills = selectedWaybills.filter(waybill => allowed.includes(waybill.waybillStatus || ''));
        const nonActiveWaybills = selectedWaybills.filter(waybill => !allowed.includes(waybill.waybillStatus || ''));

        const activeWaybillsMap: { key: string, value: string }[] = [];
        activeWaybills.forEach(w => {
            if (w.waybillCode) {
                activeWaybillsMap.push({key: w.waybillCode, value: w.waybillShipmentCode || ''});
            }
        });
        const activeWaybillCodes = activeWaybillsMap.map(entry => entry.key);
        const nonActiveWaybillCodes = nonActiveWaybills.map(w => w.waybillCode).filter(code => !!code);

        // Se tutte le bolle sono ACTIVE, procedi direttamente
        if (nonActiveWaybills.length === 0) {
            this.executeOperation(operation, activeWaybillsMap);
            return;
        }

        // Se ci sono bolle non ACTIVE, mostra il popup di conferma
        this.statusConfirmationData = {
            operation,
            selectedWaybillCodes,
            activeWaybillCodes,
            activeWaybillsMap,
            nonActiveWaybillCodes
        };
        this.isStatusConfirmationPopupVisible = true;
    }

    // Metodo per eseguire l'operazione dopo la conferma
    executeOperation(operation: 'pdf' | 'axylog', waybillsMap: { key: string, value: string }[]): void {
        const waybillCodes = waybillsMap.map(entry => entry.key);
        if (waybillCodes.length === 0) {
            const operationName = operation === 'pdf' ? 'stampa PDF' : 'invio in Axylog';
            const statuses = this.getEligibleStatusesForOperation(operation).join(' o ');
            this.notificationService.warning(`Nessuna bolla ${statuses} disponibile per ${operationName}`);
            return;
        }

        console.log(`${operation.toUpperCase()} richiesto per i waybillCode:`, waybillCodes);

        if (operation === 'pdf') {
            // Imposta lo stato di caricamento
            this.isPrintingPDF = true;

            // Chiama il service per generare e scaricare il PDF delle bolle selezionate
            this.shipmentManagementService.printWaybills(waybillsMap).subscribe({
                next: (blob) => {
                    this.isPrintingPDF = false;
                    this.notificationService.success(
                        `PDF generato e scaricato con successo per ${waybillCodes.length} ${waybillCodes.length === 1 ? 'bolla' : 'bolle'}`
                    );
                },
                error: (error) => {
                    this.isPrintingPDF = false;
                    console.error('Error generating PDF:', error);
                    this.notificationService.error('Errore durante la generazione del PDF');
                }
            });
        } else {
            this.isSendingAxylog = true;
            this.shipmentManagementService.sendWaybillsToAxylog(waybillCodes, this.clientsBrands)
                .pipe(
                    catchError(error => {
                        console.error('Error sending to Axylog:', error);
                        this.notificationService.error('Errore durante l\'invio in Axylog');
                        return of(null);
                    }),
                    finalize(() => {
                        this.isSendingAxylog = false;
                    })
                )
                .subscribe(response => {
                    // On success close related UI and clear selections
                    this.isAxylogDetailPopupVisible = false;
                    this.selectedWaybillCodes.clear();
                    this.notificationService.success(`Bolle inviate con successo in Axylog: ${waybillCodes.length} ${waybillCodes.length === 1 ? 'bolla' : 'bolle'}`);
                });
        }
    }

    // Metodi per gestire il popup di conferma status
    confirmStatusOperation(): void {
        if (this.statusConfirmationData) {
            this.executeOperation(
                this.statusConfirmationData.operation,
                this.statusConfirmationData.activeWaybillsMap
            );
        }
        this.closeStatusConfirmationPopup();
    }

    cancelStatusOperation(): void {
        this.closeStatusConfirmationPopup();
    }

    closeStatusConfirmationPopup(): void {
        this.isStatusConfirmationPopupVisible = false;
        this.statusConfirmationData = null;
    }

    // Helper methods for status confirmation popup

    /** Stati ammessi per l'operazione: stampa PDF = ACTIVE e DELIVERED, Axylog = solo ACTIVE */
    getEligibleStatusesForOperation(operation: 'pdf' | 'axylog'): string[] {
        return operation === 'pdf' ? ['ACTIVE', 'DELIVERED'] : ['ACTIVE'];
    }

    /** Etichetta degli stati ammessi per l'operazione in conferma (es. "ACTIVE o DELIVERED") */
    get statusConfirmationEligibleLabel(): string {
        const operation = this.statusConfirmationData?.operation ?? 'pdf';
        return this.getEligibleStatusesForOperation(operation).join(' o ');
    }

    /** True se la bolla ha uno stato ammesso per l'operazione in conferma */
    isWaybillEligibleForConfirmation(waybillCode: string): boolean {
        const waybill = this.waybills.find(w => w.waybillCode === waybillCode);
        const operation = this.statusConfirmationData?.operation ?? 'pdf';
        return this.getEligibleStatusesForOperation(operation).includes(waybill?.waybillStatus || '');
    }

    isWaybillActive(waybillCode: string): boolean {
        const waybill = this.waybills.find(w => w.waybillCode === waybillCode);
        return waybill?.waybillStatus === 'ACTIVE';
    }

    getWaybillStatus(waybillCode: string): string {
        const waybill = this.waybills.find(w => w.waybillCode === waybillCode);
        return waybill?.waybillStatus || 'UNKNOWN';
    }

    /**
     * Gestisce la conferma del popup Axylog
     */
    onAxylogDetailConfirmed(data: { waybillCodes: string[] }): void {
        this.isAxylogDetailPopupVisible = false;
        this.isSendingAxylog = true;

        try {
            console.log('Invio a Axylog con i seguenti dati:', data);

            // Qui si può aggiungere la logica per l'invio effettivo a Axylog
            // utilizzando i dati raccolti dal popup (waybillCodes)

            // Verifica lo status delle bolle selezionate
            this.validateWaybillStatusAndProceed('axylog', data.waybillCodes);


        } catch (error) {
            console.error('Errore durante l\'invio a Axylog:', error);
            this.notificationService.error('Errore durante l\'invio a Axylog');
        } finally {
            // Simula un delay per mostrare l'indicatore di loading
            setTimeout(() => {
                this.isSendingAxylog = false;
                // Resetta lo stato di caricamento del popup
                if (this.axylogDetailPopup) {
                    this.axylogDetailPopup.isSending = false;
                }
            }, 1500);
        }
    }

    /**
     * Gestisce la chiusura del popup Axylog
     */
    onAxylogDetailClosed(): void {
        this.isAxylogDetailPopupVisible = false;
    }

    /**
     * Gestisce la conferma del popup Mail
     */
    onMailDetailConfirmed(data: {
        waybillCodes: string[],
        mailType: string,
        routingData?: { routingCode: string; routingDate: string; waybillCodes: string[] }[]
    }): void {
        this.isMailDetailPopupVisible = false;
        this.isSendingEmail = true;

        try {
            console.log('Invio via email con i seguenti dati:', data);
            console.log('Tipo di mail:', data.mailType === 'per_waybill' ? 'Invia Singola Bolla' : 'Invia Intero Instradamento');

            if (data.routingData) {
                console.log('Dati instradamenti:', data.routingData);
            }

            // Qui si può aggiungere la logica per l'invio effettivo via email
            // utilizzando i dati raccolti dal popup (waybillCodes, mailType e routingData)

            // Mostra notifica di successo con tipo di mail
            const mailTypeText = data.mailType === 'per_waybill' ? 'singola bolla' : 'intero instradamento';
            const countText = data.mailType === 'per_waybill'
                ? `${data.waybillCodes.length} bolle`
                : `${data.routingData?.length || 0} instradamenti (${data.waybillCodes.length} bolle totali)`;

            this.notificationService.success(
                `Invio via email avviato per ${countText} (${mailTypeText})`
            );

        } catch (error) {
            console.error('Errore durante l\'invio via email:', error);
            this.notificationService.error('Errore durante l\'invio via email');
        } finally {
            // Simula un delay per mostrare l'indicatore di loading
            setTimeout(() => {
                this.isSendingEmail = false;
                // Resetta lo stato di caricamento del popup
                if (this.mailDetailPopup) {
                    this.mailDetailPopup.isSending = false;
                }
            }, 1500);
        }
    }

    /**
     * Gestisce la chiusura del popup Mail
     */
    onMailDetailClosed(): void {
        this.isMailDetailPopupVisible = false;
    }

    invalidateSelectedParcelsForLoadingLists(): void {
        if (!this.isSelectionModeActive || this.selectedParcelCodes.size === 0) {
            this.notificationService.warning('Nessun collo selezionato');
            return;
        }

        this.selectedParcelsForInvalidation = this.parcels
            .filter(parcel => parcel.parcelCode && this.selectedParcelCodes.has(parcel.parcelCode))
            .map(parcel => ({
                parcelCode: parcel.parcelCode!,
                parcelInactiveForLoadingLists: !!parcel.parcelInactiveForLoadingLists
            }));

        if (this.selectedParcelsForInvalidation.length === 0) {
            this.notificationService.warning('Nessun collo disponibile');
            return;
        }

        // tab iniziale: se almeno uno è valido da invalidare, apro su INVALIDA
        const hasValidParcels = this.selectedParcelsForInvalidation.some(
            parcel => !parcel.parcelInactiveForLoadingLists
        );

        this.loadingListsPopupInitialTab = hasValidParcels ? 'invalidate' : 'validate';
        this.isInvalidateLoadingListsPopupVisible = true;
    }

    onInvalidateLoadingListsPopupClosed(): void {
        this.isInvalidateLoadingListsPopupVisible = false;
        this.selectedParcelsForInvalidation = [];
        this.loadingListsPopupInitialTab = 'invalidate';
    }

    onInvalidateLoadingListsPopupConfirmed(event: {
        action: 'invalidate' | 'validate';
        parcelCodes: string[];
    }): void {
        if (!event || !event.parcelCodes || event.parcelCodes.length === 0) {
            this.notificationService.warning('Nessun collo selezionato');
            return;
        }

        this.isInvalidating = true;

        const isToInvalid = event.action === 'invalidate';

        this.shipmentManagementService.invalidateParcelsForLoadingLists(event.parcelCodes, isToInvalid)
            .pipe(
                catchError(error => {
                    console.error('Errore durante aggiornamento colli:', error);
                    this.notificationService.error(
                        isToInvalid
                            ? 'Errore durante l\'invalidazione dei colli nei borderò'
                            : 'Errore durante la rivalida dei colli nei borderò'
                    );
                    return of(null);
                }),
                finalize(() => {
                    this.isInvalidating = false;
                })
            )
            .subscribe(response => {
                if (response) {
                    this.updateLocalParcelLoadingListsFlag(event.parcelCodes, isToInvalid);

                    this.notificationService.success(
                        isToInvalid
                            ? `${event.parcelCodes.length} ${event.parcelCodes.length === 1 ? 'collo invalidato' : 'colli invalidati'} con successo`
                            : `${event.parcelCodes.length} ${event.parcelCodes.length === 1 ? 'collo rivalidato' : 'colli rivalidati'} con successo`
                    );

                    this.isInvalidateLoadingListsPopupVisible = false;
                    this.selectedParcelsForInvalidation = [];
                    this.loadingListsPopupInitialTab = 'invalidate';
                    this.selectedParcelCodes.clear();

                    this.refreshResults();
                }
            });
    }

    openInvalidateLoadingListsPopupFromDetail(parcelCodes: string[]): void {
        if (!parcelCodes || parcelCodes.length === 0) {
            this.notificationService.warning('Nessun collo selezionato');
            return;
        }

        this.selectedParcelsForInvalidation = this.parcels
            .filter(parcel => parcel.parcelCode && parcelCodes.includes(parcel.parcelCode))
            .map(parcel => ({
                parcelCode: parcel.parcelCode!,
                parcelInactiveForLoadingLists: !!parcel.parcelInactiveForLoadingLists
            }));

        if (this.selectedParcelsForInvalidation.length === 0) {
            this.notificationService.warning('Nessun collo disponibile');
            return;
        }

        const firstParcel = this.selectedParcelsForInvalidation[0];
        this.loadingListsPopupInitialTab = firstParcel.parcelInactiveForLoadingLists ? 'validate' : 'invalidate';
        this.isInvalidateLoadingListsPopupVisible = true;
    }

    private updateLocalParcelLoadingListsFlag(
        parcelCodes: string[],
        inactive: boolean
    ): void {
        const codesSet = new Set(parcelCodes);

        this.parcels = this.parcels.map(parcel => {
            if (parcel.parcelCode && codesSet.has(parcel.parcelCode)) {
                return {
                    ...parcel,
                    parcelInactiveForLoadingLists: inactive
                };
            }
            return parcel;
        });

        this.originalParcels = this.originalParcels.map(parcel => {
            if (parcel.parcelCode && codesSet.has(parcel.parcelCode)) {
                return {
                    ...parcel,
                    parcelInactiveForLoadingLists: inactive
                };
            }
            return parcel;
        });

        if (this.selectedParcel && this.selectedParcel.parcelCode && codesSet.has(this.selectedParcel.parcelCode)) {
            this.selectedParcel = {
                ...this.selectedParcel,
                parcelInactiveForLoadingLists: inactive
            };
        }

        if (this.selectedParcelForDetail && this.selectedParcelForDetail.parcelCode && codesSet.has(this.selectedParcelForDetail.parcelCode)) {
            this.selectedParcelForDetail = {
                ...this.selectedParcelForDetail,
                parcelInactiveForLoadingLists: inactive
            };
        }

        if (this.selectedParcelForCompletion && this.selectedParcelForCompletion.parcelCode && codesSet.has(this.selectedParcelForCompletion.parcelCode)) {
            this.selectedParcelForCompletion = {
                ...this.selectedParcelForCompletion,
                parcelInactiveForLoadingLists: inactive
            };
        }
    }

}
